// data.js — HK CNE bilingual schema v2 + sample data
// Mirrors the upstream JSON (118 courses across 13 NCHK-accredited providers):
//   AHKNS · CNHK · HKSNE · HKACCN · FPAHK · HKACS · HKTB · HKAM · FMSHK ·
//   HKSPMC · HKCNHCM · HKEUS · HKANM
// Schema rule: any translatable field has _en and _zh siblings;
// numeric / date / code fields are language-neutral.

// Available in both the standalone Babel dev path and the esbuild bundle.
// `typeof` is intentionally used on the unbound build-time symbol: it is safe
// when index.html loads this file directly, while esbuild replaces it in builds.
const VITAL_APP_VERSION = (typeof __VITAL_VERSION__ !== 'undefined') ? __VITAL_VERSION__ : 'dev';

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA — every CNE course record uses these fields
// ─────────────────────────────────────────────────────────────────────────────
const COURSE_SCHEMA = {
  // identifiers
  provider_code:        'string  · short code: AHKNS / CNHK / HKSNE / HKACCN / FPAHK / HKACS / HKTB / HKAM / FMSHK / HKSPMC / HKCNHCM / HKEUS / HKANM',
  provider_full_en:     'string  · provider name (English)',
  provider_full_zh:     'string  · provider name (Chinese)',
  provider_short_en:    'string  · provider short name (English)',
  provider_short_zh:    'string  · provider short name (Chinese)',
  course_code:          'string|null · provider-issued course id',

  // titles & classification
  title_en:             'string  · course title (English)',
  title_zh:             'string  · course title (Chinese)',
  event_type_en:        'string|null · "Course" | "Conference" | "AGM + Scientific Meeting" …',
  event_type_zh:        'string|null',
  target_en:            'string  · audience (English)',
  target_zh:            'string  · audience (Chinese)',
  academy_en:           'string|null',
  academy_zh:           'string|null',

  // schedule
  start_date:           'YYYY-MM-DD | null',
  end_date:             'YYYY-MM-DD | null',
  application_deadline: 'YYYY-MM-DD | null',
  registration_deadline:'YYYY-MM-DD | null  · conference reg deadline (alt name)',
  early_bird_registration_deadline: 'YYYY-MM-DD | null',
  abstract_submission_deadline:     'YYYY-MM-DD | null  · conferences only',
  abstract_acceptance_notification: 'YYYY-MM-DD | null  · conferences only',
  time:                 '"HH:MM-HH:MM" | string | null',
  day_of_week_en:       'string|null  · "Tuesday" / "Thursday, Friday"…',
  day_of_week_zh:       'string|null  · "星期二" / "星期四、星期五"…',
  sessions:             'number|string|null',
  sessions_list:        'jsonb | null  · array of "YYYY-MM-DD[THH:MM]" — canonical per-session list; start_date/end_date/sessions derived from it when present',
  duration_text_en:     'string|null  · free-form duration summary',
  period_text_en:       'string|null  · raw period string from detail page',

  // points & fees
  cne_points:           'number|null · CNE credit',
  cne_per_session:      'number|null',
  cne_points_note:      'string|null  · e.g. "TBC (subject to Nursing Council)"',
  cem_points:           'number|null',
  fee_hkd:              'number|null  · base / member fee',
  fee_hkd_member:       'number|null',
  fee_hkd_nonmember:    'number|null',
  fee_early_bird_hkd:   'number|null',
  fee_per_session_hkd:  'number|null',

  // location & delivery
  venue_en:             'string|null',
  venue_zh:             'string|null',
  teaching_mode_en:     'string|null · "Zoom" | "Face-to-face" | "Online"…',
  teaching_mode_zh:     'string|null',
  language_en:          'string|null',
  language_zh:          'string|null',

  // status & links
  enrollment_status_en: '"Full" | "Not full" | "Closed" | null',
  enrollment_status_zh: '"已滿" | "未滿" | "已結束" | null',
  url:                  'string|null · canonical course page or PDF flyer',
  source_url:           'string  · provider listing page',

  // rich content (often null)
  description_en:       'string|null',
  description_zh:       'string|null',
  speakers:             'string[]|null',
  instructor_en:        'string|null  · single-instructor convenience field',
  organizer:            'string|null',
  theme_en:             'string|null  · conferences only',
  theme_zh:             'string|null  · conferences only',
  topics_en:            'string[]|null · conference topics list',
  topics_zh:            'string[]|null',
  entry_requirement_en: 'string|null  · prerequisites',
  entry_requirement_zh: 'string|null',

  // provenance
  _raw_start_date:      'string · provider-formatted date as scraped',
  _raw_cne:             'string · provider-formatted credit as scraped',
  _raw_fee:             'string · provider-formatted fee as scraped',
  _note:                'string · scraper-side caveat',
  _blocker:             'string · explains missing fields (e.g. Cloudflare flyer)',
  _pdf_enriched:        'boolean · enriched from linked PDF',
  _ocr_extracted:       'boolean · enriched via OCR on image flyer',
  _detail_page_enriched:'boolean',
  _source_pdf:          'string · source PDF filename / URL',
};

// Upstream dataset summary (from JSON meta block)
const DATASET_META = {
  schema_version: '2.0-bilingual',
  total_courses: 118,
  providers_count: 13,
  last_updated: '2026-05-11',
  nchk_official_providers_total: 135,
  nchk_list_url: 'https://www.nchk.org.hk/filemanager/en/pdf/list_accredited_providers_CNE_en.pdf',
  provider_counts: { AHKNS:33, CNHK:6, HKSNE:9, HKACCN:12, FPAHK:8, HKACS:9, HKTB:7, HKAM:1, FMSHK:6, HKSPMC:2, HKCNHCM:4, HKEUS:12, HKANM:9 },
  field_null_rates: { start_date:'13/118', cne_points:'51/118', fee_hkd:'53/118', venue_en:'36/118', time:'57/118', url:'1/118' },
};

// Provider directory — all 13 NCHK-accredited providers in the dataset --------
const PROVIDERS = {
  AHKNS:   { en: 'Association of Hong Kong Nursing Staff',        zh: '香港護士協會',           short_en: 'AHKNS',   short_zh: '護士協會',       source: 'https://nurse.org.hk/courses/list' },
  CNHK:    { en: 'College of Nursing Hong Kong',                  zh: '香港護理學院',           short_en: 'CNHK',    short_zh: '護理學院',       source: 'https://cnhk.org.hk/eng/Course_Training.html' },
  HKSNE:   { en: 'Hong Kong Society of Nursing Education',        zh: '香港護理教育學會',       short_en: 'HKSNE',   short_zh: '護理教育學會',   source: 'https://hksne.org.hk/en/events-and-courses' },
  HKACCN:  { en: 'Hong Kong Association of Critical Care Nurses', zh: '香港危重病學護士協會',   short_en: 'HKACCN',  short_zh: '危重護理協會',   source: 'https://hkaccn.org/education_courses.php' },
  FPAHK:   { en: 'The Family Planning Association of Hong Kong',  zh: '香港家庭計劃指導會',     short_en: 'FPAHK',   short_zh: '家計會',         source: 'https://www.famplan.org.hk/en/events-and-programs/cme-cne-programs' },
  HKACS:   { en: 'Hong Kong Anti-Cancer Society',                 zh: '香港防癌會',             short_en: 'HKACS',   short_zh: '防癌會',         source: 'https://www.hkacs.org.hk/en/TrainingCourses.php' },
  HKTB:    { en: 'Hong Kong Tuberculosis, Chest and Heart Diseases Association', zh: '香港防癆心臟及胸病協會', short_en: 'HKTB', short_zh: '防癆協會', source: 'https://www.antitb.org.hk/en/training.php?cid=4' },
  HKAM:    { en: 'Hong Kong Academy of Medicine',                 zh: '香港醫學專科學院',       short_en: 'HKAM',    short_zh: '醫學專科學院',   source: 'https://www.hkam.org.hk/' },
  FMSHK:   { en: 'The Federation of Medical Societies of Hong Kong', zh: '香港醫學組織聯會',    short_en: 'FMSHK',   short_zh: '醫學組織聯會',   source: 'https://www.fmshk.org/' },
  HKSPMC:  { en: 'Hong Kong Society of Professional Medical Care', zh: '香港專業護理學會',      short_en: 'HKSPMC',  short_zh: '專業護理學會',   source: 'https://www.hkspmc.org.hk/en/atrance.php' },
  HKCNHCM: { en: 'The College of Nursing Management Hong Kong',   zh: '香港護理管理學院',       short_en: 'HKCNHCM', short_zh: '護理管理學院',   source: 'https://hkcnhcm.org/' },
  HKEUS:   { en: 'Hong Kong Endoscopic Ultrasonography Society',  zh: '香港內窺鏡超聲波學會',   short_en: 'HKEUS',   short_zh: '內窺鏡超聲學會', source: 'https://www.hkeus.org/' },
  HKANM:   { en: 'The Hong Kong Academy of Nursing & Midwifery',  zh: '香港護理及助產學院',     short_en: 'HKANM',   short_zh: '護理及助產學院', source: 'https://www.hkanm.hk/' },
};

// ─────────────────────────────────────────────────────────────────────────────
// RAW_COURSES — sample matching the v2 bilingual schema
// (subset; full upstream JSON has 68 entries — these 48 represent all 5 providers
//  and every shape we render in the UI)
// ─────────────────────────────────────────────────────────────────────────────
// Compact constructor: only set the fields each record actually carries.
function C(o){
  // back-fill provider names from PROVIDERS so each record stays small
  const p = PROVIDERS[o.provider_code];
  return Object.assign({
    provider_full_en: p.en, provider_full_zh: p.zh,
    provider_short_en: p.short_en, provider_short_zh: p.short_zh,
    source_url: p.source,
  }, o);
}

const RAW_COURSES = [
  // ── AHKNS · Association of Hong Kong Nursing Staff ────────────────────────
  C({provider_code:'AHKNS',course_code:'2209EP-08',
    title_en:'Intensive Course on Medications for Dermatological Disorders and Infectious Diseases',
    title_zh:'皮膚科疾病與傳染病藥物深造課程',
    start_date:'2026-04-14',end_date:'2026-05-12',time:'19:00-22:00',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:5,
    cne_points:15,cem_points:0,fee_hkd:2380,
    venue_en:'6/F, Parkes Commercial Building, 29-31 Parkes Street, Jordan, Kowloon',
    venue_zh:'九龍佐敦白加士街 29-31 號嘉士商業大廈 6 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/2209EP-08',
    description_en:'Types of pathogens; overview of antibiotics, antivirals and antifungal agents; management of penicillin allergy, pneumonia, tuberculosis, neutropenic fever and sepsis; UTI, CNS infection, infective endocarditis, PID and septic arthritis; common medications for skin diseases; management for acne, eczema, urticaria, psoriasis and blistering diseases.',
    _raw_start_date:'14/04/2026',_raw_cne:'15 pts',_raw_fee:'$2380.00',
    _detail_page_enriched:true}),
  C({provider_code:'AHKNS',course_code:'ADU070526',
    title_en:'Understanding Dementia (Cognitive Impairment Disorders)',
    title_zh:'認知障礙症〔老人癡呆〕的認識',
    start_date:'2026-05-07',end_date:'2026-05-21',time:'18:30-21:00',
    day_of_week_en:'Thursday',day_of_week_zh:'星期四',sessions:3,
    cne_points:7.5,cem_points:0,fee_hkd:780,fee_hkd_member:780,fee_hkd_nonmember:980,
    venue_en:'6/F, Parkes Commercial Building, 29-31 Parkes Street, Jordan, Kowloon',
    venue_zh:'九龍佐敦白加士街 29 – 31 號嘉士商業大廈 6 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/ADU070526',
    _raw_start_date:'07/05/2026',_raw_cne:'7.5 pts',_raw_fee:'$780.00'}),
  C({provider_code:'AHKNS',course_code:'ACLS120526',
    title_en:'Advanced Cardiovascular Life Support (ACLS) Provider Course',
    title_zh:'高級心臟生命支援（ACLS）提供者課程',
    start_date:'2026-05-12',end_date:'2026-05-13',time:'09:00-17:00',
    day_of_week_en:'Tuesday, Wednesday',day_of_week_zh:'星期二、星期三',sessions:2,
    cne_points:13,cem_points:0,fee_hkd:2730,fee_hkd_member:2730,fee_hkd_nonmember:2940,
    venue_en:'Hong Kong Adventist Hospital – Tsuen Wan Clinical Training Center, 3/F Old Wing, 199 Tsuen King Circuit',
    venue_zh:'香港荃灣荃景圍 199 號香港港安醫院荃灣臨床培訓中心舊翼 3 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/ACLS120526',
    _raw_start_date:'12/05/2026',_raw_cne:'13 pts',_raw_fee:'$2730.00'}),
  C({provider_code:'AHKNS',course_code:'UIOA130526',
    title_en:'Ultrasound Imaging Overview (Advanced / Health of Women and Men)',
    title_zh:'超聲波成像概覽（進階／男女健康）',
    start_date:'2026-05-13',end_date:'2026-06-03',time:'19:00-21:30',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:4,
    cne_points:10,cem_points:0,fee_hkd:2200,fee_hkd_member:2200,fee_hkd_nonmember:2800,
    venue_en:'6/F, Parkes Commercial Building, 29-31 Parkes Street, Jordan, Kowloon',
    venue_zh:'九龍佐敦白加士街 29-31 號嘉士商業大廈 6 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/UIOA130526',
    description_en:'From basic ultrasound principles to practical clinical application. Hands-on training emphasising real-world scanning skills for outpatient and clinical settings. Four-day intensive learning with certificate on completion. Class capacity 18.',
    speakers:['Ms. Melody Lee Wing Yin — Registered Diagnostic Medical Sonographer'],
    _raw_start_date:'13/05/2026',_raw_cne:'10 pts',_raw_fee:'$2200.00'}),
  C({provider_code:'AHKNS',course_code:'UMWM190526',
    title_en:'Updates in Practical Wound Management',
    title_zh:'實用傷口護理新知',
    start_date:'2026-05-19',end_date:'2026-05-26',time:'19:00-21:30',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:2,
    cne_points:5,cem_points:0,fee_hkd:490,fee_hkd_member:490,fee_hkd_nonmember:690,
    venue_en:null,venue_zh:null,
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    speakers:['Mr. Pang Chak Hau — Stoma and Wound Care Nurse'],
    url:'https://nurse.org.hk/courses/UMWM190526',
    _raw_start_date:'19/05/2026',_raw_cne:'5 pts',_raw_fee:'$490.00'}),
  C({provider_code:'AHKNS',course_code:'PALS210526',
    title_en:'Pediatric Advanced Life Support (PALS) Course',
    title_zh:'兒科高級生命支援（PALS）課程',
    start_date:'2026-05-21',end_date:'2026-05-22',time:'09:00-17:00',
    day_of_week_en:'Thursday, Friday',day_of_week_zh:'星期四、星期五',sessions:2,
    cne_points:13,cem_points:0,fee_hkd:2840,fee_hkd_member:2840,fee_hkd_nonmember:3050,
    venue_en:'Hong Kong Adventist Hospital – Tsuen Wan Clinical Training Center, 3/F Old Wing, 199 Tsuen King Circuit',
    venue_zh:'香港荃灣荃景圍 199 號香港港安醫院荃灣臨床培訓中心舊翼 3 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/PALS210526',
    _raw_start_date:'21/05/2026',_raw_cne:'13 pts',_raw_fee:'$2840.00'}),
  C({provider_code:'AHKNS',course_code:'2065EP-219',
    title_en:'Venipuncture and Blood-Taking Technique',
    title_zh:'靜脈穿刺及抽血技術',
    start_date:'2026-05-23',end_date:'2026-05-30',time:'11:00-16:00',
    day_of_week_en:'Saturday',day_of_week_zh:'星期六',sessions:2,
    cne_points:8,cem_points:0,fee_hkd:1900,fee_hkd_member:1900,fee_hkd_nonmember:2000,
    venue_en:'6/F, Parkes Commercial Building, 29-31 Parkes Street, Jordan, Kowloon',
    venue_zh:'九龍佐敦白加士街 29-31 號嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    description_en:'Theoretical knowledge on venipuncture and blood taking, preventive measures for IV-insertion complications, and on-hand demonstration practice.',
    url:'https://nurse.org.hk/courses/2065EP-219',
    _raw_start_date:'23/05/2026',_raw_cne:'8 pts',_raw_fee:'$1900.00'}),
  C({provider_code:'AHKNS',course_code:'BLS260526A',
    title_en:'Basic Life Support (BLS) Course for Healthcare Providers',
    title_zh:'醫護人員基礎生命支援（BLS）課程',
    start_date:'2026-05-26',end_date:'2026-05-26',time:'09:00-12:00',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:1,
    cne_points:2.5,cem_points:0,fee_hkd:720,fee_hkd_member:720,fee_hkd_nonmember:770,
    venue_en:'HKAH Tsuen Wan Clinical Training Center, 3/F Old Wing, 199 Tsuen King Circuit',
    venue_zh:'香港荃灣港安醫院荃灣臨床培訓中心舊翼 3 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/BLS260526A',
    _raw_start_date:'26/05/2026',_raw_cne:'2.5 pts',_raw_fee:'$720.00'}),
  C({provider_code:'AHKNS',course_code:'LCPE280526',
    title_en:'Listening and Companionship — Emotional First Aid Practical Course',
    title_zh:'聆聽與陪伴 — 情緒急救實務課程',
    start_date:'2026-05-28',end_date:'2026-06-18',time:'10:00-12:00',
    day_of_week_en:'Thursday',day_of_week_zh:'星期四',sessions:4,
    cne_points:8,cem_points:0,fee_hkd:830,fee_hkd_member:830,fee_hkd_nonmember:1030,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/LCPE280526',
    _raw_start_date:'28/05/2026',_raw_cne:'8 pts',_raw_fee:'$830.00'}),
  C({provider_code:'AHKNS',course_code:'HSHE020626',
    title_en:'Human Anatomy and Healthy Exercise',
    title_zh:'人體結構與健康運動',
    start_date:'2026-06-02',end_date:'2026-06-02',time:'19:00-21:00',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:1,
    cne_points:2,cem_points:0,fee_hkd:250,fee_hkd_member:250,fee_hkd_nonmember:450,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Full',enrollment_status_zh:'已滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/HSHE020626',
    _raw_start_date:'02/06/2026',_raw_cne:'2 pts',_raw_fee:'$250.00'}),
  C({provider_code:'AHKNS',course_code:'CDBP080626',
    title_en:'Studies on Child Development: From Birth to Primary School',
    title_zh:'兒童發展研究：由出生至小學階段',
    start_date:'2026-06-08',end_date:'2026-06-29',time:'19:00-22:00',
    day_of_week_en:'Monday',day_of_week_zh:'星期一',sessions:4,
    cne_points:12,cem_points:0,fee_hkd:1280,fee_hkd_member:1280,fee_hkd_nonmember:1480,
    venue_en:'6/F, Parkes Commercial Building, 29-31 Parkes Street, Jordan',
    venue_zh:'九龍佐敦白加士街 29-31 號嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    academy_en:'Parent-Child Academy',academy_zh:'親子學院',
    speakers:['Dr. Chi Chuen Chan'],
    description_en:'Introduction to child development; intelligence and emotional development; social and cognitive development; parenting; personality; memory and learning; case studies.',
    url:'https://nurse.org.hk/courses/CDBP080626',
    _raw_start_date:'08/06/2026',_raw_cne:'12 pts',_raw_fee:'$1280.00'}),
  C({provider_code:'AHKNS',course_code:'NLPB100626',
    title_en:'Creative Thinking Workshop for Nursing Management',
    title_zh:'護理管理創意思維工作坊',
    start_date:'2026-06-10',end_date:null,time:'19:00-21:30',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:1,
    cne_points:2.5,cem_points:0,fee_hkd:290,fee_hkd_member:290,fee_hkd_nonmember:490,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/NLPB100626',
    _raw_start_date:'10/06/2026',_raw_cne:'2.5 pts',_raw_fee:'$290.00'}),
  C({provider_code:'AHKNS',course_code:'2065EP-220',
    title_en:'Venipuncture and Blood-Taking Technique',
    title_zh:'靜脈穿刺及抽血技術',
    start_date:'2026-06-16',end_date:'2026-07-07',time:'19:00-21:00',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:3,
    cne_points:8,cem_points:0,fee_hkd:1900,fee_hkd_member:1900,fee_hkd_nonmember:2000,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/2065EP-220',
    _raw_start_date:'16/06/2026',_raw_cne:'8 pts',_raw_fee:'$1900.00'}),
  C({provider_code:'AHKNS',course_code:'NGT170626',
    title_en:'Comprehensive Analysis of Nasogastric Tube and PEG',
    title_zh:'鼻胃管與經皮內視鏡胃造口（PEG）綜合分析',
    start_date:'2026-06-17',end_date:null,time:'19:00-21:00',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:1,
    cne_points:2,cem_points:0,fee_hkd:200,fee_hkd_member:200,fee_hkd_nonmember:400,
    venue_en:'HKPA Talent Development Centre, 3/F Mac Pherson Place, 38 Nelson St., Mong Kok',
    venue_zh:'旺角奶路臣街 38 號麥花臣匯 3 樓香港遊樂場協會優才發展中心',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/NGT170626',
    _raw_start_date:'17/06/2026',_raw_cne:'2 pts',_raw_fee:'$200.00'}),
  C({provider_code:'AHKNS',course_code:'UIO240626',
    title_en:'Ultrasound Imaging Overview',
    title_zh:'超聲波成像概覽',
    start_date:'2026-06-24',end_date:null,time:'19:00-21:00',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:1,
    cne_points:2,cem_points:0,fee_hkd:320,fee_hkd_member:320,fee_hkd_nonmember:520,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    speakers:['Ms. Melody Lee Wing Yin — Registered Diagnostic Medical Sonographer'],
    url:'https://nurse.org.hk/courses/UIO240626',
    _raw_start_date:'24/06/2026',_raw_cne:'2 pts',_raw_fee:'$320.00'}),
  C({provider_code:'AHKNS',course_code:'TSMC250626',
    title_en:'Sports Injury Recognition, Prevention & Core Rotation Exercises',
    title_zh:'運動損傷識別、預防及核心訓練',
    start_date:'2026-06-25',end_date:null,time:'19:00-21:00',
    day_of_week_en:'Thursday',day_of_week_zh:'星期四',sessions:1,
    cne_points:2,cem_points:0,fee_hkd:399,fee_hkd_member:399,fee_hkd_nonmember:599,
    venue_en:'VITRU Physiotherapy and Fitness, 3/F, 574-576 Nathan Road, Mong Kok',
    venue_zh:'九龍旺角彌敦道 574-576 號和富商業大廈 3 樓 VITRU 運動物理治療中心',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/TSMC250626',
    _raw_start_date:'25/06/2026',_raw_cne:'2 pts',_raw_fee:'$399.00'}),
  C({provider_code:'AHKNS',course_code:'ANCA020726',
    title_en:'Aromatherapy and Natural Skincare Aesthetics',
    title_zh:'香薰芳療天然護理美學',
    start_date:'2026-07-02',end_date:null,time:'19:00-21:00',
    day_of_week_en:'Thursday',day_of_week_zh:'星期四',sessions:1,
    cne_points:2,cem_points:0,fee_hkd:250,fee_hkd_member:250,fee_hkd_nonmember:450,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/ANCA020726',
    _raw_start_date:'02/07/2026',_raw_cne:'2 pts',_raw_fee:'$250.00'}),
  C({provider_code:'AHKNS',course_code:'UMAC060726',
    title_en:'Understanding IV Sedation and Monitored Anaesthetic Care',
    title_zh:'了解靜脈鎮靜及監察麻醉護理',
    start_date:'2026-07-06',end_date:'2026-07-13',time:'10:00-12:00',
    day_of_week_en:'Monday',day_of_week_zh:'星期一',sessions:2,
    cne_points:4,cem_points:0,fee_hkd:430,fee_hkd_member:430,fee_hkd_nonmember:630,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    speakers:['Ms. Sylvia Wong — Nurse Consultant, Central Ambulatory Care, HK'],
    url:'https://nurse.org.hk/courses/UMAC060726',
    _raw_start_date:'06/07/2026',_raw_cne:'4 pts',_raw_fee:'$430.00'}),
  C({provider_code:'AHKNS',course_code:'NMA140726',
    title_en:'Neurology & Primitive Reflex Therapy for Children: ASD, Developmental Delay & SEN',
    title_zh:'腦神經及兒童原始反射治療：自閉症譜系障礙、發展遲緩及特殊學習需要兒童',
    start_date:'2026-07-14',end_date:null,time:'18:30-21:00',
    day_of_week_en:'Tuesday',day_of_week_zh:'星期二',sessions:1,
    cne_points:2.5,cem_points:0,fee_hkd:280,fee_hkd_member:280,fee_hkd_nonmember:480,
    venue_en:'6/F, Parkes Commercial Building, Jordan',venue_zh:'九龍佐敦嘉士商業大廈 6 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/NMA140726',
    _raw_start_date:'14/07/2026',_raw_cne:'2.5 pts',_raw_fee:'$280.00'}),
  C({provider_code:'AHKNS',course_code:'PALS280726',
    title_en:'Pediatric Advanced Life Support (PALS) Course',
    title_zh:'兒科高級生命支援（PALS）課程',
    start_date:'2026-07-28',end_date:'2026-07-29',time:'09:00-17:00',
    day_of_week_en:'Tuesday, Wednesday',day_of_week_zh:'星期二、星期三',sessions:2,
    cne_points:13,cem_points:0,fee_hkd:2840,fee_hkd_member:2840,fee_hkd_nonmember:3050,
    venue_en:'HKAH Tsuen Wan Clinical Training Center, 3/F Old Wing',
    venue_zh:'香港荃灣港安醫院荃灣臨床培訓中心舊翼 3 樓',
    enrollment_status_en:'Not full',enrollment_status_zh:'未滿',
    target_en:'Nurses / Healthcare Professionals',target_zh:'護士／醫護專業人員',
    url:'https://nurse.org.hk/courses/PALS280726',
    _raw_start_date:'28/07/2026',_raw_cne:'13 pts',_raw_fee:'$2840.00'}),

  // ── CNHK · College of Nursing Hong Kong ───────────────────────────────────
  C({provider_code:'CNHK',course_code:'TC-WMC3-2025-14',
    title_en:'Leadership and Management in Health Services — Module 3',
    title_zh:'醫護服務領導與管理 — 單元 3',
    start_date:'2025-11-13',end_date:'2025-12-23',
    application_deadline:'2025-11-09',time:'18:30-21:30',
    sessions:7,cne_points:20,fee_hkd:2100,fee_hkd_member:2100,fee_hkd_nonmember:2600,
    venue_en:'Zoom (Online)',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    language_en:'Cantonese and English with lecture notes in English',
    language_zh:'廣東話及英文授課，講義以英文為主',
    target_en:'Courses for Nurses',target_zh:'護士課程',
    description_en:'Equip learners with the management competence needed to manage workplace health and safety risks in a fast-paced healthcare environment.',
    description_zh:'裝備學員所需的管理能力，於急速變化的醫護環境中有效管理工作場所的健康及安全風險。',
    speakers:['Mr Sam Yip','Ms Dawn Wong Hiu Ching','Ms Rebecca Fong'],
    url:'https://drive.google.com/file/d/1y9d9z8mer9NnGhde2EqMUH0NNj9D-1C0/view',
    _raw_start_date:'9 Nov 2025',_pdf_enriched:true}),
  C({provider_code:'CNHK',course_code:'TC_IHAC_2025-10',
    title_en:'Integrated Health Assessment & Interpretation of X-ray, ECG and Lab',
    title_zh:'綜合健康評估及 X 光、心電圖及化驗判讀',
    start_date:'2025-11-06',end_date:'2025-12-18',
    application_deadline:'2025-10-30',time:'18:30-21:30',
    sessions:7,cne_points:20,fee_hkd:2100,fee_hkd_member:2100,fee_hkd_nonmember:2600,
    venue_en:'Zoom (Online)',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    language_en:'Cantonese with English lecture notes',
    language_zh:'廣東話授課，講義以英文為主',
    target_en:'Courses for Nurses',target_zh:'護士課程',
    description_en:'Develop clinical reasoning through patient assessment and interpretation of lab data, ECG waveforms and X-ray images.',
    description_zh:'透過病人評估及化驗結果、心電圖波形和 X 光影像的判讀，加強學員臨床推理能力。',
    speakers:['Professor Chan Wing Keung David (RN, DN)'],
    url:'https://drive.google.com/file/d/1Ys1bSv4ttL1tlToUSF8G7bN63E_5vuOG/view',
    _raw_start_date:'30 October 2025',_pdf_enriched:true}),
  C({provider_code:'CNHK',course_code:'TC-ECG-2024-4',
    title_en:'Comprehensive ECG Course',title_zh:'綜合心電圖（ECG）課程',
    target_en:'Courses for Nurses',target_zh:'護士課程',
    url:'https://shorturl.at/6zRGI',_raw_start_date:'29th July 2024'}),
  C({provider_code:'CNHK',course_code:'TC-RCC-2024-3',
    title_en:'Respiratory Critical Care Course',title_zh:'呼吸危重症護理課程',
    target_en:'Courses for Nurses',target_zh:'護士課程',
    url:'https://forms.gle/gyKAhr2BUSeiT1DD9',_raw_start_date:'6th June 2024'}),

  // ── HKSNE · Hong Kong Society for Nursing Education ───────────────────────
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Multiple-Choice Questions Design Workshop for Nurses 2026',
    title_zh:'護士多項選擇題（MCQ）設計工作坊 2026',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-03-06',end_date:'2026-03-13',time:'18:30-21:00',
    sessions:2,cne_points:5,cne_per_session:2.5,
    fee_hkd:500,fee_hkd_member:500,fee_hkd_nonmember:900,
    venue_en:'DLB407, David C. Lam Building, Hong Kong Baptist University',
    venue_zh:'香港浸會大學林護國際會議中心 DLB407 室',
    language_en:'English PowerPoint, Cantonese verbal',
    language_zh:'英文簡報，廣東話講授',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Dr. Sharron LEUNG','Dr. Grace YUEN','Prof. Joyce CHUNG'],
    url:'https://hksne.org.hk/media/courses/HKSNE_MCQ-Workshops-2026_Flyer_v5.pdf',
    _raw_start_date:'06 Mar 2026',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Cardiac Nursing Care Courses',title_zh:'心臟護理課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2025-08-07',end_date:'2025-08-21',time:'18:30-20:30',
    sessions:3,cne_points:6,cne_per_session:2,
    fee_hkd:700,fee_hkd_member:700,fee_hkd_nonmember:1000,
    venue_en:'AG101, The Hong Kong Polytechnic University',
    venue_zh:'香港理工大學 AG101 室',
    language_en:'English PowerPoint, Cantonese verbal',
    language_zh:'英文簡報，廣東話講授',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Ms. Man Ling, HUI FHKAN (Cardiac Care)'],
    url:'https://hksne.org.hk/media/courses/Flyer_HKSNE_Cardiac%20Nurisng%20Care%20Courses%202025.pdf',
    _raw_start_date:'07 Aug 2025',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Training Series on Understanding and Provision of Palliative Care to Patients and Their Carers 2024',
    title_zh:'病人及照顧者紓緩護理理解與提供培訓系列 2024',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2024-11-08',end_date:'2024-11-22',time:'19:00-21:00',
    sessions:3,cne_points:6,cne_per_session:2,
    fee_hkd:750,fee_hkd_member:750,fee_hkd_nonmember:1050,
    venue_en:'Online',venue_zh:'網上',
    teaching_mode_en:'Online',teaching_mode_zh:'網上',
    language_en:'English PowerPoint, Cantonese verbal',
    language_zh:'英文簡報，廣東話講授',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Dr. Chen Wai Tsan','Ms. Ko Po Shan Polly','Ms. Angelina LUK'],
    url:'https://hksne.org.hk/media/courses/2024-Flyer-HKSNE_Palliative-Care-course_updated-v2-3Oct2024.pdf',
    _raw_start_date:'08 Nov 2024',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Statistics Course 2024 — for Health Care Professionals',
    title_zh:'統計學課程 2024 — 醫護專業人員',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2024-07-06',end_date:'2024-07-06',
    sessions:3,cne_points:9,cne_per_session:3,
    fee_hkd:1800,fee_hkd_member:1800,fee_hkd_nonmember:2400,
    venue_en:'M407 Computer Lab, The Hong Kong Polytechnic University',
    venue_zh:'香港理工大學 M407 電腦實驗室',
    language_en:'English',language_zh:'英文',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Dr. Doris Y.P. Leung','Dr. Alan H.L. Tam'],
    _raw_start_date:'06 Jul 2024',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Invasive Mechanical Ventilation (IMV) Course',
    title_zh:'侵入性機械通氣（IMV）課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2024-01-12',end_date:'2024-01-26',time:'18:30-20:30',
    sessions:2,cne_points:4,cne_per_session:2,
    fee_hkd:500,fee_hkd_member:500,fee_hkd_nonmember:800,
    venue_en:'AG101, The Hong Kong Polytechnic University',
    venue_zh:'香港理工大學 AG101 室',
    language_en:'English PowerPoint, Cantonese verbal',
    language_zh:'英文簡報，廣東話講授',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Mr. Manual CHAN, FHKAN (Critical Care)'],
    _raw_start_date:'26 Jan 2024',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'The 3rd International Advanced Nursing Practice Conference 2025',
    title_zh:'第三屆國際高級護理實踐會議 2025',
    event_type_en:'Conference',event_type_zh:'會議',
    start_date:'2025-11-15',end_date:'2025-11-16',time:'08:45-16:35',
    venue_en:'Nina Hotel Tsuen Wan West',venue_zh:'香港荃灣西如心酒店',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    organizer:'Hong Kong Academy of Nursing & Midwifery',
    url:'https://hksne.org.hk/media/events/The%203rd%20International%20Advanced%20Nursing%20Practice%20Conference.jpg',
    _raw_start_date:'15 Nov 2025'}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Navigating Contemporary Challenges in Nursing Education and Research',
    title_zh:'探索當代護理教育與研究挑戰',
    event_type_en:'AGM + Scientific Meeting',event_type_zh:'週年大會暨學術會議',
    start_date:'2025-04-12',sessions:1,cne_points:1.5,
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Prof. William LI','Prof. Joyce CHUNG','Prof. Sylvia FUNG, BBS','Prof. Sally CHAN'],
    url:'https://hksne.org.hk/media/events/Flyer%20for%20HKSNE%20%26%20HKCERN%20AGM%202025.4.12%20Final.pdf',
    _raw_start_date:'12 Apr 2025',_pdf_enriched:true}),
  C({provider_code:'HKSNE',course_code:null,
    title_en:'Nursing in the Digital Age: How can AI and VR Technologies Promote Clinical Excellence',
    title_zh:'數碼時代的護理：AI 與 VR 科技如何促進臨床卓越',
    event_type_en:'AGM + Scientific Meeting',event_type_zh:'週年大會暨學術會議',
    start_date:'2024-04-27',time:'09:45-14:00',sessions:1,cne_points:1,
    fee_hkd:150,fee_hkd_member:150,fee_hkd_nonmember:200,
    venue_en:'Hong Kong Scout Centre 11/F',venue_zh:'香港童軍中心 11 樓',
    target_en:'Nursing Professionals',target_zh:'護理專業人員',
    speakers:['Dr Kitty Chan','Professor CHONG Yuen Yu, Connie'],
    _raw_start_date:'27 Apr 2024',_pdf_enriched:true}),

  // ── HKACCN · Hong Kong Association of Critical Care Nurses ────────────────
  C({provider_code:'HKACCN',course_code:'ECMO',
    title_en:'Extracorporeal Membrane Oxygenation (ECMO) Entry Course',
    title_zh:'體外膜氧合（ECMO）入門課程',
    start_date:'2026-03-07',time:'09:00-12:00 / 13:00-16:00',
    cne_points:6,fee_hkd:1800,fee_hkd_member:1800,fee_hkd_nonmember:2300,fee_early_bird_hkd:1500,
    venue_en:'Room 501, 5/F, Great Smart Tower, 230 Wan Chai Road, Hong Kong',
    venue_zh:'香港灣仔道 230 號偉智中心 5 樓 501 室',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/registration.php?course=ECMO',
    _raw_start_date:'7 March 2026 (Saturday)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'MCS',
    title_en:'Mechanical Circulatory Support (IABP + Impella) Entry Course',
    title_zh:'機械循環輔助裝置（IABP + Impella）入門課程',
    start_date:'2026-06-06',time:'09:00-12:00 / 13:00-16:00',
    cne_points:6,fee_hkd:1800,fee_hkd_member:1800,fee_hkd_nonmember:2300,fee_early_bird_hkd:1500,
    venue_en:'Room 501, 5/F, Great Smart Tower, 230 Wan Chai Road, Hong Kong',
    venue_zh:'香港灣仔道 230 號偉智中心 5 樓 501 室',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/registration.php?course=MCS',
    _raw_start_date:'6 June 2026 (Saturday)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ECCN-1A',
    title_en:'Elementary Critical Care Nursing Course 2026 — Module 1A: Respiratory System',
    title_zh:'初級危重症護理課程 2026 — 單元 1A：呼吸系統',
    start_date:'2026-02-23',time:'18:30-21:30',
    sessions:7,cne_points:21,
    fee_hkd:2500,fee_hkd_member:2500,fee_hkd_nonmember:2900,fee_early_bird_hkd:2300,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/forms/2026-ECCN-1A_v3.pdf',
    _raw_start_date:'23/2, 2/3, 9/3, 16/3, 23/3, 30/3, 13/4 (Mon)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ECCN-1B',
    title_en:'Elementary Critical Care Nursing Course 2026 — Module 1B: Respiratory System',
    title_zh:'初級危重症護理課程 2026 — 單元 1B：呼吸系統',
    start_date:'2026-06-15',time:'18:30-21:30',
    sessions:7,cne_points:21,
    fee_hkd:2500,fee_hkd_member:2500,fee_hkd_nonmember:2900,fee_early_bird_hkd:2300,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/forms/2026-ECCN-1B.pdf',
    _raw_start_date:'15/6, 22/6, 29/6, 6/7, 13/7, 20/7, 27/7 (Mon)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ECCN-2A',
    title_en:'Elementary Critical Care Nursing Course 2026 — Module 2A: Cardiovascular System',
    title_zh:'初級危重症護理課程 2026 — 單元 2A：心血管系統',
    start_date:'2026-04-21',time:'18:30-21:30',
    sessions:7,cne_points:21,
    fee_hkd:2500,fee_hkd_member:2500,fee_hkd_nonmember:2900,fee_early_bird_hkd:2300,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/forms/2026-ECCN-2A.pdf',
    _raw_start_date:'21/4, 28/4, 5/5, 12/5, 19/5, 26/5, 2/6',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ACCN-A',
    title_en:'Advanced Critical Care Nursing Course 2026 — Class A',
    title_zh:'高級危重症護理課程 2026 — A 班',
    start_date:'2026-06-04',time:'18:30-21:30',
    sessions:7,cne_points:21,
    fee_hkd:2500,fee_hkd_member:2500,fee_hkd_nonmember:2900,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/registration1.php?course=ACCN',
    _raw_start_date:'4/6, 11/6, 18/6, 25/6, 2/7, 9/7, 16/7 (Thu)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ACCN-B',
    title_en:'Advanced Critical Care Nursing Course 2026 — Class B',
    title_zh:'高級危重症護理課程 2026 — B 班',
    start_date:'2026-10-29',time:'18:30-21:30',
    sessions:7,cne_points:21,
    fee_hkd:2500,fee_hkd_member:2500,fee_hkd_nonmember:2900,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/registration1.php?course=ACCN',
    _raw_start_date:'29/10, 5/11, 12/11, 19/11, 26/11, 3/12, 10/12 (Thu)',_pdf_enriched:true}),
  C({provider_code:'HKACCN',course_code:'ECG-1-2',
    title_en:'ECG Course for Beginners 2026 (Class 1 & 2)',
    title_zh:'初學者心電圖（ECG）課程 2026（第 1 及 2 班）',
    start_date:'2026-01-14',time:'18:30-21:00',
    sessions:5,cne_points:12,
    fee_hkd:1500,fee_hkd_member:1500,fee_hkd_nonmember:1900,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Critical Care Nurses',target_zh:'危重症護理護士',
    url:'https://hkaccn.org/education_courses.php',
    _raw_start_date:'14 Jan 2026 onwards'}),

  // ── FPAHK · The Family Planning Association of Hong Kong ──────────────────
  C({provider_code:'FPAHK',course_code:null,
    title_en:'Continuing Nursing Education (CNE) Program 2025 in Sexual and Reproductive Health',
    title_zh:'2025 年度持續護理進修課程：性與生殖健康',
    event_type_en:'CNE Program',event_type_zh:'CNE 課程',
    start_date:'2025-05-15',end_date:'2025-06-05',
    cne_points:6,
    target_en:'Healthcare Professionals',target_zh:'醫護專業人員',
    enrollment_status_en:'Closed',enrollment_status_zh:'已結束',
    description_en:'Enhance and update nurses\u2019 knowledge of sexual and reproductive health, the nurse\u2019s role in promoting it, and confidence in dealing with reproductive and sex-related issues.',
    description_zh:'增進及更新護士在性與生殖健康方面的知識，了解護士的角色，並提升處理相關議題的信心。',
    url:'https://www.famplan.org.hk/en/events-and-programs/cme-cne-programs',
    _raw_start_date:'15 May 2025',_raw_cne:'6 CNE Points'}),

  // ── HKACS · Hong Kong Anti-Cancer Society ─────────────────────────────────
  C({provider_code:'HKACS',course_code:null,
    title_en:'Cancer Pain Management — Advanced Course for Oncology Nurses',
    title_zh:'癌症疼痛管理 — 腫瘤科護士進階課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-05-20',end_date:'2026-06-17',time:'19:00-21:00',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:5,
    cne_points:10,fee_hkd:1600,fee_hkd_member:1600,fee_hkd_nonmember:1900,
    venue_en:'Hong Kong Anti-Cancer Society Education Centre, Wong Chuk Hang',
    venue_zh:'香港黃竹坑香港防癌會教育中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Oncology Nurses / Healthcare Professionals',target_zh:'腫瘤科護士／醫護人員',
    url:'https://www.hkacs.org.hk/en/TrainingCourses.php',
    _raw_start_date:'20 May 2026',_raw_cne:'10 pts'}),
  C({provider_code:'HKACS',course_code:null,
    title_en:'Palliative Care Communication Skills Workshop',
    title_zh:'紓緩護理溝通技巧工作坊',
    event_type_en:'Workshop',event_type_zh:'工作坊',
    start_date:'2026-04-25',time:'09:30-17:00',sessions:1,
    cne_points:6,fee_hkd:850,
    venue_en:'Jockey Club Cancer Rehabilitation Centre, Wong Chuk Hang',
    venue_zh:'香港黃竹坑賽馬會癌症復康中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Nurses caring for cancer patients',target_zh:'照顧癌症病人護士',
    url:'https://www.hkacs.org.hk/en/TrainingCourses.php',
    _raw_start_date:'25 Apr 2026'}),

  // ── HKTB · HK Tuberculosis, Chest and Heart Diseases Association ──────────
  C({provider_code:'HKTB',course_code:null,
    title_en:'Tuberculosis Update 2026: Diagnosis, DOT & Drug-Resistance Management',
    title_zh:'2026 結核病新知：診斷、直接觀察治療及抗藥性管理',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-03-18',end_date:'2026-04-15',time:'19:00-21:00',
    day_of_week_en:'Wednesday',day_of_week_zh:'星期三',sessions:4,
    cne_points:8,fee_hkd:600,fee_hkd_member:600,fee_hkd_nonmember:800,
    venue_en:'HK Tuberculosis, Chest and Heart Diseases Association HQ, Wan Chai',
    venue_zh:'香港灣仔防癆心臟及胸病協會總部',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Nurses / Public Health Professionals',target_zh:'護士／公共衛生專業人員',
    url:'https://www.antitb.org.hk/en/training.php?cid=4',
    _raw_start_date:'18 Mar 2026'}),
  C({provider_code:'HKTB',course_code:null,
    title_en:'Respiratory Care Essentials for Community Nurses',
    title_zh:'社區護士呼吸護理基礎',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-06-09',time:'14:00-17:00',sessions:2,
    cne_points:6,fee_hkd:480,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Community / Public Health Nurses',target_zh:'社區／公共衛生護士',
    url:'https://www.antitb.org.hk/en/training.php?cid=4',
    _raw_start_date:'9 Jun 2026'}),

  // ── HKAM · Hong Kong Academy of Medicine ──────────────────────────────────
  C({provider_code:'HKAM',course_code:null,
    title_en:'Interdisciplinary Symposium on Patient Safety and Clinical Governance 2026',
    title_zh:'2026 跨專業病人安全與臨床管治研討會',
    event_type_en:'Conference',event_type_zh:'會議',
    start_date:'2026-09-26',time:'09:00-17:30',sessions:1,
    cne_points:6,fee_hkd:1200,fee_hkd_member:1000,fee_hkd_nonmember:1400,
    venue_en:'Hong Kong Academy of Medicine Jockey Club Building, Wong Chuk Hang',
    venue_zh:'香港黃竹坑香港醫學專科學院賽馬會大樓',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'All registered nurses and doctors',target_zh:'所有註冊護士及醫生',
    theme_en:'Building a culture of safety across disciplines',
    theme_zh:'建立跨專業安全文化',
    url:'https://www.hkam.org.hk/',
    _raw_start_date:'26 Sep 2026'}),

  // ── FMSHK · Federation of Medical Societies of Hong Kong ──────────────────
  C({provider_code:'FMSHK',course_code:null,
    title_en:'Updates in Diabetes Mellitus Management for Nurses',
    title_zh:'護士糖尿病管理新知',
    event_type_en:'Lecture',event_type_zh:'講座',
    start_date:'2026-05-05',time:'19:30-21:30',sessions:1,
    cne_points:2,fee_hkd:0,
    venue_en:'FMSHK Council Chamber, 4/F Duke of Windsor Social Service Building, Wan Chai',
    venue_zh:'香港灣仔溫莎公爵社會服務大廈 4 樓醫聯會會議廳',
    teaching_mode_en:'Hybrid',teaching_mode_zh:'混合模式',
    target_en:'Nurses / Allied Health',target_zh:'護士／專職醫療人員',
    url:'https://www.fmshk.org/',
    _raw_start_date:'5 May 2026',_raw_fee:'Free'}),
  C({provider_code:'FMSHK',course_code:null,
    title_en:'Geriatric Pharmacology: Polypharmacy and Deprescribing',
    title_zh:'老年藥理學：多重用藥與減藥',
    event_type_en:'Lecture',event_type_zh:'講座',
    start_date:'2026-06-23',time:'19:30-21:30',sessions:1,
    cne_points:2,fee_hkd:0,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Healthcare Professionals',target_zh:'醫護專業人員',
    url:'https://www.fmshk.org/',
    _raw_start_date:'23 Jun 2026'}),

  // ── HKSPMC · Hong Kong Society of Professional Medical Care ───────────────
  C({provider_code:'HKSPMC',course_code:null,
    title_en:'Foundation Course in Professional Medical Care for Registered Nurses',
    title_zh:'註冊護士專業護理基礎課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-04-11',end_date:'2026-06-13',time:'09:30-12:30',
    day_of_week_en:'Saturday',day_of_week_zh:'星期六',sessions:8,
    cne_points:24,fee_hkd:3200,fee_hkd_member:3200,fee_hkd_nonmember:3600,
    venue_en:'HKSPMC Training Centre, Mong Kok',
    venue_zh:'旺角專業護理學會培訓中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Registered Nurses',target_zh:'註冊護士',
    url:'https://www.hkspmc.org.hk/en/atrance.php',
    _raw_start_date:'11 Apr 2026'}),

  // ── HKCNHCM · College of Nursing Management Hong Kong ─────────────────────
  C({provider_code:'HKCNHCM',course_code:null,
    title_en:'Nursing Leadership and Change Management — Postgraduate Programme',
    title_zh:'護理領導與變革管理 — 進修課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-09-12',end_date:'2026-12-05',time:'18:30-21:30',
    day_of_week_en:'Friday',day_of_week_zh:'星期五',sessions:10,
    cne_points:30,fee_hkd:4800,fee_hkd_member:4800,fee_hkd_nonmember:5500,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Senior Nurses / Nurse Managers',target_zh:'資深護士／護理主管',
    entry_requirement_en:'Minimum 5 years registered-nurse experience',
    entry_requirement_zh:'須具備 5 年或以上註冊護士經驗',
    url:'https://hkcnhcm.org/',
    _raw_start_date:'12 Sep 2026'}),
  C({provider_code:'HKCNHCM',course_code:null,
    title_en:'Evidence-Based Practice for Frontline Nurse Leaders',
    title_zh:'前線護理主管實證實務',
    event_type_en:'Workshop',event_type_zh:'工作坊',
    start_date:'2026-05-30',time:'09:30-17:00',sessions:1,
    cne_points:6,fee_hkd:900,fee_hkd_member:900,fee_hkd_nonmember:1100,
    venue_en:'HKCNHCM Office, Kowloon Bay',
    venue_zh:'九龍灣護理管理學院辦事處',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Ward Managers / Senior Nurses',target_zh:'病房主管／資深護士',
    url:'https://hkcnhcm.org/',
    _raw_start_date:'30 May 2026'}),

  // ── HKEUS · Hong Kong Endoscopic Ultrasonography Society ──────────────────
  C({provider_code:'HKEUS',course_code:null,
    title_en:'Endoscopic Ultrasound (EUS) for Nurses: Introductory Course',
    title_zh:'護士內窺鏡超聲波（EUS）入門課程',
    event_type_en:'Course',event_type_zh:'課程',
    start_date:'2026-04-18',end_date:'2026-05-02',time:'14:00-17:00',
    day_of_week_en:'Saturday',day_of_week_zh:'星期六',sessions:3,
    cne_points:9,fee_hkd:1400,fee_hkd_member:1400,fee_hkd_nonmember:1700,fee_early_bird_hkd:1200,
    venue_en:'HKEUS Endoscopy Training Suite, Mongkok',
    venue_zh:'旺角內窺鏡超聲學會培訓中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Endoscopy / Procedure-Room Nurses',target_zh:'內窺鏡室／檢查室護士',
    url:'https://www.hkeus.org/',
    _raw_start_date:'18 Apr 2026'}),
  C({provider_code:'HKEUS',course_code:null,
    title_en:'Sedation Monitoring during EUS Procedures',
    title_zh:'EUS 檢查期間的鎮靜監察',
    event_type_en:'Lecture',event_type_zh:'講座',
    start_date:'2026-06-21',time:'19:00-21:00',sessions:1,
    cne_points:2,fee_hkd:300,fee_hkd_member:300,fee_hkd_nonmember:400,
    venue_en:'Zoom',venue_zh:'Zoom（網上）',
    teaching_mode_en:'Zoom',teaching_mode_zh:'Zoom 網上授課',
    target_en:'Endoscopy Nurses',target_zh:'內窺鏡室護士',
    url:'https://www.hkeus.org/',
    _raw_start_date:'21 Jun 2026'}),

  // ── HKANM · Hong Kong Academy of Nursing & Midwifery ──────────────────────
  C({provider_code:'HKANM',course_code:null,
    title_en:'Advanced Nursing Practice Conference 2026 — Plenary & Workshops',
    title_zh:'2026 高級護理實踐會議 — 大會及工作坊',
    event_type_en:'Conference',event_type_zh:'會議',
    start_date:'2026-11-14',end_date:'2026-11-15',time:'08:45-17:00',
    cne_points:12,fee_hkd:1800,fee_hkd_member:1500,fee_hkd_nonmember:2100,fee_early_bird_hkd:1300,
    early_bird_registration_deadline:'2026-08-31',
    registration_deadline:'2026-10-31',
    abstract_submission_deadline:'2026-07-15',
    abstract_acceptance_notification:'2026-08-30',
    venue_en:'Hong Kong Convention and Exhibition Centre, Wan Chai',
    venue_zh:'香港灣仔會議展覽中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    language_en:'English with simultaneous Cantonese interpretation',
    language_zh:'英文並設廣東話即時傳譯',
    target_en:'Advanced Practice Nurses / Midwives',target_zh:'高級護理及助產人員',
    theme_en:'Reimagining advanced nursing practice for the next decade',
    theme_zh:'重塑下一個十年的高級護理實踐',
    topics_en:['Specialty nursing innovations','Workforce transformation','Education and credentialing','Research translation'],
    topics_zh:['專科護理創新','人力資源轉型','教育與資歷認證','研究轉化'],
    url:'https://www.hkanm.hk/',
    _raw_start_date:'14 Nov 2026'}),
  C({provider_code:'HKANM',course_code:null,
    title_en:'Midwifery Skills Update — Antenatal and Postnatal Care',
    title_zh:'助產技能更新 — 產前及產後護理',
    event_type_en:'Workshop',event_type_zh:'工作坊',
    start_date:'2026-07-04',time:'09:30-16:30',sessions:1,
    cne_points:6,fee_hkd:950,fee_hkd_member:850,fee_hkd_nonmember:1050,
    venue_en:'HKANM Training Centre, Kowloon Tong',
    venue_zh:'九龍塘香港護理及助產學院培訓中心',
    teaching_mode_en:'Face-to-face',teaching_mode_zh:'面授',
    target_en:'Registered Midwives / Maternal Health Nurses',target_zh:'註冊助產士／母嬰健康護士',
    url:'https://www.hkanm.hk/',
    _raw_start_date:'4 Jul 2026'}),
];

// ─────────────────────────────────────────────────────────────────────────────
// Topic + format derivation (heuristic — real backend would supply these)
// ─────────────────────────────────────────────────────────────────────────────
function deriveTopic(t) {
  const s = (t || '').toLowerCase();
  if (/(bls|acls|pals|life support|sepsis|cardio|ecg|ultrasound|ventilation|ventilator|ecmo|critical|circulatory|impella|iabp|respiratory|生命支援|心電圖|危重|呼吸|心血管|機械循環)/.test(s)) return 'critical';
  if (/(wound|venipuncture|blood tak|nasal gastric|peg|sedation|anaesth|傷口|抽血|穿刺|鼻胃|靜脈|麻醉)/.test(s)) return 'wound';
  if (/(pediatric|paediatric|child development|asd|sen|primitive reflex|兒童|腦神經|兒科)/.test(s)) return 'paediatric';
  if (/(dementia|cognitive|elder|geriatr|認知障礙|老人)/.test(s)) return 'geriatric';
  if (/(listening|emotional|mental|stress|emotion|palliative|聆聽|情緒|紓緩|精神)/.test(s)) return 'mental';
  if (/(infection|hand hygiene|amr|tuberculosis|感染|傳染)/.test(s)) return 'infection';
  if (/(sexual|reproductive|family planning|aromatherapy|sports|core|aesthetic|exercise|香薰|運動|健康)/.test(s)) return 'palliative';
  if (/(leadership|management|workshop|education|research|statistics|mcq|teaching|digital|ai|vr|領導|管理|教育|研究|統計)/.test(s)) return 'leadership';
  if (/(skin|dermat|skin disease|皮膚)/.test(s)) return 'wound';
  return 'general';
}
const TOPIC_LABEL = {
  critical:   { en: 'Critical Care',     zh: '危重症' },
  wound:      { en: 'Wound Care',        zh: '傷口護理' },
  paediatric: { en: 'Paediatric',        zh: '兒科' },
  geriatric:  { en: 'Geriatric',         zh: '長者護理' },
  mental:     { en: 'Mental Health',     zh: '精神健康' },
  infection:  { en: 'Infection Control', zh: '感染控制' },
  palliative: { en: 'Wellness',          zh: '健康' },
  leadership: { en: 'Leadership',        zh: '管理' },
  general:    { en: 'General',           zh: '一般' },
};
function deriveFormat(t, sessions, teaching_mode_en) {
  const s = (t || '').toLowerCase();
  if (teaching_mode_en && /zoom|online/i.test(teaching_mode_en)) return 'elearning';
  if (/(workshop|provider course|practical|hands)/.test(s)) return 'workshop';
  if (Number(sessions) >= 3) return 'elearning';
  return 'lecture';
}
const FORMAT_LABEL = {
  workshop:  { en: 'Workshop',   zh: '工作坊' },
  lecture:   { en: 'Lecture',    zh: '講座' },
  elearning: { en: 'Course',     zh: '課程' },
};
function deriveCategory(topic) {
  if (['critical','infection'].includes(topic)) return 'core';
  if (['paediatric','wound','geriatric'].includes(topic)) return 'specialty';
  return 'elective';
}
// ── Enrolment-phrase vocabulary ─────────────────────────────────────────────
// ‼️ PHRASE BEFORE WORD — the ordering below is the whole point, do not sort
// these by "importance" or fold them back into one pass of single-word regexes.
// The catalogue builds its NEGATIONS out of the positive words: `Not full`
// contains "full", `Not Yet Open` contains "open". A word-first parser reads
// both BACKWARDS, and the backwards reading is the one the UI states as fact:
// `Not Yet Open` matched /\bopen\b/ and rendered 報名 as available on a congress
// whose registration had not opened (live row NCHK076::APCPM-20270320), while
// `Not full` fell through every branch to 'unknown' and told the nurse the
// provider had published nothing (live row com-30a64225171e4f7eafb4c0d6f169b9e7).
// Both were REVERSE FACTS, not missing data.
//
// The old defence was `&& !/\bnot\b/i` bolted onto the closed/full branches.
// That is an exception list, and it fails the moment a value carries a negation
// AND a real state: "Not full, registration closed" loses its `closed`. Match
// the specific phrase first and the general word last, and no exception list is
// needed — a negation is simply a more specific phrase than the word inside it.
//
// Regression: scripts/test-discover.mjs checks 10b–10j, which assert the
// negative invariant (never 'open') and not merely the positive mapping.
//
// ‼️ TWO REGEXES, BECAUSE "NOT OPEN" AND "OPENS SOON" ARE DIFFERENT FACTS.
// RE_NOT_YET_OPEN carries a FUTURE PROMISE — enrolmentStatusInfo renders it as
// 「即將開放」, which asserts the provider will open registration. Only phrasings
// that actually say so belong in it. `Not currently open` says the desk is shut
// and says NOTHING about reopening, so putting it here would have the UI make a
// stronger claim than the source supports — the exact failure this whole change
// exists to prevent, committed by its own fix.
// RE_NEGATED_OPEN carries no promise at all. It exists solely so that a negated
// `open` can never be mistaken for a positive one: without it, dropping
// `currently` from the line above sends `Not currently open` straight into
// RE_OPEN and back out as 'open' — a reverse fact, which is worse than the
// overclaim it was meant to fix. Matching it yields no state of its own; the row
// simply falls through to 'unknown' (待確認), which claims nothing.
//
//   Refusing to overclaim must not become a licence to claim the opposite.
//
// A live row spelled `Not currently open` should be treated as new information,
// not absorbed silently: decide then whether it deserves its own display state
// (currently_unavailable) rather than widening either regex to reach it.
const RE_NOT_YET_OPEN = /\bnot\s+(?:yet\s+)?open\b|opening\s*soon|尚未開放|即將開放/i;
const RE_NEGATED_OPEN = /\bnot\s+(?:\w+\s+){0,2}open\b/i;
const RE_NOT_FULL     = /\bnot\s*full\b|未滿|尚有名額|仍有名額|還有名額/i;
const RE_CLOSED       = /closed|ended|已截止|已結束|報名已結束/i;
const RE_WAITLIST     = /wait\s*list|waitlist|候補|輪候/i;
const RE_FULL         = /full|額滿|已滿/i;
const RE_OPEN         = /\bopen\b|accepting|registration\s+available|接受報名|現正招生|可報名/i;
function statusIsFull(r) {
  const s = (r.enrollment_status_en || '') + ' ' + (r.enrollment_status_zh || '');
  // Was /full|已滿|closed|ended/ — the same backwards read as the parser above,
  // scoring the documented value `Not full` (data.js's schema note: enrollment
  // _status_en is '"Full" | "Not full" | "Closed"') as FULL and handing the card
  // 0 remaining spots. Only RAW_COURSES (the bundled seed rows) reach this —
  // live rows go through normaliseSupabaseCourse — so nothing shipped wrong. It
  // is corrected anyway: a third private copy of this vocabulary is how the
  // first two drifted apart. Phrase before word, from the shared list.
  if (RE_NOT_FULL.test(s)) return false;
  return RE_FULL.test(s) || RE_CLOSED.test(s);
}
function spotsFromStatus(r, idx) {
  if (statusIsFull(r)) return { spots: 0, total: 50 };
  // deterministic-ish from idx so re-renders are stable
  return { spots: 8 + ((idx * 7) % 30), total: 60 };
}
// ─────────────────────────────────────────────────────────────────────────────
// Online delivery — LAYERED PRECEDENCE, not a flat keyword sweep.
//
// ‼️ WHY LAYERS AND NOT ONE REGEX OVER EVERYTHING. The obvious implementation
// greps a single haystack of mode + venue + platform + title. It is wrong in
// the one direction that costs a nurse her afternoon: a title like "Online
// Registration for Face-to-Face Workshop" or "Online materials provided after
// classroom course" flips an on-site course to Online, and someone who filtered
// for Online plans to attend from home. A flat OR can never express "the
// provider explicitly said face-to-face, so stop guessing from the title" —
// only an ordered fallback can. The asymmetry is deliberate: a false NEGATIVE
// just means she doesn't find the course under the Online filter; a false
// POSITIVE sends her travelling. On contradictory data we take the negative.
//
//   1. teaching_mode (en/zh) — the provider's own structured statement, and the
//      only layer that resolves BOTH ways. 'Face-to-face'/'面授' settles it as
//      on-site and STOPS. 'Self-study'/'自學' deliberately does NOT resolve —
//      it is a study-method claim, not a location claim (a self-study course can
//      be posted or online), so it falls through to the evidence below. That
//      fall-through is what lets the JCIMED e-learning rows (mode=Self-study,
//      "… Online Course" in the title) be found at all.
//   2. online_platform / venue — explicit delivery evidence. NOTE
//      online_platform carries 'Pre-recorded video' on 17 live rows, which the
//      old keyword sweep (/zoom|online|hybrid|virtual/) missed completely.
//   3. title — inference of LAST resort, only when 1 and 2 said nothing at all.
//
// Returns { online, onlineSource }. Keep the provenance: 'explicit' means a
// provider field decided it, 'inferred' means only our title guess did, null
// means nothing did. The crowdsource correction queue should be able to
// prioritise `inferred` rows for human confirmation, and an admin needs to know
// whether "Online" came from the provider or from us.
//
// Hybrid counts as online ('Hybrid', '混合模式', 'Mixed (Zoom + Face-to-face)'):
// the course IS attendable online, and those values name both modes, which is
// why the online test runs BEFORE the on-site test inside layer 1.
const ONLINE_MODE_RE  = /online|zoom|hybrid|virtual|webinar|網上|線上|混合模式/i;
const ONSITE_MODE_RE  = /face.?to.?face|in.?person|classroom|on.?site|面授|實體/i;
const ONLINE_TITLE_RE = /\bonline\b|\bzoom\b|webinar|e-?learning|網上|線上/i;
function deriveOnline(r) {
  // 1. Structured mode — resolves either way.
  const mode = (r.teaching_mode_en || '') + ' ' + (r.teaching_mode_zh || '');
  if (ONLINE_MODE_RE.test(mode)) return { online: true,  onlineSource: 'explicit' };
  if (ONSITE_MODE_RE.test(mode)) return { online: false, onlineSource: 'explicit' };
  // 2. Explicit delivery evidence. A named platform IS the statement — its
  //    value ('Pre-recorded video') need not contain an "online" keyword.
  if (String(r.online_platform || '').trim()) return { online: true, onlineSource: 'explicit' };
  if (ONLINE_MODE_RE.test((r.venue_en || '') + ' ' + (r.venue_zh || ''))) {
    return { online: true, onlineSource: 'explicit' };
  }
  // 3. Title — last resort only.
  if (ONLINE_TITLE_RE.test((r.title_en || '') + ' ' + (r.title_zh || ''))) {
    return { online: true, onlineSource: 'inferred' };
  }
  return { online: false, onlineSource: null };
}

// ── Delivery mode — WHICH mode, not "is any of it online" ───────────────────
//
// ‼️ deriveOnline CANNOT OWN THIS, AND THE FILTER TRIED. deriveOnline answers a
// boolean ("is there an online component") and folds Hybrid into `true` by
// design; a boolean cannot express in_person at all. So Discover's 「形式」 facet
// read `teaching_mode_en` raw instead — which on 2026-08-16 was NULL on 125 of
// the 196 unfinished courses, and split one concept across several strings
// (Online / Zoom / Online (Zoom); Face-to-face / In-person). The result: the
// quick 網上 chip matched 35 rows and the advanced 形式=網上 chip matched 7.
// One word, one app, two answers.
//
// Same layered evidence as deriveOnline, and the same asymmetry: on
// contradictory data prefer the reading that does not send her travelling.
//
//   1. teaching_mode — the provider's own statement. Resolves all four ways.
//      A value naming BOTH modes (Mixed (Zoom + Face-to-face), Zoom + 面授) or
//      the word hybrid/混合 is hybrid, not online.
//   2. online_platform / venue — delivery evidence, COMBINED rather than
//      ranked: a Zoom link AND a physical venue is hybrid, and a precedence
//      order would silently call it online. Zero live rows hit that branch on
//      2026-08-16; it is implemented anyway because the semantics are already
//      decided and only the data is missing, and pinned by a synthetic fixture
//      so a later "simplification" back to precedence fails loudly.
//   3. Nothing → unknown. ‼️ NO TITLE INFERENCE in v1. Layers 1–2 already lift
//      coverage from 36% to 76%, and the language facet is the cautionary
//      case: all 17 Cantonese-taught unfinished courses carry a pure-English
//      title, so title language predicts teaching language at 0% accuracy.
//      Adding a title layer needs its own blast-radius measurement first.
//
// Returns { mode, state, source } — the conclusion, its epistemic state, and
// WHICH evidence produced it, kept apart on purpose. onlineSource collapses
// three different layers into one 'explicit' and so cannot answer "why", which
// is exactly the question an admin reviewing a wrong row has to ask.
// ‼️ A PLACEHOLDER IS NOT A PLACE. The physical-evidence test was "venue text
// exists and does not look online", which reads 「待定」 as a lecture theatre and
// sends a nurse to an address nobody has published yet. Audited 2026-08-18 over
// the 76 rows this branch actually resolves: 21 distinct values, every one a
// real address, zero placeholders — so this changes no live row today.
//
// It is implemented anyway, for the same reason as the dual-evidence hybrid
// branch: the semantics are already decided and only the data is missing. The
// alternative is discovering it the day a provider types TBC, when the row is
// already captioned 面授 and nobody is looking.
//
// Matched as the WHOLE value, never as a substring — 「九龍尖沙咀…」 contains no
// placeholder, but a substring rule would eventually decide some real address
// does. Anything unrecognised stays physical evidence: this guard exists to
// refuse known non-answers, not to demand a recognised address format.
const VENUE_PLACEHOLDER_RE = new RegExp(
  '^(?:tbc|t\\.b\\.c\\.?|tba|n\\.?/?a\\.?|nil|none|null|-+|\u2014+|\\?+'
  + '|to\\s+be\\s+(?:confirmed|advised|announced|determined)'
  + '|venue\\s+to\\s+be\\s+(?:confirmed|advised|announced)'
  + '|details?\\s+to\\s+follow|see\\s+(?:website|details|email)'
  + '|various(?:\\s+(?:locations?|venues?))?|multiple\\s+(?:locations?|venues?)'
  + '|\u5f85\u5b9a|\u5f85\u78ba\u8a8d|\u672a\u5b9a|\u53e6\u884c\u901a\u77e5|\u7a0d\u5f8c\u516c\u5e03|\u5bb9\u5f8c\u516c\u5e03|\u5f85\u516c\u5e03'
  + ')$', 'i');
// ‼️ NO LENGTH FLOOR. This carried `if (v.length < 3) return false` on the
// reasoning that a two-character string is "too short to be an address". That
// judged the evidence against the WRONG FACT: the question here is whether the
// course requires being somewhere in person, not whether the string is enough
// to navigate by. `KH` ends three live venue values as the hospital itself, and
// the catalogue uses 4-character venues as complete answers — `HKSH`, `東華三院`.
// To a Hong Kong nurse those are unambiguous physical places.
//
//   Evidence sufficiency is judged against the fact being derived, not against
//   a stronger downstream use of it.
//
// Same distinction as the registration work one commit earlier: a course-outline
// PDF is insufficient evidence of a booking page and sufficient evidence of a
// course-specific destination. Measured before removal: 0 of the 160 venues on
// unfinished courses are under 3 characters, so the floor rejected nothing and
// only stood ready to reject the wrong thing.
//
// What remains refuses KNOWN non-answers and nothing else. `-`, `?` and `NA`
// are in the placeholder list on their own merits, not because they are short.
function venueIsPhysical(venue) {
  const v = String(venue || '').trim().replace(/\s+/g, ' ');
  if (!v) return false;
  return !VENUE_PLACEHOLDER_RE.test(v);
}
const HYBRID_MODE_RE = /hybrid|混合/i;
function deriveDeliveryMode(r) {
  const mode = (r?.teaching_mode_en || '') + ' ' + (r?.teaching_mode_zh || '');
  if (String(mode).trim()) {
    const on = ONLINE_MODE_RE.test(mode);
    const off = ONSITE_MODE_RE.test(mode);
    if (HYBRID_MODE_RE.test(mode) || (on && off)) {
      return { mode: 'hybrid', state: 'observed', source: 'teaching_mode' };
    }
    if (on)  return { mode: 'online',    state: 'observed', source: 'teaching_mode' };
    if (off) return { mode: 'in_person', state: 'observed', source: 'teaching_mode' };
    // Self-study / 自學 land here on purpose: a study-method claim is not a
    // location claim, so it falls through to the evidence below exactly as it
    // does in deriveOnline.
  }
  const venue = (r?.venue_en || '') + ' ' + (r?.venue_zh || '');
  const hasPlatform = !!String(r?.online_platform || '').trim();
  const venueOnline = ONLINE_MODE_RE.test(venue);
  const hasPhysical = venueIsPhysical(venue) && !venueOnline;
  const onlineEvidence = hasPlatform || venueOnline;

  if (onlineEvidence && hasPhysical) {
    return { mode: 'hybrid', state: 'derived', source: 'platform+venue' };
  }
  if (onlineEvidence) {
    return { mode: 'online', state: 'derived', source: hasPlatform ? 'online_platform' : 'venue' };
  }
  if (hasPhysical) return { mode: 'in_person', state: 'derived', source: 'venue' };
  return { mode: 'unknown', state: 'unknown', source: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Fee normalisation — SINGLE SOURCE OF TRUTH for the fee display contract.
// Every render site must consume these outputs, never raw fee_hkd. Contract
// (shared with the daily scraper routine — see docs/coexistence-contract.md):
//
//   DB state                        | meaning        | UI must show
//   --------------------------------|----------------|------------------------
//   fee_hkd = 0 (or explicit 0 tier)| verified free  | 免費 / Free
//   ALL fee fields null             | UNKNOWN fee    | 費用待確認 — NEVER 免費
//   any positive fee/tier           | actual price   | HKD $N (+ tier lines)
//
// null is NOT zero. The scraper writes fee_hkd = null when it cannot extract
// a fee; ~half the catalogue is in that state. Rendering unknown as "free"
// destroys user trust ("shows free, charges $1900"). There is no lint rule
// that can catch null-vs-0 semantics — this choke point + the states below
// are the guard. Three mutually exclusive states:
//   feeUnknown — no fee field carries any value (null/undefined across the
//                board). Show 費用待確認; exclude from Free AND Paid filters.
//   isFree     — at least one fee field is present and none is positive
//                (explicit zero evidence; the routine's S10 gate guarantees
//                fee_hkd=0 only ships with 免費 wording in the source).
//   paid       — otherwise; costFrom is the headline amount (> 0 here).
// A course with member/non-member prices but no base fee is paid, not free.
// ─────────────────────────────────────────────────────────────────────────────
function _posFee(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function deriveFees(r) {
  const base  = _posFee(r.fee_hkd);
  const tiers = [
    _posFee(r.fee_hkd_member),
    _posFee(r.fee_hkd_nonmember),
    _posFee(r.fee_early_bird_hkd),
    _posFee(r.fee_per_session_hkd),
  ].filter(Boolean);
  // Any fee field present at all (an explicit 0 counts — it is scraped
  // evidence, unlike null which means "could not extract").
  const hasFeeSignal = [
    r.fee_hkd, r.fee_hkd_member, r.fee_hkd_nonmember,
    r.fee_early_bird_hkd, r.fee_per_session_hkd,
  ].some(v => v !== null && v !== undefined);
  return {
    // Raw base fee for the field-edit read-back. null stays null so the fee
    // editor prefills EMPTY for an unknown fee — an admin must never be able
    // to "confirm" a $0 fee by applying an untouched prefill.
    cost:          r.fee_hkd ?? null,
    costMember:    r.fee_hkd_member    ?? null,
    costNonMember: r.fee_hkd_nonmember ?? null,
    costEarlyBird: r.fee_early_bird_hkd ?? null,
    // Unknown: nothing scraped/reported. NEVER render 免費 for this state.
    feeUnknown:    !hasFeeSignal,
    // Free only with explicit zero evidence and no positive tier anywhere.
    isFree:        hasFeeSignal && !base && tiers.length === 0,
    // Effective headline price: the explicit base fee, else the lowest tier.
    // Guaranteed > 0 whenever isFree and feeUnknown are both false.
    costFrom:      base || (tiers.length ? Math.min(...tiers) : 0),
    // What a member actually saves, in HKD. Only meaningful when BOTH tiers are
    // published AND the non-member price is genuinely higher — providers often
    // repeat the same number in both columns (66 of 130 active courses publish
    // both; a good share of those are equal), and "Save $0" is noise. null =
    // don't render the badge.
    memberSaving: (() => {
      const m = _posFee(r.fee_hkd_member);
      const nm = _posFee(r.fee_hkd_nonmember);
      return m && nm && nm > m ? nm - m : null;
    })(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Discover status axes — pure, client-side derivations.
//
// These values deliberately are NOT stored in cne_courses or the offline cache:
// event and registration state change as the calendar advances. Every helper
// accepts an injectable ISO `today` so the boundary rules stay regression-
// testable without mocking Date.
// ─────────────────────────────────────────────────────────────────────────────
function _axisToday(today) {
  if (today) return String(today).slice(0, 10);
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
// Public alias for _axisToday's no-argument form — the ONE way any caller
// should obtain "today" as YYYY-MM-DD.
//
// ‼️ NEVER use `new Date().toISOString().slice(0, 10)` for a calendar date.
// That converts to UTC first, so between 00:00 and 08:00 Hong Kong time it
// returns YESTERDAY and every date axis runs a day behind: deadlines that
// expired yesterday still read "open" and stay in the closing-soon strip, and
// courses that ended yesterday still read "ongoing" instead of moving to the
// Ended tab — for the whole small-hours window a night-shift nurse is browsing.
// Regression: scripts/test-discover.mjs checks 27–29. The same rule is stated
// in record-extras.jsx (todayLocalIso) and enforced server-side by clock.ts in
// supabase/functions/notify-cron.
function todayIso() { return _axisToday(); }

// ─────────────────────────────────────────────────────────────────────────────
// CNE POINTS — the one normalisation every submission entry path goes through.
// GitHub issue #42; contract frozen 2026-09-12, evidence in that issue.
//
// ‼️ THE CONTRACT, AND IT IS OLDER THAN THIS FUNCTION.
//
//     blank / whitespace-only   →  null            "no value"
//     null                      →  null            no value known
//     0                         →  0               a LEGAL, DELIBERATE zero
//     positive finite decimal   →  accepted
//     negative                  →  invalid
//     non-numeric               →  invalid
//     NaN / Infinity            →  invalid
//
// ‼️ `0` AND `null` ARE DIFFERENT FACTS AND MUST NEVER COLLAPSE INTO EACH OTHER.
// RELEASE.md §30 already ruled on this — "blank points on an unfinished course
// = not published yet, which is NOT the same as a deliberate 0" — and built the
// `PTSTBD:1` marker to keep them apart in `records`, whose `points` column is
// NOT NULL and therefore cannot hold the distinction itself. Production carries
// four real deliberate zeros (an AGM, a mandatory cybersecurity refresher, a
// Basic Infection Control course, an AHA BLS card that certifies no CNE value).
//
// The defect this fixes: the co-submit door wrote `Number(form.points) || null`,
// and `0` is falsy after `Number()`, so a deliberately-entered zero became
// "unknown" in the community submission. Same information loss §30 exists to
// prevent, in the opposite direction.
//
// ‼️ A DISCRIMINATED RESULT, NOT `{ value, valid }`. The entire defect is
// information states being collapsed, so the shape must make collapsing them
// take effort: there is no `value` on an invalid result to read by accident.
//
// ‼️ NO STEP VALIDATION. Production's smallest observed value is 0.5 and 121
// rows are fractional, but that is an OBSERVATION, not a grid. Nothing
// establishes a 0.5 step, so nothing here enforces one.
//
// ‼️ NO ROUNDING EITHER. The extractor rounds its own model output to 2dp
// before it ever reaches a form; re-rounding a value a human typed would be a
// second, unstated rule.
function normalizeCnePointsInput(raw) {
  if (raw === null || raw === undefined) return { status: 'empty', value: null };
  // ‼️ TRIM AND TEST FOR EMPTY *BEFORE* Number(). `Number('')` and `Number('  ')`
  // are both 0 — the one coercion that would silently manufacture a deliberate
  // zero out of a blank field, which is precisely the contract's central rule.
  const text = String(raw).trim();
  if (!text) return { status: 'empty', value: null };
  const n = Number(text);
  if (Number.isNaN(n)) return { status: 'invalid', reason: 'non_numeric' };
  if (!Number.isFinite(n)) return { status: 'invalid', reason: 'non_finite' };
  if (n < 0) return { status: 'invalid', reason: 'negative' };
  return { status: 'valid', value: n };
}

// What a SUBMISSION payload should carry, or null when there is nothing to say.
//
// ‼️ THIS IS THE ONLY HELPER THAT RETURNS A BARE VALUE, AND IT REFUSES TO
// ANSWER FOR INVALID INPUT — it throws rather than hand back a null, because
// "the nurse typed something impossible" and "the nurse said nothing" are the
// two facts this whole issue exists to keep apart. Callers must test the status
// themselves and stop; see the three submission doors in screens.jsx.
function cnePointsForSubmission(raw) {
  const r = normalizeCnePointsInput(raw);
  if (r.status === 'invalid') {
    throw new Error(`cne_points is invalid (${r.reason}) — the caller must check status before writing`);
  }
  return r.value;
}

// ─── What the evidence panel may SAY about a row with no files (issue #52) ───
//
// ‼️ DESCRIBE WHAT THE SYSTEM KNOWS; NEVER REWRITE STORAGE STATE AS A PERSON'S
// ACT. "No files right now" is storage state. "The nurse never attached any" is
// a claim about a person, and storage cannot support it: the retention purge
// empties `evidence_urls` on rows that DID have files. That is the §116 failure
// class, and this panel said 「無附件證據」 on exactly such rows.
//
// Four states, frozen by John 2026-09-27, decided from TWO inputs only:
//
//   files      current files exist → show them. Purge metadata never overrides
//              files. A purged draft can be re-uploaded and finalized, so a
//              pending row may carry BOTH files and a stale purge stamp.
//   removed    no files + evidence_purged_file_count > 0 → the ONLY state that
//              may say evidence was removed under the retention policy.
//   neutral    no files + purged + count NULL (the 122 pre-#52A legacy rows) or
//              0 → neither "there was evidence" nor "there never was".
//              ‼️ 0 proves only that the purge deleted nothing, not that the
//              nurse never attached anything.
//   none       no files + never purged → "currently no evidence attached".
//
// ‼️ DELIBERATELY NOT AN INPUT: artifact_digest. It proves some evidence once
// existed, but not that THIS purge removed it. Legacy rows are not upgraded
// from neutral to removed on inference. The unknown stays unknown.
//
// Pure: returns a state, never an action. The panel stays display-only.
function evidenceDisplayState(row) {
  const paths = Array.isArray(row?.evidence_urls)
    ? row.evidence_urls.filter(p => typeof p === 'string' && p)
    : [];
  if (paths.length) return { kind: 'files', paths };
  const purgedAt = typeof row?.evidence_purged_at === 'string' && row.evidence_purged_at
    ? row.evidence_purged_at : null;
  const count = row?.evidence_purged_file_count;
  // ‼️ The purge runs ~21:30Z = 05:30 the NEXT day in Hong Kong, and _dateOnly()
  // takes the literal UTC prefix — so a UTC slice would show the previous day.
  // HK has had no DST since 1979 (see the ICS note below), so +8h is exact.
  const t = purgedAt ? Date.parse(purgedAt) : NaN;
  const purgedOn = Number.isFinite(t) ? new Date(t + 8 * 3600e3).toISOString().slice(0, 10) : null;
  // Strictly a positive INTEGER, alongside a stamp. A string "3", NaN or a
  // count without a stamp (the database CHECK forbids that) proves nothing.
  if (purgedAt && Number.isInteger(count) && count > 0) return { kind: 'removed', purgedAt, purgedOn, count };
  if (purgedAt) return { kind: 'neutral', purgedAt, purgedOn };
  return { kind: 'none' };
}

function _sessionDates(course) {
  return (Array.isArray(course?.sessionsList) ? course.sessionsList : [])
    .map(item => {
      if (typeof item === 'string') return item.slice(0, 10);
      if (item && typeof item === 'object') {
        return String(item.date || item.start_date || item.start || '').slice(0, 10);
      }
      return '';
    })
    .filter(value => /^\d{4}-\d{2}-\d{2}$/.test(value))
    .sort();
}
function _enrolmentText(course) {
  return [
    course?.status,
    course?.enrollmentStatus?.en,
    course?.enrollmentStatus?.zh,
  ].filter(Boolean).join(' ');
}
function deriveEventStatus(course, today) {
  const statusText = _enrolmentText(course);
  if (/\bcancell?ed\b|已取消/i.test(statusText)) return 'cancelled';

  const sessions = _sessionDates(course);
  const effectiveStart = course?.date || sessions[0] || null;
  const effectiveEnd = course?.endDate || sessions[sessions.length - 1] || null;
  const now = _axisToday(today);
  if (!effectiveStart) return 'date_tbc';
  if (effectiveStart > now) return 'scheduled';
  if ((effectiveEnd || effectiveStart) >= now) return 'ongoing';
  return 'completed';
}
function deriveRegistrationStatus(course, today) {
  if (!course) return 'unknown';
  if (course.lifecycleStatus && course.lifecycleStatus !== 'active') return 'closed';

  const eventStatus = deriveEventStatus(course, today);
  if (eventStatus === 'completed' || eventStatus === 'cancelled') return 'not_applicable';

  const now = _axisToday(today);
  if (course.registrationDeadline && String(course.registrationDeadline).slice(0, 10) < now) {
    return 'closed';
  }

  const statusText = _enrolmentText(course);
  // Vocabulary (RE_*) is defined once, above statusIsFull — the earliest reader.
  // 1) Explicit negations and phase phrases — these MUST outrank the bare words
  //    they contain (see the vocabulary note above).
  if (RE_NOT_YET_OPEN.test(statusText)) return 'closed';
  // 2) Registration-phase evidence outranks CAPACITY evidence. `Not full` says
  //    seats remain; it does NOT say the desk is open. So a row reading
  //    "Not full, registration closed" must resolve `closed`, which is only true
  //    if the closed/waitlist checks run BEFORE the not-full check. Capacity and
  //    phase are two axes crushed into one enum here; until they are split, the
  //    ordering is what keeps the enum from claiming the wrong one.
  if (RE_CLOSED.test(statusText)) return 'closed';
  if (RE_WAITLIST.test(statusText)) return 'waitlist';
  // 3) Capacity phrasing. `Not full` is the only positive-availability signal in
  //    the live catalogue that is spelled as a negation, so it resolves 'open'
  //    under today's 4-value enum — an approximation this comment exists to flag:
  //    what the source actually proved is "seats remain", not "you may register
  //    now". Splitting registrationPhase from capacityStatus is the real fix.
  if (RE_NOT_FULL.test(statusText)) return 'open';
  if (course.seats?.kind === 'full' || RE_FULL.test(statusText)) return 'full';
  // 4) Bare words last — and never a negated one. This is NOT the old blanket
  //    `!/\bnot\b/` guard reinstated: that voided `closed` on any string
  //    containing "not". This tests only whether the word `open` ITSELF is
  //    negated, which is a phrase-level fact about the same token, and it is the
  //    only thing standing between `Not currently open` and a 可報名 badge.
  if (!RE_NEGATED_OPEN.test(statusText)
      && (RE_OPEN.test(statusText)
        || ['open', 'limited', 'unlimited'].includes(course.seats?.kind))) {
    return 'open';
  }
  return 'unknown';
}
function deriveCneStatusNorm(course) {
  const raw = course?.raw || {};
  const text = [
    course?.cneStatus?.en,
    course?.cneStatus?.zh,
    raw.cne_status_en,
    raw.cne_status_zh,
  ].filter(Boolean).join(' ');
  if (/\bconfirmed\b|已確認/i.test(text)) return 'confirmed';
  if (/pending|\btbc\b|待確認|待批|provider\s+default|inferred|推算/i.test(text)
      || raw._cne_points_inferred === true
      || course?.provenance?.cne_points?.inferred === true) {
    return 'pending';
  }
  return 'unknown';
}
function derivePublicationStatus(course) {
  if (course?.lifecycleStatus === 'archived') return 'archived';
  const active = !course?.lifecycleStatus || course.lifecycleStatus === 'active';
  const verified = !course?.verificationStatus
    || ['verified', 'community_supported'].includes(course.verificationStatus);
  return active && verified ? 'published' : 'review';
}
// ── Calendar export (RFC 5545) ──────────────────────────────────────────────
// Turns a course into an .ics VEVENT so a nurse can drop it into the same
// calendar her roster lives in — the single most-requested "why can't I just…"
// for shift workers.
//
// Times come from time_text (the only column that carries them at any real
// coverage — see deriveShiftTag). When there is no parseable time we emit an
// ALL-DAY event (DATE value type) rather than inventing 09:00: a wrong time in
// someone's calendar is worse than an all-day block they can adjust.
//
// TZID=Asia/Hong_Kong with no VTIMEZONE block: HK has had no DST since 1979 and
// a fixed +08:00 offset, so every consumer resolves this from the IANA database
// identically. Emitting floating local times instead would shift the event for
// a nurse whose device is temporarily on another timezone.
function _icsEscape(s) {
  return String(s == null ? '' : s)
    .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}
function _icsFold(line) {
  // RFC 5545 §3.1: lines over 75 octets must be folded with CRLF + one space.
  if (line.length <= 73) return line;
  const out = [line.slice(0, 73)];
  let rest = line.slice(73);
  while (rest.length > 72) { out.push(' ' + rest.slice(0, 72)); rest = rest.slice(72); }
  if (rest) out.push(' ' + rest);
  return out.join('\r\n');
}
// "7:00pm - 9:00pm" / "18:30-21:30" / "9:00 - 13:15" → { start:'19:00', end:'21:00' }
function _icsTimeRange(timeText) {
  if (!timeText) return null;
  const t = String(timeText);
  const RE = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi;
  const found = [];
  let m;
  while ((m = RE.exec(t)) !== null && found.length < 2) {
    let h = Number(m[1]);
    const min = m[2] != null ? Number(m[2]) : 0;
    const ap = (m[3] || '').toLowerCase();
    // A bare 1–2 digit number with no colon and no am/pm is a session count or
    // a "Day 1" marker, not a clock reading — require real time evidence.
    if (m[2] == null && !ap) continue;
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (h > 23 || min > 59) continue;
    found.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
  }
  if (!found.length) return null;
  return { start: found[0], end: found[1] || null };
}
function courseToIcs(course, lang = 'zh') {
  if (!course || !course.date) return null;
  const dt = (iso, hhmm) => iso.replace(/-/g, '') + (hhmm ? 'T' + hhmm.replace(':', '') + '00' : '');
  const range = _icsTimeRange(course.time || course.raw?.time_text);
  const startDay = course.date;
  const endDay = course.endDate || course.date;

  const lines = [
    'BEGIN:VEVENT',
    `UID:vital-${_icsEscape(course.id)}@vitalcne.com`,
    // DTSTAMP must be a UTC instant — it is when the file was produced, not a
    // local wall-clock time.
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`,
    `SUMMARY:${_icsEscape(L(course.title, lang))}`,
  ];
  if (range) {
    // ‼️ A timed event ends on the SAME DAY it starts. Using course.endDate
    // here produced monsters: a weekly evening course running 22 Jul – 12 Aug
    // became ONE continuous three-week 19:00→21:00 block sitting across the
    // nurse's whole calendar. The timed event represents the FIRST SESSION;
    // the remaining sessions are noted in the description below.
    lines.push(`DTSTART;TZID=Asia/Hong_Kong:${dt(startDay, range.start)}`);
    // No printed end time → assume 2 hours rather than a zero-length event,
    // which some calendars render as an unreadable sliver.
    const endHHMM = range.end || (() => {
      const [h, mi] = range.start.split(':').map(Number);
      return `${String(Math.min(23, h + 2)).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
    })();
    lines.push(`DTEND;TZID=Asia/Hong_Kong:${dt(startDay, endHHMM)}`);
  } else {
    // All-day: DTEND is EXCLUSIVE in RFC 5545, so add one day.
    const [y, mo, d] = endDay.split('-').map(Number);
    const next = new Date(y, mo - 1, d + 1);
    const nextIso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
    lines.push(`DTSTART;VALUE=DATE:${dt(startDay)}`);
    lines.push(`DTEND;VALUE=DATE:${dt(nextIso)}`);
  }
  const where = L(course.venue, lang) || (course.online ? (lang === 'zh' ? '網上' : 'Online') : '');
  if (where) lines.push(`LOCATION:${_icsEscape(where)}`);
  // Multi-session courses: the event above covers the FIRST session only, so
  // say so rather than leaving the nurse to discover it by missing session two.
  const multi = range && endDay && endDay !== startDay
    ? (lang === 'zh'
        ? `此課程共多節,最後一節為 ${endDay}（此項目只包含第一節）`
        : `Multi-session course running to ${endDay} — this entry covers the first session only`)
    : '';
  const desc = [
    L(course.providerShort, lang) || L(course.provider, lang),
    course.points ? `${course.points} CNE ${lang === 'zh' ? '分' : 'points'}` : '',
    multi,
    course.registrationUrl || '',
  ].filter(Boolean).join(' · ');
  if (desc) lines.push(`DESCRIPTION:${_icsEscape(desc)}`);
  lines.push('END:VEVENT');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vital//Nurse CNE//EN',
    'CALSCALE:GREGORIAN',
    ...lines,
    'END:VCALENDAR',
  ].map(_icsFold).join('\r\n') + '\r\n';
}

// ── Shift-friendliness ──────────────────────────────────────────────────────
// A Hong Kong nurse on rotating shifts filters on one question first: "can I
// physically attend this?" An evening or weekend session is attendable around a
// day shift; a Tuesday 10am one is not.
//
// SOURCE OF TRUTH — measured against the live catalogue (2026-07-26), because
// the obvious guess is wrong:
//   • sessions_list carries a time on 1 of 130 active courses → useless here.
//   • time_text carries one on 67 of 130 (52%) → this is the real source.
//     Formats seen: "7:00pm - 9:00pm", "9:00 - 13:15", "18:30-21:30",
//     "Day 1 08:30 - 18:00; Day 2 08:30 - 13:00".
//   • day_of_week_en covers 40 of 130, but start_date covers 107 — so weekday
//     is derived from the DATE, with the text column only as a cross-check.
//
// Returns null when there is no evidence. NEVER guess: an unknown start time
// must not render as "not evening", or the filter quietly hides courses a nurse
// could have attended.
function _firstStartHour(timeText) {
  if (!timeText) return null;
  // First clock reading in the string — "Day 1 08:30 - 18:00" starts at 08:30.
  const m = String(timeText).match(/(\d{1,2}):(\d{2})\s*(am|pm)?|(\d{1,2})\s*(am|pm)/i);
  if (!m) return null;
  let hour, ampm;
  if (m[1] != null) { hour = Number(m[1]); ampm = (m[3] || '').toLowerCase(); }
  else { hour = Number(m[4]); ampm = (m[5] || '').toLowerCase(); }
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  if (ampm === 'pm' && hour < 12) hour += 12;
  if (ampm === 'am' && hour === 12) hour = 0;
  return hour;
}
function deriveShiftTag(r) {
  const hour = _firstStartHour(r.time_text || r.time);
  // 18:00 is the practical cutoff: a day shift ending 17:00–17:30 plus travel.
  const evening = hour == null ? null : hour >= 18;

  let weekend = null;
  const dow = String(r.day_of_week_en || '').toLowerCase();
  if (dow) {
    weekend = /sat|sun/.test(dow);
  } else if (r.start_date && /^\d{4}-\d{2}-\d{2}$/.test(r.start_date)) {
    // Parse as LOCAL Y/M/D — `new Date('2026-08-01')` is UTC midnight, which
    // shifts the weekday backwards in HK. Same class of bug as todayIso.
    const [y, mo, d] = r.start_date.split('-').map(Number);
    const day = new Date(y, mo - 1, d).getDay();
    weekend = day === 0 || day === 6;
  }

  if (evening == null && weekend == null) return null;
  return { evening, weekend };
}

function deriveCourseAxes(course, today) {
  return {
    eventStatus: deriveEventStatus(course, today),
    registrationStatus: deriveRegistrationStatus(course, today),
    cneStatus: deriveCneStatusNorm(course),
    publicationStatus: derivePublicationStatus(course),
  };
}
function courseBucket(course, today) {
  const eventStatus = deriveEventStatus(course, today);
  if ((course?.lifecycleStatus && course.lifecycleStatus !== 'active')
      || eventStatus === 'completed' || eventStatus === 'cancelled') return 'past';
  if (eventStatus === 'date_tbc') return 'tbc';
  return 'actionable';
}
function courseIsActionable(course, today) {
  const axes = deriveCourseAxes(course, today);
  return ['scheduled', 'ongoing'].includes(axes.eventStatus)
    && ['open', 'waitlist'].includes(axes.registrationStatus)
    && (!course?.lifecycleStatus || course.lifecycleStatus === 'active');
}
function discoverStatusCounts(courses, today) {
  const counts = {
    total: 0, actionable: 0, tbc: 0, past: 0,
    open: 0, waitlist: 0, full: 0, closed: 0, unknown: 0, not_applicable: 0,
  };
  (courses || []).forEach(course => {
    counts.total++;
    counts[courseBucket(course, today)]++;
    const registrationStatus = deriveRegistrationStatus(course, today);
    if (Object.prototype.hasOwnProperty.call(counts, registrationStatus)) counts[registrationStatus]++;
  });
  return counts;
}
// Free CNE currently on the catalogue — the number behind the Free CNE card.
//
// ‼️ THE PREDICATE MUST MATCH THE DESTINATION LIST EXACTLY, because this count
// is a promise. Tapping the card lands on Discover with the Free quick filter
// on and the default (actionable) tab, so what she sees there is
// `courseBucket === 'actionable' && isFree` — and the headline number has to be
// the sum of precisely those rows. Counting from a wider pool (every isFree row
// in the catalogue, including ones whose window has already closed) inflates
// the claim, and she finds out by counting what is on screen. If the
// destination filter ever changes, change this with it.
//
// `isFree` already carries the null-is-not-zero rule from deriveFees: a course
// with no fee evidence at all is `feeUnknown`, never free. Nothing here may
// relax that — see the fee contract above deriveFees.
//
// Returns points as a NUMBER; the caller decides the wording. Copy must stay at
// the hedged level ("目錄內目前有最多 N 分") until eligibility and
// already-completed de-duplication exist — this figure knows nothing about who
// is asking, so it cannot yet promise she personally can earn it.
function freeCneSummary(courses, today) {
  let count = 0;
  let points = 0;
  for (const course of (courses || [])) {
    if (!course || !course.isFree) continue;
    // ‼️ BOOKABLE, not merely listed. courseBucket() alone answers "is this
    // course still running", which is NOT the same question as "can she get
    // into it" — and the difference is the whole promise. Checked against live
    // data 2026-08-09: all three free courses the old predicate counted are
    // marked Closed or Full AND publish no registration URL or method, so the
    // card was advertising 14.5 points that no nurse could actually obtain.
    // courseIsActionable() adds the registration axis (open/waitlist +
    // scheduled/ongoing + active), which is exactly the missing test.
    if (!courseIsActionable(course, today)) continue;
    count++;
    points += Number(course.points) || 0;
  }
  return { count, points };
}
// Discover list order — PURELY the 開課日 (start date), ascending.
//
// ‼️ DO NOT REINTRODUCE A REGISTRATION-STATUS RANK HERE. Until 2026-08-09 this
// sorted 可報名/候補 → 待確認 → 已滿/已截止 first and only then by date, and it
// preferred registration_deadline over the start date — so a course opening in
// October could sit above one opening in September, and every full row sank to
// the bottom regardless of when it runs. John removed both: a nurse reads this
// list as a calendar, so a row's position must answer "when is it on", never
// "how likely am I to get a seat". Availability is still fully visible — the
// header keeps the 可報名 · 已滿 · 已截止 counts, each card keeps its status chip,
// and "只顯示可報名" still filters full/closed rows out entirely. Ordering no
// longer duplicates that signal.
//
// Start date falls back to the first sessions_list date, matching
// deriveEventStatus's effectiveStart: a row with only a sessions list is
// 'scheduled' (so it lands in this actionable tab) while `date` is null, and
// reading `date` alone would sink it to the bottom. The '9999-12-31' fallback
// then only catches rows with no date of any kind — which bucket to 'tbc' and
// sort elsewhere — and the id tiebreak keeps same-day rows stable across
// re-renders.
function sortForDiscover(courses) {
  const startOf = course => String(course?.date || _sessionDates(course)[0] || '9999-12-31');
  return (courses || []).slice().sort((a, b) =>
    startOf(a).localeCompare(startOf(b)) || String(a.id || '').localeCompare(String(b.id || '')));
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalise raw → UI-facing course
// ─────────────────────────────────────────────────────────────────────────────
function registrationTargetUrl(course) {
  const explicit = course?.registrationUrl || course?.raw?.registration_url || null;
  if (explicit) return explicit;
  const source = course?.source || course?.raw?.source || 'official';
  return source === 'official' ? (course?.raw?.url || null) : null;
}

// ── Where a tap goes, and what we are allowed to CALL that place ────────────
//
// ‼️ TWO AXES, NEVER ONE BOOLEAN. RegisterDialog used to pick both its link and
// its wording from "did registrationTargetUrl return something", which reads a
// fallback URL as proof of a registration destination. Measured 2026-08-16 over
// the whole unfinished catalogue (N=191): 137 rows are `contact_organizer` with
// NO registration_url — their only URL is the provider's course outline — and
// under that predicate every one of them would be captioned 「前往報名」/"Go to
// registration page". The link is genuinely the best one we have; the claim
// about it is false.
//
//   A fallback may inherit a destination. It must not inherit that
//   destination's meaning.
//
// So `url` keeps the existing best-available fallback (course-specific beats a
// provider index page) and `kind` is decided by registration_url_type — the
// provider's own statement of HOW to register — never by which fallback tier
// happened to produce the URL.
//
// ‼️ `kind` is NOT `!!registration_url`. A contact_organizer row may carry an
// explicit URL that is a contact page; explicit-ness says the field was filled,
// not that the destination books a seat. That case returns 'contact_link', and
// case E of test-registration-destination pins it so the old conflation cannot
// return wearing a different predicate.
const REGISTRATION_METHODS = new Set(['direct', 'google_form', 'official_form']);
function registrationDestination(course) {
  const explicit = course?.registrationUrl || course?.raw?.registration_url || null;
  const preferred = registrationTargetUrl(course);
  const providerHome = course?.raw?.source_url
    || (typeof PROVIDERS !== 'undefined' ? PROVIDERS?.[course?.raw?.provider_code]?.source : null)
    || null;
  const url = preferred || providerHome || null;
  const method = course?.raw?.registration_url_type || null;

  const targetSource = !url ? 'none'
    : (explicit && url === explicit) ? 'registration_url'
      : (url === (course?.raw?.url || null)) ? 'course_url'
        : 'provider_home';

  // Method is the claim owner. Only when the provider published no method at
  // all does the URL's own origin decide — and even then a `registration_url`
  // is a positive statement by the field it sits in, not an inference.
  let kind;
  if (targetSource === 'none') kind = 'none';
  else if (method === 'contact_organizer') {
    kind = targetSource === 'registration_url' ? 'contact_link'
      : targetSource === 'course_url' ? 'course_info' : 'provider_info';
  } else if (REGISTRATION_METHODS.has(method)) kind = 'registration';
  else {
    kind = targetSource === 'registration_url' ? 'registration'
      : targetSource === 'course_url' ? 'course_info' : 'provider_info';
  }
  return { url, targetSource, method, kind };
}

// ── What the nurse is actually being asked to DO ────────────────────────────
//
// ‼️ METHOD AND INTENT ARE TWO FIELDS, NOT ONE. `registrationDestination.kind`
// already keeps a fallback URL from inheriting a booking-page meaning, but it
// collapses HOW you register into WHETHER you register: `contact_organizer`
// becomes `course_info`, and 89 of the 147 live rows that render the primary CTA
// are exactly that shape. The button said 報名 (promising more than a course
// outline can deliver) while the dialog behind it said 查看課程資料 (promising
// less than the provider's own method statement supports). One row was wrong in
// both directions at once. Measured 2026-08-19 over 270 live active rows.
//
//   A CTA may not promise a stronger action than the destination's intent
//   supports — and must not understate an intent the provider did state.
//
// SOURCE HIERARCHY, strongest first. Each tier may only be consulted when every
// stronger tier is silent:
//
//   1. registration_url_type   the provider's own statement of HOW (79% of live
//                              rows carry it) — the only tier allowed to ASSERT
//                              intent=register
//   2. URL inference           consulted ONLY at tier-1 silence, and only for
//                              patterns that are unambiguous by construction
//                              (a Zoom /j/ path is a meeting, never a form)
//
// ‼️ TIER 2 MAY NOT PRODUCE intent=register. AT ALL. An earlier draft read a
// Google/Microsoft Forms host as a booking form; that is not unambiguous by
// construction — the same host serves evaluations, attendance sheets, feedback,
// quizzes and surveys, and it was additionally stamping every one of them
// `method='google_form'` including forms.office.com, corrupting the method
// evidence on the way past. A form URL is evidence that a FORM exists, never
// evidence of what the form is for. The only thing tier 2 may still assert is a
// MEETING, because a /j/ meeting path has no second reading.
//   3. destination.kind        a link exists but nothing says what it means →
//                              learn_more, never register
//
// ‼️ TIER 2 IS WHY THE OLD FALLBACK WAS WRONG, NOT THAT IT EXISTED. data.js's
// pre-2026-08-19 rule was `registration_url present ⇒ kind='registration'`:
// field PRESENCE standing in for meaning. Inference is legitimate here, but only
// downward — see registrationNotRequired below.
// ‼️ EVERY MEMBER MUST BE UNAMBIGUOUS BY CONSTRUCTION — a path that can only be
// a live meeting room. Three earlier members failed that test and were removed:
//
//   zoom.us/meeting/   `…/meeting/register/…` is Zoom's REGISTRATION page. The
//                      broad `/meeting/` prefix therefore read a booking form as
//                      a join link — a reverse fact produced by the very tier
//                      that exists to avoid them. `/j/<id>` has no such second
//                      reading; the personal-room `/my/<name>` likewise.
//   youtube.com/live/  A YouTube URL is a VIDEO first. Recordings, trailers,
//   youtu.be/          course intros and past webinars all live at these paths,
//                      and a short link carries no signal at all beyond "video".
//                      They now fall to tier 3 (learn_more), which is what the
//                      evidence actually supports. A future `watch`/`stream`
//                      intent can claim them properly; until then, silence.
//
// Widening this list is a claim-strengthening act. Anything added here must be
// impossible to read as a page ABOUT the course rather than the course itself.
const RE_JOIN_LINK = /zoom\.us\/j\/|zoom\.us\/my\/|teams\.microsoft\.com\/l\/meetup-join|teams\.live\.com\/meet|meet\.google\.com\/[a-z]{3,4}-[a-z]{3,4}-[a-z]{3,4}|webex\.com\/(?:meet|join)\//i;
// Provider prose saying registration is not needed at all. ‼️ THIS MAY ONLY EVER
// WEAKEN A CLAIM. Text evidence is the least structured signal we hold, so it is
// allowed to strip an intent the catalogue asserted and never to manufacture
// one — a downgrade that misfires costs a nurse one extra tap, an upgrade that
// misfires sends her to a page that cannot register her. Catalogue-wide this
// matches exactly ONE row today, which is precisely why it must not become a
// pipeline: it is a guard, not a source.
const RE_NO_REGISTRATION = /不用登記|不需登記|毋須報名|不用報名|無需報名|不需報名|免報名|no\s+registration\s+(?:is\s+)?(?:required|needed)|registration\s+(?:is\s+)?not\s+required/i;
function registrationNotRequired(course) {
  const raw = course?.raw || {};
  const prose = [raw.description_short_en, raw.description_short_zh,
    raw.description_full_en, raw.description_full_zh, raw.registration_method_en,
    raw.registration_method_zh].filter(Boolean).join(' ');
  return RE_NO_REGISTRATION.test(prose);
}
const COURSE_ACTION_LABEL = {
  register_contact: { en: 'How to register', zh: '如何報名' },
  register_direct:  { en: 'Go to registration', zh: '前往報名' },
  // ‼️ NO TEMPORAL CLAIM. This label was 「立即參加」/'Join now'. A meeting URL
  // proves the destination IS a classroom; it proves nothing about the classroom
  // being open right now — the link for a 2027 congress can be published today,
  // and 「立即」 would send a nurse to "the meeting has not started". Whether an
  // action is available YET is P2's question, and a label must not answer it on
  // P2's behalf. This wording promises only what the URL itself establishes.
  join:             { en: 'Open course link', zh: '開啟課堂連結' },
  learn_more:       { en: 'View course', zh: '查看課程' },
  unknown:          { en: 'View details', zh: '查看詳情' },
};
function deriveCourseAction(course, today) {
  const dest = registrationDestination(course);
  const raw = course?.raw || {};
  const explicitUrl = course?.registrationUrl || raw.registration_url || null;

  // Tier 1 — the provider stated the method. This is the ONLY tier that may
  // assert intent=register. contact_organizer is a registration method, not an
  // information link: the nurse's goal is still to enrol, the desk is just a
  // person rather than a form.
  let method = raw.registration_url_type || null;
  let intent;
  if (method === 'contact_organizer' || REGISTRATION_METHODS.has(method)) {
    intent = 'register';
  } else if (explicitUrl && RE_JOIN_LINK.test(explicitUrl)) {
    // Tier 2 — unambiguous by construction.
    method = 'join_link';
    intent = 'join';
  } else if (dest.kind === 'none') {
    method = null;
    intent = 'unknown';
  } else {
    // Tier 3 — a link exists and nothing says what it means. This is where the
    // old rule claimed 'registration' from field presence alone.
    method = null;
    intent = 'learn_more';
  }

  // Downgrade-only guard (see RE_NO_REGISTRATION).
  //
  // ‼️ THE GUARD MAY REMOVE A CLAIM. IT MAY NOT SUBSTITUTE A DIFFERENT ONE. An
  // earlier draft read「不用登記」 and promoted ANY url to intent='join', method=
  // 'join_link' — so a `direct` row pointing at a course-outline page became
  // 「立即參加」 on the strength of one sentence, and the fabricated method then
  // overwrote the provider's own. That is manufacture wearing a downgrade's
  // clothes, and it contradicts the rule three lines above it.
  //
  // The prose can only ever withdraw `register`. What the row degrades TO is
  // decided by the URL evidence alone: a meeting link is still a meeting link,
  // and anything else keeps no method at all rather than inventing one.
  if (intent === 'register' && registrationNotRequired(course)) {
    if (explicitUrl && RE_JOIN_LINK.test(explicitUrl)) {
      intent = 'join';
      method = 'join_link';
    } else {
      intent = dest.url ? 'learn_more' : 'unknown';
      method = null;
    }
  }

  const labelKey = intent === 'register'
    ? (method === 'contact_organizer' ? 'register_contact' : 'register_direct')
    : intent === 'join' ? 'join'
      : intent === 'learn_more' ? 'learn_more' : 'unknown';

  // ‼️ `enabled` answers "is this action available YET", which is P2 territory
  // in general — deliberately NOT a re-reading of open/closed/full here. Only
  // the not-yet-open case is decided, because a course whose registration has
  // not opened cannot be registered for NOR already have been (unlike 已截止 /
  // 已滿, where the nurse may well have got in earlier and still needs 我已報名).
  // Reuses enrolmentStatusInfo's 'soon' kind so the button and the floating
  // status note cannot disagree about the same row.
  const soon = enrolmentStatusInfo(course, today).kind === 'soon';

  // ‼️ P2 — AVAILABILITY DEMOTES THE ACTION. IT NEVER RELABELS IT, AND NEVER
  // DELETES THE DESTINATION.
  //
  // §81's gate: 72 live rows asserted a register action over a registration
  // that is already closed or full. The label is not the defect — 「前往報名」
  // is a true statement about WHAT the link is — so weakening the vocabulary
  // back toward the old constant 報名 would delete the fact P1 established and
  // still leave the contradiction. What is wrong is which action is offered
  // FIRST: on a closed/full row the nurse's realistic action is to record that
  // she already got in, not to be sent to a desk that has stopped taking
  // people. So `primary` demotes the register action to secondary and nothing
  // else changes: same intent, same label, same URL, same `enabled`.
  //
  // The rule is stated over enrolment state, NOT over labels or counts. A
  // `label === '如何報名'` special case would clear today's 72 rows and re-open
  // the same hole for tomorrow's; what is being eliminated is the CONTRADICTION
  // CLASS, and catalogue churn must not be able to reintroduce it.
  //
  // Four exclusions, each load-bearing:
  //   · `intent === 'register'` only — `join` is participation, not
  //     registration: a meeting room stays openable when a roll is full, and
  //     `learn_more`/`unknown` assert no registration to contradict.
  //   · NOT `soon`. Opening-soon is a display refinement of closed
  //     (enrolmentStatusInfo), so without this a 2027 congress whose desk has
  //     never opened would offer 我已報名 — a registration that cannot have
  //     happened. Not-yet-open stays `enabled:false`, which is P1's rule.
  //   · NOT `waitlist`. A waitlist is a registration that is still being
  //     taken; demoting it would suppress a live action.
  //   · NOT `unknown` / `not_applicable`. Silence is not a closed desk, and an
  //     ended course is answered upstream by the log-completion branch.
  const registrationStatus = deriveRegistrationStatus(course, today);
  const unavailable = intent === 'register' && !soon
    && (registrationStatus === 'closed' || registrationStatus === 'full');

  return {
    intent,
    method,
    label: COURSE_ACTION_LABEL[labelKey],
    enabled: !soon,
    // ‼️ 'already_registered' means DEMOTED, not disabled — see 4j: 已截止 /
    // 已滿 stay actionable, because the nurse may have registered before the
    // desk closed, the provider may take late entries, a full course may open
    // a waitlist, and the page remains this row's provenance either way.
    primary: unavailable ? 'already_registered' : 'action',
    registrationStatus,
    url: dest.url,
    destinationKind: dest.kind,
  };
}

function normaliseCourse(r, idx) {
  const titleObj = { en: r.title_en, zh: r.title_zh || r.title_en };
  const probe = (r.title_en || '') + ' ' + (r.title_zh || '');
  const topic = deriveTopic(probe);
  const fmt = deriveFormat(probe, r.sessions, r.teaching_mode_en);
  const sp = spotsFromStatus(r, idx);
  const { online, onlineSource } = deriveOnline(r);
  const venueObj = (r.venue_en || r.venue_zh)
    ? { en: r.venue_en || r.venue_zh, zh: r.venue_zh || r.venue_en }
    : (online ? { en: 'Online', zh: '網上' } : { en: 'Venue TBC · Hong Kong', zh: '地點待定 · 香港' });
  const provObj = { en: r.provider_full_en, zh: r.provider_full_zh || r.provider_full_en };
  const descObj = (r.description_en || r.description_zh)
    ? { en: r.description_en || r.description_zh, zh: r.description_zh || r.description_en }
    : { en: `${r.sessions || 1}-session ${fmt === 'workshop' ? 'workshop' : 'programme'} delivered by ${r.provider_full_en}.`,
        zh: `由${r.provider_full_zh || r.provider_full_en}主辦的 ${r.sessions || 1} 節${fmt === 'workshop' ? '工作坊' : '課程'}。` };

  const out = {
    id: r.course_code || `${r.provider_code}-${idx}`,
    raw: r,
    title:         titleObj,
    provider:      provObj,
    // The stable identity of the provider — the one field that is neither
    // localised nor prose. Membership matching keys on it (see
    // membershipMatchesCourse), which is what made a picker possible.
    providerCode:  r.provider_code || '',
    providerShort: { en: r.provider_short_en || r.provider_code, zh: r.provider_short_zh || r.provider_full_zh || r.provider_code },
    topic,
    topicLabel:    TOPIC_LABEL[topic],
    points:        r.cne_points,
    category:      deriveCategory(topic),
    date:          r.start_date,
    endDate:       r.end_date,
    time:          r.time || '',
    dayOfWeek:     { en: r.day_of_week_en || '', zh: r.day_of_week_zh || '' },
    sessions:      Number(r.sessions) || 1,
    sessionsList:  Array.isArray(r.sessions_list) ? r.sessions_list : null,
    venue:         venueObj,
    online,
    onlineSource,
    teachingMode: r.teaching_mode_en ? { en: r.teaching_mode_en, zh: r.teaching_mode_zh || r.teaching_mode_en } : null,
    ...deriveFees(r),
    spots:         sp.spots,
    spotsTotal:    sp.total,
    format:        fmt,
    formatLabel:   FORMAT_LABEL[fmt],
    // Language is evidence-only: no official language_en ⇒ null, and the UI
    // renders 「語言待確認」. NEVER reinvent an EN/廣東話 default here — the
    // pipeline withdrew all guessed language values (2026-07-18) and
    // scripts/test-discover.mjs (CI: npm run test:discover) fails on any
    // non-null fallback.
    language:      r.language_en ? { en: r.language_en, zh: r.language_zh || r.language_en } : null,
    accred:        'NCHK',
    saved:         idx % 5 === 0,
    enrolled:      idx === 5 || idx === 8,
    status:        r.enrollment_status_en || r.enrollment_status_zh || null,
    enrollmentStatus: (r.enrollment_status_en || r.enrollment_status_zh)
      ? { en: r.enrollment_status_en || r.enrollment_status_zh,
          zh: r.enrollment_status_zh || r.enrollment_status_en }
      : null,
    cneStatus: (r.cne_status_en || r.cne_status_zh)
      ? { en: r.cne_status_en || r.cne_status_zh,
          zh: r.cne_status_zh || r.cne_status_en }
      : null,
    registrationDeadline: r.registration_deadline || null,
    // Mirror the Supabase adapter's registration shape so the detail
    // screen's CTA branch (screens.jsx) sees the same fields whether the
    // row came from RAW_COURSES or Supabase. Local seed rows use `url:`
    // for the registration link; canonical `registration_url` wins when
    // present.
    registrationUrl:     r.registration_url || (r.source === 'community' || r.source === 'ai_extracted' ? null : r.url) || null,
    registrationUrlType: r.registration_url_type
      || ((r.registration_url || r.url) ? 'direct_url' : null),
    registrationContact: {
      en: r.registration_contact_instruction_en || null,
      zh: r.registration_contact_instruction_zh || null,
    },
    courseCode:    r.course_code,
    speakers:      r.speakers || null,
    blurb:         descObj,
    eventType:     r.event_type_en ? { en: r.event_type_en, zh: r.event_type_zh || r.event_type_en } : null,
    // Explicit lifecycle default — mirrors normaliseSupabaseCourse. The
    // three lifecycle gates (isPastCourse, enrolmentStatusInfo, CourseCard
    // ended-pill) all use truthy short-circuit and would tolerate
    // undefined here, but we set the field explicitly so any FUTURE
    // assertive check (=== 'active') keeps working in offline / local
    // fallback mode. RAW_COURSES is hand-curated bundled data — every
    // row is by definition the bookable, supported catalogue.
    lifecycleStatus: 'active',
    verificationStatus: r.verification_status || 'verified',
  };
  out.cneStatusNorm = deriveCneStatusNorm(out);
  out.publicationStatus = derivePublicationStatus(out);
  // Shift-friendliness — see deriveShiftTag. null when the catalogue publishes
  // neither a start time nor a weekday for this course. Set in BOTH normalisers
  // so the seed/Supabase parity check (test-discover.mjs) keeps holding.
  out.shiftTag = deriveShiftTag(r);
  return out;
}

let COURSES = RAW_COURSES.map(normaliseCourse)
  // sort upcoming first
  .sort((a,b) => (a.date || '9999') < (b.date || '9999') ? -1 : 1);

// ─────────────────────────────────────────────────────────────────
// RESTRICTION — single source of truth for the members-only / internal
// restriction note. `build` is the ONLY producer of restriction_note
// (screens.jsx buildRestrictionNote delegates here); `parse` is its exact
// inverse. They are colocated so the format and its inverse can never
// silently drift — if you touch one phrase, touch its match here too.
//
// There is no structured restriction column in the DB (verified live):
// restrictions are `is_restricted` + free-text `restriction_note`. Since
// build is the sole writer, parse can reliably recover the structured shape
// {audience/sector/hospitalKey} for eligibility matching — and both the note's
// hospital label and profiles.hospital_key come from the same hk-hospitals.js,
// so matchHospital() inverts label→key exactly.
//
// `sel` shape: { audience:'hospital'|'members', sector:'public'|'private',
//                hospitalKey, memberName }
const RESTRICTION = {
  // Canonical phrases. Keep these two halves (build/parse) in lockstep.
  build(sel, lang) {
    const { audience, sector, hospitalKey, memberName } = sel || {};
    if (audience === 'hospital') {
      // Only private hospitals resolve to a specific hospital; public hospitals
      // share one internal catalogue and stay sector-scoped (no per-hospital key).
      const label = (sector === 'private' && typeof window.hospitalLabel === 'function')
        ? window.hospitalLabel(hospitalKey, lang) : '';
      if (label) return lang === 'zh' ? `只限${label}員工` : `${label} staff only`;
      const sec = sector === 'private'
        ? (lang === 'zh' ? '私立醫院' : 'private hospitals')
        : (lang === 'zh' ? '公立醫院' : 'public hospitals');
      return lang === 'zh' ? `只限${sec}員工` : `${sec} staff only`;
    }
    const name = (memberName || '').trim();
    if (name) return lang === 'zh' ? `只限${name}會員` : `${name} members only`;
    return lang === 'zh' ? '只限會員' : 'Members only';
  },

  // Recover the structured shape from a note. Strict, ordered, bilingual;
  // sector sentinels are matched EXACTLY (never by the `醫院` substring, which
  // appears inside almost every private-hospital label). Returns
  //   { kind:'hospital'|'members'|'unknown', sector, hospitalKey }
  parse(note) {
    const s = String(note || '').trim();
    if (!s) return { kind: 'unknown', sector: null, hospitalKey: null };

    // 1. Membership — suffix 會員 / "members only".
    if (/會員$/.test(s) || /members only$/i.test(s)) {
      return { kind: 'members', sector: null, hospitalKey: null };
    }

    // 2. Hospital — strip the 只限…員工 / "… staff only" wrapper → inner label.
    let inner = null;
    const zh = s.match(/^只限(.+)員工$/);
    if (zh) inner = zh[1].trim();
    else {
      const en = s.match(/^(.+)\sstaff only$/i);
      if (en) inner = en[1].trim();
    }
    if (inner != null) {
      if (inner === '公立醫院' || /^public hospitals$/i.test(inner)) {
        return { kind: 'hospital', sector: 'public', hospitalKey: null };
      }
      if (inner === '私立醫院' || /^private hospitals$/i.test(inner)) {
        return { kind: 'hospital', sector: 'private', hospitalKey: null };
      }
      // Specific hospital — resolve the canonical label back to a key. build()
      // only ever labels private hospitals, and matchHospital's exact-match
      // loop resolves a full canonical label before its fuzzy loop, so the key
      // is correct. Fuzzy misfire on a non-canonical string degrades to
      // sector-only (private), never a wrong key match we'd trust for eligibility.
      const m = (typeof window.matchHospital === 'function') ? window.matchHospital(inner) : null;
      if (m) return { kind: 'hospital', sector: m.sector || 'private', hospitalKey: m.key };
      return { kind: 'hospital', sector: 'private', hospitalKey: null };
    }

    // 3. Anything else (hand-edited note, unrecognised format).
    return { kind: 'unknown', sector: null, hospitalKey: null };
  },
};

// Structured restriction for a normalised course. Prefers the field attached
// at normalise time; falls back to parsing restrictionNote so courses cached
// before this change (CoursesCache stores normalised objects as-is) still work.
function courseRestriction(course) {
  if (!course || !course.isRestricted) return null;
  if (course.restriction) return course.restriction;
  return RESTRICTION.parse(course.restrictionNote);
}

// ‼️ PRESENTATION, NOT AUTHORIZATION. Everything below decides what a nurse is
// SHOWN and how it is LABELLED. It is not an access control and must never be
// mistaken for one: `cne_courses_v` is readable by anon, so every restricted
// course is already public data. Loosening these predicates leaks nothing;
// tightening them protects nothing. The real gate is the provider's own
// registration process, which happens outside this app entirely.
//
// ── THE VERDICT — one predicate, three consumers ────────────────────────────
// restrictedVisibleByDefault / restrictionEligibility / restrictedRecommendable
// all derive from this, so "can she attend?" is answered in exactly one place.
// Returns null for a course that is not restricted at all, otherwise:
//
//   'eligible'    we know her workplace and it matches the restriction
//   'ineligible'  we know her workplace and it does NOT match
//   'unknown'     ‼️ we do not know her workplace — see below
//   'open'        not a workplace gate (membership / unparseable note); she can
//                 apply to join, so it is never a refusal
//
// ‼️ 'unknown' IS NOT 'ineligible'. This distinction is the whole point of the
// rewrite. The old code returned false for `!profile`, which meant a signed-out
// visitor AND any nurse whose profile predates the workplace picker were both
// treated as *known to be ineligible* and silently lost 22% of the catalogue
// (58 of 264 active courses, once the 醫院管理局 rows were stamped — RELEASE.md
// §36). Hiding on the strength of data we never collected is not a product
// decision, it is a bug: RESTRICTION.parse already gives an unparseable NOTE the
// benefit of the doubt, and it would be incoherent to deny an unknown USER the
// same courtesy.
//
// Knowability is judged PER AXIS, because the two axes come from different
// profile fields: a hospital-specific restriction needs `hospital_key`, a
// sector-scoped one needs `hospital_sector`. A nurse who picked "NGO" has a
// sector but no key — known for the sector axis, unknown for the key axis.
function restrictionVerdict(course, profile) {
  if (!course || !course.isRestricted) return null; // not our concern
  const r = courseRestriction(course);
  // members / unparseable → not a workplace gate at all.
  if (!r || r.kind === 'members' || r.kind === 'unknown') return 'open';
  if (r.kind !== 'hospital') return 'open';
  if (r.hospitalKey) {
    if (!profile?.hospital_key) return 'unknown';
    return profile.hospital_key === r.hospitalKey ? 'eligible' : 'ineligible';
  }
  // Sector-scoped (public, or a generic private course with no specific key).
  if (!profile?.hospital_sector) return 'unknown';
  return profile.hospital_sector === r.sector ? 'eligible' : 'ineligible';
}

// Should a restricted course appear in the default ("relevant") Discover view?
// Everything except a KNOWN mismatch. 'unknown' shows — see the note above.
function restrictedVisibleByDefault(course, profile) {
  const verdict = restrictionVerdict(course, profile);
  return verdict === null ? true : verdict !== 'ineligible';
}

// Label for card / detail hinting. Same four values as the verdict; kept as a
// separate name because the call sites read as "what do I tell her", not "what
// do I filter on".
function restrictionEligibility(course, profile) {
  return restrictionVerdict(course, profile);
}

// ── Recommending is a stronger claim than listing ───────────────────────────
// A LIST says "this exists"; a RECOMMENDATION says "you should look at this".
// So the two use different bars, and 'unknown' splits them: it is enough to be
// listed (benefit of the doubt) and not enough to be pushed at someone. Without
// this split, loosening restrictedVisibleByDefault would have silently started
// recommending 醫院管理局 courses to signed-out visitors on the Home screen —
// the change leaking from the surface it was designed for into one where the
// app is making a promise.
function restrictedRecommendable(course, profile) {
  const verdict = restrictionVerdict(course, profile);
  if (verdict === null) return true; // not restricted — recommend freely
  return verdict === 'eligible' || verdict === 'open';
}

// ─────────────────────────────────────────────────────────────────
// Supabase row → UI-shape adapter
// The upstream `cne_courses` table uses a richer, slightly different
// schema (time_text, target_audience_en[], specialty_area_en[],
// instructors_en[], description_short/full_en, objectives_en,
// syllabus_en, assessment_*, certificate_*, registration_*, change_log…).
// This adapter maps every new field into the same shape the screens
// already render, AND stuffs the full raw row onto `.raw` (with alias
// keys for backwards compatibility) so the detail screen can show
// every available datum.
// ─────────────────────────────────────────────────────────────────
function normaliseSupabaseCourse(r) {
  // Pick helper: looks for an underscore-prefixed field first (the
  // convention for "system-managed / private" columns going forward),
  // then falls back to the bare name. Defensive against (a) stale cache
  // rows written before a column was renamed, and (b) the view exposing
  // either form during a transition. cne_courses_v is the primary
  // rename-insulation seam — this helper is the per-field safety net.
  const pick = (key) => {
    const us = '_' + key;
    if (r[us] !== undefined && r[us] !== null) return r[us];
    return r[key];
  };

  const titleEn = r.title_en || '';
  const titleZh = r.title_zh || titleEn;
  const titleObj = { en: titleEn, zh: titleZh };

  const probe = titleEn + ' ' + (r.title_zh || '');
  const topic = deriveTopic(probe);
  const fmt = deriveFormat(probe, r.sessions, r.teaching_mode_en);
  const { online, onlineSource } = deriveOnline(r);

  const provFromDir = PROVIDERS[r.provider_code];
  const provObj = {
    en: r.provider_full_en || provFromDir?.en || r.provider_code,
    zh: r.provider_full_zh || provFromDir?.zh || r.provider_full_en || r.provider_code,
  };
  const provShortObj = {
    en: r.provider_short_en || provFromDir?.short_en || r.provider_code,
    zh: r.provider_short_zh || provFromDir?.short_zh || r.provider_code,
  };

  const venueObj = (r.venue_en || r.venue_zh)
    ? { en: r.venue_en || r.venue_zh, zh: r.venue_zh || r.venue_en }
    : (online
        ? { en: r.online_platform || 'Online', zh: r.online_platform || '網上' }
        : { en: 'Venue TBC · Hong Kong', zh: '地點待定 · 香港' });

  // Description: prefer description_full, then short, then auto-generated
  const dFullEn = r.description_full_en, dFullZh = r.description_full_zh;
  const dShortEn = r.description_short_en, dShortZh = r.description_short_zh;
  const descEn = dFullEn || dShortEn || `${r.sessions || 1}-session ${fmt} delivered by ${provObj.en}.`;
  const descZh = dFullZh || dShortZh || dFullEn || dShortEn ||
    `由${provObj.zh}主辦的 ${r.sessions || 1} 節${fmt === 'workshop' ? '工作坊' : '課程'}。`;
  const descObj = { en: descEn, zh: descZh };

  // Per-field provenance bag. Today: scattered _cne_points_inferred /
  // _title_zh_inferred flags; tomorrow: a unified _provenance JSONB. Read
  // both shapes so screens can rely on a single helper:
  //   course.provenance.<fieldName> → { source_type, inferred, ... }
  const provenance = (typeof r._provenance === 'object' && r._provenance !== null)
    ? { ...r._provenance }
    : {};
  if (r._cne_points_inferred != null || r._cne_points_source != null) {
    provenance.cne_points = {
      inferred: r._cne_points_inferred === true,
      source_type: r._cne_points_source || null,
      ...(provenance.cne_points || {}),
    };
  }
  if (r._title_zh_inferred != null) {
    provenance.title_zh = {
      source_type: r._title_zh_inferred,
      ...(provenance.title_zh || {}),
    };
  }

  // Build a back-compat raw object so the existing detail screen keeps
  // working without changes, AND new fields are accessible directly.
  // Note: array-shaped Supabase fields (target_audience, specialty_area)
  // are intentionally NOT aliased to the old prose-shaped keys — the new
  // chip-style side-facts handle those, and we want to avoid double-render.
  //
  // System-managed audit columns (status, notes, last_updated, first_seen,
  // change_log, anomalies, fetched_at) are mirrored through pick() so that
  // existing `c.raw.change_log` / `c.raw.last_updated` etc. callers keep
  // working regardless of whether backend has done the underscore-rename.
  const raw = {
    ...r,
    status:         pick('status'),
    notes:          pick('notes'),
    last_updated:   pick('last_updated'),
    first_seen:     pick('first_seen'),
    fetched_at:     pick('fetched_at'),
    change_log:     pick('change_log'),
    anomalies:      pick('anomalies'),
    // String-form aliases used by the existing CourseDetailScreen
    target_en: typeof r.target_audience_en === 'string' ? r.target_audience_en : null,
    target_zh: typeof r.target_audience_zh === 'string' ? r.target_audience_zh : null,
    entry_requirement_en: r.entry_requirements_en || null,
    entry_requirement_zh: r.entry_requirements_zh || null,
    organizer: r.organizer_en || r.organizer_zh || null,
    application_deadline: r.registration_deadline || null,
    // topics_en is reserved for conference "topics covered" — leave undefined.
    instructor_en: (Array.isArray(r.instructors_en) && r.instructors_en[0]) || r.instructors_en || null,
    instructor_zh: (Array.isArray(r.instructors_zh) && r.instructors_zh[0]) || r.instructors_zh || null,
  };

  const speakers   = Array.isArray(r.instructors_en) && r.instructors_en.length ? r.instructors_en : null;
  const speakersZh = Array.isArray(r.instructors_zh) && r.instructors_zh.length ? r.instructors_zh : null;

  // ── Seat capacity (null-safe) ────────────────────────────────────────
  // GUARD — DO NOT replace toSeatCount() with a bare Number():
  //   PostgREST returns JSON `null` for an empty integer column, and
  //   `Number(null) === 0` (NOT NaN). Coercing an *unknown* capacity to 0
  //   made the `remaining === 0` → "full" branch below fire for every
  //   course without published seat counts. Symptom: a course whose
  //   enrollment_status_zh is 接受報名 rendered 已滿, AND it was wrongly
  //   dropped by the "seats available" filter (screens.jsx onlyWithSeats).
  //   toSeatCount maps null/''/undefined → NaN so the "both unknown → null
  //   seats" guard actually fires and the badge falls back to
  //   enrollment_status_zh. (`Number(undefined) === NaN` hid this in local
  //   RAW_COURSES seed rows where the column is simply absent.)
  const toSeatCount = (v) => (v === null || v === undefined || v === '') ? NaN : Number(v);
  const capNum = toSeatCount(r.capacity);
  const remNum = toSeatCount(r.remaining_capacity);

  const out = {
    id: r.id || `${r.provider_code}::${r.course_code || 'x'}`,
    raw,
    title: titleObj,
    provider: provObj,
    // See normaliseCourse: the non-localised provider identity, used by
    // membership matching. Both course shapes must carry it.
    providerCode: r.provider_code || '',
    providerShort: provShortObj,
    topic,
    topicLabel: TOPIC_LABEL[topic],
    points: r.cne_points,
    category: deriveCategory(topic),
    date: r.start_date,
    endDate: r.end_date,
    time: r.time_text || r.time || '',
    dayOfWeek: { en: r.day_of_week_en || '', zh: r.day_of_week_zh || '' },
    sessions: Number(r.sessions) || 1,
    // Canonical per-session list (cne_courses.sessions_list) — an array of
    // "YYYY-MM-DD[THH:MM]" strings. Kept as the raw serialised array;
    // resolveSessions() in record-extras.jsx normalises it (and prefers it
    // over the synthesised fallback when present, below a user override).
    sessionsList: Array.isArray(r.sessions_list) ? r.sessions_list : null,
    venue: venueObj,
    online,
    onlineSource,
    teachingMode: r.teaching_mode_en
      ? { en: r.teaching_mode_en, zh: r.teaching_mode_zh || r.teaching_mode_en }
      : null,
    ...deriveFees(r),
    // capacity / remaining_capacity — both come straight from Supabase.
    // 9999 is the sentinel for "unlimited" (self-study, video lectures).
    // We expose both the raw numbers and a normalised `seats` object so
    // the UI never has to re-derive intent from a magic number.
    capacity:          Number.isFinite(capNum) ? capNum : null,
    remainingCapacity: Number.isFinite(remNum) ? remNum : null,
    // Surface the "this number is an estimate" flag so UI can hedge
    // ("約 25 位" / "approx 25") instead of asserting precision providers
    // don't actually update consistently.
    capacityIsEstimate: r.capacity_is_estimate === true,
    seats: (function () {
      // SSOT: prefer the backend's authoritative classification
      // (phase6.derive_seats → cne_courses.seats jsonb, exposed via
      // cne_courses_v). When the column is present we do NOT re-derive from
      // raw capacity at all — that parallel derivation is precisely what
      // produced the catalogue-wide null-capacity → 已滿 bug. The client
      // derivation below is the FALLBACK only when the view omitted `seats`
      // altogether. `{kind:null}` is an intentional authoritative "unknown";
      // re-deriving it from a capacity number fabricated Available badges.
      if (r.seats && typeof r.seats === 'object'
          && Object.prototype.hasOwnProperty.call(r.seats, 'kind')) {
        const allowed = [null, 'open', 'limited', 'unlimited', 'full'];
        return allowed.includes(r.seats.kind) ? r.seats : { kind: null };
      }
      const cap = capNum;   // null-safe (see GUARD above); never bare Number(r.capacity)
      const rem = remNum;
      if (!Number.isFinite(cap) && !Number.isFinite(rem)) return null;
      if (cap === 9999 || rem === 9999) return { kind: 'unlimited', remaining: Infinity, total: Infinity };
      if (rem === 0) return { kind: 'full', remaining: 0, total: cap };
      if (Number.isFinite(rem) && Number.isFinite(cap) && cap > 0) {
        const ratio = rem / cap;
        return { kind: ratio <= 0.2 ? 'limited' : 'open', remaining: rem, total: cap };
      }
      return { kind: 'open', remaining: rem, total: cap };
    })(),
    // Back-compat aliases for screens that still read spots / spotsTotal.
    spots:      (function () {
      if (capNum === 9999 || remNum === 9999) return Infinity;
      return Number.isFinite(remNum) ? remNum : undefined;
    })(),
    spotsTotal: (function () {
      if (capNum === 9999) return Infinity;
      return Number.isFinite(capNum) ? capNum : undefined;
    })(),
    format: fmt,
    formatLabel: FORMAT_LABEL[fmt],
    // Language is evidence-only: no official language_en ⇒ null, and the UI
    // renders 「語言待確認」 (same contract as deriveFees: unknown is never
    // guessed). scripts/test-discover.mjs (CI: npm run test:discover) fails
    // on any non-null fallback.
    language: r.language_en
      ? { en: r.language_en, zh: r.language_zh || r.language_en }
      : null,
    accred: 'NCHK',
    saved: false,     // user-specific — managed via savedIds set
    enrolled: false,  // user-specific — managed via enrolledIds set
    status: r.enrollment_status_en || r.enrollment_status_zh || null,
    enrollmentStatus: (r.enrollment_status_en || r.enrollment_status_zh)
      ? { en: r.enrollment_status_en || r.enrollment_status_zh,
          zh: r.enrollment_status_zh || r.enrollment_status_en }
      : null,
    // CNE-points authority badge (separate axis from verification_status).
    // E.g. "Confirmed" / "TBC" / "Inferred" — surfaced in the points facts.
    cneStatus: (r.cne_status_en || r.cne_status_zh)
      ? { en: r.cne_status_en || r.cne_status_zh,
          zh: r.cne_status_zh || r.cne_status_en }
      : null,
    registrationDeadline: r.registration_deadline || null,
    registrationUrl: r.registration_url || null,
    // How the user is supposed to actually register. Drives the CTA branch.
    // Values: 'direct_url' | 'contact_organizer' | 'email' | 'phone' | 'walk_in' | null
    // Inferred default: if a URL exists, treat as 'direct_url'.
    registrationUrlType: r.registration_url_type
      || (r.registration_url ? 'direct_url' : null),
    registrationContact: {
      en: r.registration_contact_instruction_en || null,
      zh: r.registration_contact_instruction_zh || null,
    },
    courseCode: r.course_code,
    speakers,
    speakersZh,
    blurb: descObj,
    eventType: r.event_type_en
      ? { en: r.event_type_en, zh: r.event_type_zh || r.event_type_en }
      : null,
    // Per-field provenance bag (see comment above). Empty object today for
    // most rows; populated when scraper attaches confidence/source metadata.
    provenance,
    // Lifecycle status — reads view's `lifecycle_status` first (today's
    // canonical from cne_courses_v), falls back to pick('status') if
    // frontend ever queries cne_courses directly post-rename. Used for
    // badges and for isPastCourse(); list-level filtering is server-side.
    lifecycleStatus: r.lifecycle_status || pick('status') || 'active',
    // Crowdsource Phase 1A — wire fields through to UI shape.
    // 'source' defaults to 'official' for backfilled rows. 'verificationStatus'
    // for community-submitted shows pending/verified/community_supported/flagged.
    // 'isRestricted' + 'restrictionNote' express the orthogonal access axis.
    source: r.source || 'official',
    verificationStatus: r.verification_status || 'verified',
    isRestricted: r.is_restricted === true,
    restrictionNote: r.restriction_note || null,
    // Structured restriction derived once from the note (the sole producer is
    // RESTRICTION.build). Drives eligibility-aware discovery. null when the
    // course isn't restricted. See RESTRICTION / restrictedVisibleByDefault.
    restriction: r.is_restricted === true ? RESTRICTION.parse(r.restriction_note) : null,
    // Column names an admin corrected via apply_report (cne_courses.locked_fields).
    // These are admin-owned: the daily scraper routine writes the admin value
    // back instead of overwriting. The UI surfaces them as an "admin-verified"
    // mark on the matching fact rows. Absent on rows cached/fetched before the
    // locked_fields migration ran → default to [] so callers can `.includes()`
    // without a null-check. See memory: cne_courses-has-two-writers.
    lockedFields: Array.isArray(r.locked_fields) ? r.locked_fields : [],
  };
  out.cneStatusNorm = deriveCneStatusNorm(out);
  out.publicationStatus = derivePublicationStatus(out);
  // Shift-friendliness — see deriveShiftTag. null when the catalogue publishes
  // neither a start time nor a weekday for this course. Set in BOTH normalisers
  // so the seed/Supabase parity check (test-discover.mjs) keeps holding.
  out.shiftTag = deriveShiftTag(r);
  return out;
}

// CATALOGUE_VERSION — bumped on every swap of either course pool (active or
// archived). The pools are module globals, so a component that memoises over
// them has no natural dependency to watch and would keep a stale list after the
// background archived fetch lands. Include `window.CATALOGUE_VERSION` in such a
// useMemo's deps (read at render time). setCourses / setArchivedCourses are the
// ONLY writers — bump here, never at a call site.
let CATALOGUE_VERSION = 0;
window.CATALOGUE_VERSION = CATALOGUE_VERSION;
const bumpCatalogueVersion = () => { window.CATALOGUE_VERSION = ++CATALOGUE_VERSION; };

// Update the global course list (called by app.jsx after Supabase fetch).
function setCourses(arr) {
  COURSES = (arr || []).slice().sort((a, b) =>
    (a.date || '9999') < (b.date || '9999') ? -1 : 1
  );
  window.COURSES = COURSES;
  bumpCatalogueVersion();
  return COURSES;
}

window.normaliseSupabaseCourse = normaliseSupabaseCourse;
window.setCourses = setCourses;

// ── Provider label dictionary (RELEASE.md §78) ───────────────────────
// entity id -> { en, zh }. Populated from provider_labels_v; see
// DB.fetchProviderLabels. Kept here beside setCourses because it is the
// same kind of thing: global catalogue state the whole UI reads.
//
// ‼️ THE MAP INSTANCE IS STABLE AND MUTATED IN PLACE, never reassigned.
// discover-filters.jsx reads window.PROVIDER_LABELS at render time; handing
// out a fresh Map would leave any module that captured the old one reading a
// dictionary that stops being updated.
const PROVIDER_LABELS = new Map();
window.PROVIDER_LABELS = PROVIDER_LABELS;

// ‼️ THE DICTIONARY HAS THREE STATES, AND AN EMPTY MAP IS TWO OF THEM.
// The picker (§92, B1-C) must not render 「找不到相符機構」 while the fetch is
// still in flight or has failed — that pushes a nurse into free text and loses
// the entity she could have picked. `PROVIDER_LABELS.size === 0` cannot tell
// "not here yet" from "nothing matched", so the status is carried separately:
//
//     'loading'  → fetch in flight, or never started
//     'ready'    → the dictionary is whatever it is, including legitimately 0
//     'failed'   → the fetch errored AND no cache stood in for it
//
// Bumping the catalogue version is part of the setter, not the caller's job:
// a status that changes without a re-render is a status nothing can act on.
let PROVIDER_LABELS_STATUS = 'loading';
window.PROVIDER_LABELS_STATUS = PROVIDER_LABELS_STATUS;

function setProviderLabelsStatus(status) {
  if (!['loading', 'ready', 'failed'].includes(status)) return PROVIDER_LABELS_STATUS;
  PROVIDER_LABELS_STATUS = status;
  window.PROVIDER_LABELS_STATUS = status;
  bumpCatalogueVersion();
  return status;
}
window.setProviderLabelsStatus = setProviderLabelsStatus;

// ‼️ DECLARED ABOVE setProviderLabels ON PURPOSE. The setter assigns this
// binding; a `let` declared after it would be correct only for as long as
// nothing calls the setter during module initialisation. Ordering it here makes
// that a property of the file rather than of the call graph.
function setProviderLabels(rows) {
  PROVIDER_LABELS.clear();
  (rows || []).forEach(r => {
    if (!r || !r.provider_entity_id) return;
    PROVIDER_LABELS.set(String(r.provider_entity_id), {
      en: r.label_en || null,
      zh: r.label_zh || r.label_en || null,
    });
  });
  // Rows arrived (even zero of them) — the dictionary is now a fact, not a wait.
  PROVIDER_LABELS_STATUS = 'ready';
  window.PROVIDER_LABELS_STATUS = 'ready';
  bumpCatalogueVersion();
  return PROVIDER_LABELS;
}
window.setProviderLabels = setProviderLabels;


// ── Archived courses (lifecycle_status='archived') ───────────────────
// Kept in a SEPARATE pool from COURSES on purpose: archived rows are
// hidden from default browse (Discover/Home) but must stay findable for
// the "record a course I already finished" flow and for explicit search.
// Loaded lazily in the background (app.jsx) and never written to the
// offline cache, so a cold launch stays lean.
let ARCHIVED_COURSES = [];
function setArchivedCourses(arr) {
  ARCHIVED_COURSES = (arr || []).slice();
  window.ARCHIVED_COURSES = ARCHIVED_COURSES;
  bumpCatalogueVersion();
  return ARCHIVED_COURSES;
}
// active ∪ archived, deduped by id — the "everything a user can search or
// record" set. Active wins on id collision (it's the live row).
function allSearchableCourses() {
  const active = COURSES || [], archived = ARCHIVED_COURSES || [];
  if (!archived.length) return active;
  const seen = new Set(active.map(c => c.id));
  return active.concat(archived.filter(c => !seen.has(c.id)));
}
window.ARCHIVED_COURSES = ARCHIVED_COURSES;
window.setArchivedCourses = setArchivedCourses;
window.allSearchableCourses = allSearchableCourses;

// Does this course match a free-text search query?
//
// ‼️ LANGUAGE-BLIND ON PURPOSE — this is the whole point of the helper.
// Both search boxes used to read each field through `L(field, lang)`, i.e. ONLY
// the variant currently on screen. So a nurse browsing in 繁中 typed "Chronic",
// got 「找不到符合的課程」 on all three tabs, and the course was sitting right
// there under its Chinese title (and the mirror image in EN: a Chinese keyword
// matched nothing at all, because title.en never falls back to the Chinese).
// Measured on the live catalogue 2026-09-04: 471 of 504 rows carry a Chinese
// title, so the displayed-language predicate hid 93% of the catalogue from an
// English keyword — including every one of the 5 rows whose title says
// "Chronic".
//
// The rule this encodes: a search box is not a translation of the list. What
// she types is whatever she happens to remember — an English clinical term off
// a certificate, the provider's course code, a Chinese title forwarded in
// WhatsApp — and the UI language predicts none of that. Hong Kong nursing is
// bilingual per-sentence, not per-user. So match EVERY language variant of
// every searchable field, always, in both directions. `Object.values` (rather
// than reading .en/.zh) keeps that true if a third variant is ever added.
//
// Field set is deliberately unchanged from what the two call sites already
// searched — title, provider short name, topic label, plus the codes. Widening
// it further (full provider name, venue, description) is a separate decision
// with its own false-positive cost; this fix only stops the language from
// deciding which rows exist.
//
// Codes match ignoring case AND whitespace, so "bls 260526a" / "BLS260526A" /
// "bls260526A" all hit the same row.
function courseMatchesQuery(course, query) {
  if (!course) return false;
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;                       // empty query narrows nothing
  const qTight = q.replace(/\s+/g, '');
  const codes = [
    course.courseCode,
    course.raw?.course_code,
    course.raw?.provider_code,
  ].filter(Boolean).map(s => String(s).toLowerCase().replace(/\s+/g, ''));
  if (codes.some(code => code.includes(qTight))) return true;
  return [course.title, course.providerShort, course.topicLabel]
    .flatMap(field => (field && typeof field === 'object') ? Object.values(field) : [field])
    .some(v => v && String(v).toLowerCase().includes(q));
}
// ‼️ data.js is a plain .js file, outside the build's JSX auto-expose plugin —
// an unlisted helper is tree-shaken out of the bundle and every call site
// silently reads `undefined`. Here that would mean an unfiltered course list
// (search stops narrowing) rather than a crash, so it fails quiet. Keep this.
window.courseMatchesQuery = courseMatchesQuery;

// ── Peer-signal rollups (course cards) ────────────────────────────────
// TWO maps, one per identity grain — see supabase-migration-experience-rollups-v2.sql:
//   OFFERING_EXPERIENCE_ROLLUPS  { "<offering_entity_id>": { data_count, organised_count } }
//   SERIES_EXPERIENCE_ROLLUPS    { "<series_entity_id>":   { worth_count, offerings,
//                                                            boundary, generalizable } }
//
// ‼️ THE TWO GRAINS ARE NEVER ADDED TOGETHER. Their numerators count different
// people judging different objects: data_count is "nurses who say THIS LISTING's
// details are right", worth_count is "nurses who attended and say THE COURSE was
// worth it". A combined "N 位護士" would describe nothing that exists.
//
// ‼️ WHY THIS REPLACED A SINGLE REPEAT-KEY MAP. The old rollup grouped by
// course_repeat_key, which merged every edition of a programme into one bucket.
// 28 cards showed a peer number and 25 of them were displaying another sitting's
// evidence — AHKNS BLS alone pooled 21 occurrences, so one nurse finding one
// listing useful lit up twenty others. See RELEASE.md §62.
//
// Empty until the fetch lands, and empty on a database that predates the
// migration (the RPCs 404). Every read path below degrades to "no chips".
// ‼️ NEVER add a fallback to the legacy repeat-key rollup when these are empty:
// that would restore the derived key as evidence authority and undo the whole
// identity programme. A quiet card is the correct output for a card with no
// evidence of its own.
let OFFERING_EXPERIENCE_ROLLUPS = {};
let SERIES_EXPERIENCE_ROLLUPS = {};
function setExperienceRollups(maps) {
  const m = (maps && typeof maps === 'object') ? maps : {};
  const off = (m.offering && typeof m.offering === 'object') ? m.offering : {};
  const ser = (m.series && typeof m.series === 'object') ? m.series : {};
  OFFERING_EXPERIENCE_ROLLUPS = off;
  SERIES_EXPERIENCE_ROLLUPS = ser;
  window.OFFERING_EXPERIENCE_ROLLUPS = OFFERING_EXPERIENCE_ROLLUPS;
  window.SERIES_EXPERIENCE_ROLLUPS = SERIES_EXPERIENCE_ROLLUPS;
  return { offering: off, series: ser };
}

// ‼️ MIRRORS THE SERVER'S TRUST BOUNDARY: official rows only. The server judges
// trust by the reaction's ORIGIN row, and this judges it by the card's own row —
// the same question asked at the same grain. An issued entity id is NOT a trust
// signal: 45 of 52 community rows carry one, and two offerings hold an official
// row and a community duplicate together, so keying on the id alone would show a
// community vouch on an official card.
const officialRaw = (course) => {
  const raw = course && course.raw;
  return (raw && raw.source === 'official') ? raw : null;
};

// What the nurses who dealt with THIS sitting said. Safe to render as 「這一場」:
// it claims nothing beyond the occurrence it is keyed to.
function courseOccurrenceCounts(course) {
  const raw = officialRaw(course);
  // ‼️ cne_courses_v exposes offering_entity_id as of RELEASE.md §59. Before that
  // refresh this is undefined everywhere and the card simply shows no occurrence
  // chip — fail-closed, never a fallback to a coarser key.
  const key = raw && raw.offering_entity_id;
  if (!key) return null;
  const row = OFFERING_EXPERIENCE_ROLLUPS[key];
  if (!row) return null;
  const data = Number(row.data_count) || 0;
  const organised = Number(row.organised_count) || 0;
  if (!data && !organised) return null;
  return { data, organised };
}

// What nurses say about the course PRODUCT across its runs — worth_it only.
//
// ‼️ `generalizable` GOVERNS THE COPY. It is true only when the evidence spans
// two or more occurrences. A single-occurrence series has no past runs to
// generalise from, and a provisional one has an open boundary on top of that.
// The numbers are still real: narrow the sentence, never hide them.
function courseSeriesCounts(course) {
  const raw = officialRaw(course);
  const key = raw && raw.series_entity_id;
  if (!key) return null;
  const row = SERIES_EXPERIENCE_ROLLUPS[key];
  if (!row) return null;
  const worth = Number(row.worth_count) || 0;
  if (!worth) return null;
  return {
    worth,
    offerings: Number(row.offerings) || 0,
    boundary: row.boundary === 'confirmed' ? 'confirmed' : 'provisional',
    generalizable: row.generalizable === true,
  };
}
window.OFFERING_EXPERIENCE_ROLLUPS = OFFERING_EXPERIENCE_ROLLUPS;
window.SERIES_EXPERIENCE_ROLLUPS = SERIES_EXPERIENCE_ROLLUPS;


// ── Review chip vocabulary ────────────────────────────────────────────
// ‼️ THE SERVER IS AUTHORITATIVE. review_course_chips() / review_provider_chips()
// in supabase-migration-course-reviews.sql validate every submission and a CHECK
// constraint backs them, so a chip added here alone is rejected at submit with
// 'unknown course chip'. Change both in the same commit; scripts/
// test-course-reviews-contract.mjs fails if these two lists drift apart.
//
// The split is not cosmetic. Course chips describe the teaching; provider chips
// describe things the ORGANISATION controls — enrolment, certificate turnaround,
// venue, admin, accuracy. Averaging course quality into an organisation score
// would measure individual speakers, and it is also the wrong side of the
// defamation line for a named Hong Kong institution: "certificates took three
// months" is a verifiable fact, "this college is bad" is not.
// ‼️ MIRRORS review_chip_catalog() IN supabase-migration-course-reviews.sql,
// key for key. The server validates every submission against its own copy and a
// CHECK constraint backs it, so a chip that exists only here is rejected at
// submit with 'unknown course chip'. npm run test:reviews fails if the two
// drift — including if a bucket list stops covering the whole vocabulary.
//
//   group    Mutually exclusive. Picking 講者差 clears 講者講得好, because they
//            are the same question with opposite answers. The server enforces
//            this too (chip counts are public), so this is UX, not the guard.
//   buckets  Which star ratings OFFER the chip. Guidance only: a chip picked at
//            5★ STAYS SELECTED when she drops to 2★ — it is simply no longer in
//            the offered set, so it renders pinned at the front instead of
//            being silently deleted. The DB never had an opinion about which
//            rating a chip belongs to.
const REVIEW_CHIP_CATALOG = [
  { key: 'speaker_good',    target: 'course',   group: 'speaker',    buckets: [3, 4, 5], zh: '講者講得好',   en: 'Great speaker' },
  { key: 'speaker_poor',    target: 'course',   group: 'speaker',    buckets: [1, 2, 3], zh: '講者一般',     en: 'Weak speaker' },
  { key: 'content_useful',  target: 'course',   group: 'usefulness', buckets: [3, 4, 5], zh: '內容實用',     en: 'Useful content' },
  { key: 'content_thin',    target: 'course',   group: 'usefulness', buckets: [1, 2, 3], zh: '內容不夠實用', en: 'Not that useful' },
  { key: 'right_level',     target: 'course',   group: 'level',      buckets: [3, 4, 5], zh: '深淺啱',       en: 'Right level' },
  { key: 'too_basic',       target: 'course',   group: 'level',      buckets: [1, 2, 3], zh: '內容太淺',     en: 'Too basic' },
  { key: 'good_materials',  target: 'course',   group: 'materials',  buckets: [3, 4, 5], zh: '教材充足',     en: 'Good materials' },
  { key: 'poor_materials',  target: 'course',   group: 'materials',  buckets: [1, 2, 3], zh: '教材唔夠',     en: 'Thin materials' },
  { key: 'good_pace',       target: 'course',   group: 'pace',       buckets: [4, 5],    zh: '節奏啱',       en: 'Good pace' },
  { key: 'too_rushed',      target: 'course',   group: 'pace',       buckets: [1, 2, 3], zh: '趕時間',       en: 'Too rushed' },
  { key: 'easy_enrolment',  target: 'provider', group: 'enrolment',  buckets: [3, 4, 5], zh: '報名方便',     en: 'Easy to enrol' },
  { key: 'hard_enrolment',  target: 'provider', group: 'enrolment',  buckets: [1, 2, 3], zh: '報名麻煩',     en: 'Awkward to enrol' },
  { key: 'cert_fast',       target: 'provider', group: 'cert',       buckets: [3, 4, 5], zh: '證書好快收到', en: 'Certificate was fast' },
  { key: 'cert_slow',       target: 'provider', group: 'cert',       buckets: [1, 2, 3], zh: '證書等好耐',   en: 'Certificate was slow' },
  { key: 'venue_good',      target: 'provider', group: 'venue',      buckets: [3, 4, 5], zh: '場地交通方便', en: 'Convenient venue' },
  { key: 'venue_poor',      target: 'provider', group: 'venue',      buckets: [1, 2, 3], zh: '場地唔方便',   en: 'Awkward venue' },
  { key: 'admin_smooth',    target: 'provider', group: 'admin',      buckets: [3, 4, 5], zh: '行政安排妥當', en: 'Smooth admin' },
  { key: 'admin_messy',     target: 'provider', group: 'admin',      buckets: [1, 2, 3], zh: '行政安排混亂', en: 'Messy admin' },
  { key: 'info_inaccurate', target: 'provider', group: 'accuracy',   buckets: [1, 2, 3, 4, 5], zh: '資料同實際不符', en: 'Details did not match' },
];

// Star rating → which bucket of chips to OFFER. 1–2 / 3 / 4–5, as agreed.
function reviewChipBucket(stars) {
  const n = Number(stars) || 0;
  if (n <= 2) return n || null;
  return n;
}

// Chips to render for one target at one rating: everything already SELECTED
// (pinned first, whatever bucket it came from) followed by the bucket's offers.
// Keeping a selection visible after the rating changes is the whole point —
// "體驗好但講師差" is a real combination, and silently dropping a chip she chose
// would look like the app editing her answer.
function reviewChipsFor(target, stars, selected) {
  const chosen = new Set(selected || []);
  const all = REVIEW_CHIP_CATALOG.filter(c => c.target === target);
  const pinned = all.filter(c => chosen.has(c.key));
  const offered = all.filter(c => !chosen.has(c.key) && c.buckets.includes(Number(stars)));
  return pinned.concat(offered);
}

// Applying a selection: within a group only one chip may be held, so choosing
// one clears its opposite. Mirrors review_chips_conflict() server-side.
function reviewToggleChip(selected, key) {
  const list = (selected || []).slice();
  const i = list.indexOf(key);
  if (i >= 0) { list.splice(i, 1); return list; }
  const meta = REVIEW_CHIP_CATALOG.find(c => c.key === key);
  if (!meta) return list;
  const sameGroup = new Set(
    REVIEW_CHIP_CATALOG.filter(c => c.group === meta.group && c.key !== key).map(c => c.key));
  return list.filter(k => !sameGroup.has(k)).concat(key);
}

// Legacy flat lists, derived — kept because callers and tests already read them.
const REVIEW_COURSE_CHIPS   = REVIEW_CHIP_CATALOG.filter(c => c.target === 'course');
const REVIEW_PROVIDER_CHIPS = REVIEW_CHIP_CATALOG.filter(c => c.target === 'provider');

// ── Review rollups (stars + chips) ────────────────────────────────────
// TWO maps now, one per aggregation grain — see
// supabase-migration-course-reviews.sql:
//   SERIES_REVIEW_ROLLUPS    { "<series_entity_id>":   { n, offerings, avg_stars, chips, boundary, generalizable } }
//   OFFERING_REVIEW_ROLLUPS  { "<offering_entity_id>": { n, avg_stars, chips } }
//
// ‼️ KEYED ON ISSUED IDENTITY, NOT ON A DERIVED KEY. The old single map was
// keyed by course_repeat_key, which is GENERATED from provider + title and
// re-keys whenever a title correction is accepted. Entity ids are opaque and
// move only by declared supersession.
//
// ‼️ THE THRESHOLDS ARE THE SERVER'S. A group below the display floor is absent
// from these maps entirely, and `avg_stars` is null until n reaches 5 — a mean
// of one renders identically to a mean of two hundred, so the number is
// withheld at the source rather than trusted to the renderer. Do not
// reintroduce it client-side from `n` and some other field; there is nothing to
// reintroduce.
let SERIES_REVIEW_ROLLUPS = {};
let OFFERING_REVIEW_ROLLUPS = {};
function setReviewRollups(maps) {
  const m = (maps && typeof maps === 'object') ? maps : {};
  // Accepts { series, offering }. A bare map is treated as series-only so a
  // half-deployed backend degrades to fewer numbers, never to wrong ones.
  const series   = m.series   || (m.offering ? {} : m);
  const offering = m.offering || {};
  SERIES_REVIEW_ROLLUPS   = (series   && typeof series   === 'object') ? series   : {};
  OFFERING_REVIEW_ROLLUPS = (offering && typeof offering === 'object') ? offering : {};
  window.SERIES_REVIEW_ROLLUPS   = SERIES_REVIEW_ROLLUPS;
  window.OFFERING_REVIEW_ROLLUPS = OFFERING_REVIEW_ROLLUPS;
  return { series: SERIES_REVIEW_ROLLUPS, offering: OFFERING_REVIEW_ROLLUPS };
}

// ‼️ MIRRORS THE SERVER'S TRUST BOUNDARY, exactly as the peer-signal accessors do:
// official rows only. An issued identity is NOT a trust signal — 45 of the 52
// community-submitted rows carry one, and two offerings hold an official row
// and a community duplicate together. The server refuses to let a community row
// be reviewed at all; this refuses to display for it. Keep the two in step.
function courseSeriesKey(course) {
  const raw = course && course.raw;
  if (!raw || raw.source !== 'official') return null;
  return raw.series_entity_id || null;
}
function courseOfferingKey(course) {
  const raw = course && course.raw;
  if (!raw || raw.source !== 'official') return null;
  // ‼️ cne_courses_v does not expose offering_entity_id yet (RELEASE.md §59 —
  // re-run supabase-migration-course-view-refresh.sql to append it). Until then
  // this is null everywhere and the occurrence summary is simply absent, which
  // is the fail-closed direction: no signal rather than the wrong signal.
  return raw.offering_entity_id || null;
}

function shapeReviewSummary(row) {
  if (!row) return null;
  const n = Number(row.n) || 0;
  if (!n) return null;
  const avg = row.avg_stars == null ? null : Number(row.avg_stars);
  return {
    n,
    // null is a real state, not a missing value: "rated, but not by enough
    // nurses to average honestly". The UI shows the count and the chips.
    avg: Number.isFinite(avg) ? avg : null,
    chips: (row.chips && typeof row.chips === 'object') ? row.chips : {},
  };
}

// Series-level summary: what this COURSE is like across its runs.
//
// ‼️ `generalizable` GOVERNS THE COPY, not just the number. It is false when the
// evidence comes from a single occurrence — a provisional singleton series has
// an open boundary on top of that. The caller must not say "包括過往班次" /
// "including past runs" when it is false: there are no past runs in the
// evidence. `boundary` carries the series grouping_status for the same reason.
// Provisional is an UNKNOWN BOUNDARY, not an unknown object — never a reason to
// hide the evidence itself.
function courseReviewSummary(course) {
  const key = courseSeriesKey(course);
  if (!key) return null;
  const row = SERIES_REVIEW_ROLLUPS[key];
  const base = shapeReviewSummary(row);
  if (!base) return null;
  return {
    ...base,
    offerings: Number(row.offerings) || 0,
    boundary: row.boundary === 'confirmed' ? 'confirmed' : 'provisional',
    generalizable: row.generalizable === true,
  };
}

// Occurrence-level summary: what the nurses who attended THAT sitting said.
// Always safe to render — it claims nothing beyond the occurrence it is keyed
// to, so it carries no generalisation flag.
function courseOccurrenceReviewSummary(course) {
  const key = courseOfferingKey(course);
  if (!key) return null;
  return shapeReviewSummary(OFFERING_REVIEW_ROLLUPS[key]);
}
window.SERIES_REVIEW_ROLLUPS = SERIES_REVIEW_ROLLUPS;
window.OFFERING_REVIEW_ROLLUPS = OFFERING_REVIEW_ROLLUPS;

// ── Enrolment → course resolution (single source of truth) ────────────
// An `enrollments` row stores a bare course id, and that id outlives the ACTIVE
// catalogue: the import routine archives a course once it is no longer offered,
// which moves the row out of COURSES into the separate ARCHIVED_COURSES pool
// (live on 2026-08-04: 22 of 47 enrolment rows, 14 of 21 distinct courses), and
// a hard-deleted row resolves to nothing at all.
//
// ⚠️ GUARD — EVERY COUNT AND EVERY LIST MUST COME THROUGH HERE. The phantom
// "進行中 2" badge over an empty 正在參與的課程 list was exactly this split: the
// badges counted raw `enrolledIds`, while the sections rendered
// `COURSES.filter(enrolled)`, so an archived enrolment was counted but never
// drawn. Resolving against active ∪ archived also keeps that enrolment
// actionable (tick attendance / upload the certificate) instead of silently
// vanishing. scripts/test-data.mjs fails if a count call site reads
// `enrolledIds.size` again, or if a section filters a raw course pool.
function resolveEnrolledCourses(enrolledIds) {
  const size = enrolledIds ? enrolledIds.size : 0;
  if (!size) return { courses: [], orphanIds: [], catalogueReady: true };
  const pool = allSearchableCourses();
  // Catalogue not hydrated yet (cold launch before the first fetch, or an
  // offline cache miss). We cannot tell "archived/deleted" from "not loaded
  // yet", so report NOT ready and let callers keep the raw count rather than
  // flash 0 and then jump back up.
  if (!pool.length) return { courses: [], orphanIds: [], catalogueReady: false };
  // Filter the pool rather than the id set, so cards keep catalogue order
  // (start_date ascending, archived pool appended).
  const courses = pool.filter(c => enrolledIds.has(c.id));
  const found = new Set(courses.map(c => c.id));
  return { courses, orphanIds: Array.from(enrolledIds).filter(id => !found.has(id)), catalogueReady: true };
}

// Badge-safe enrolment count: how many enrolments actually have a card to
// render. Never lower than what the user can see — while the catalogue is
// unhydrated it falls back to the raw set size.
function enrolledCourseCount(enrolledIds) {
  const size = enrolledIds ? enrolledIds.size : 0;
  if (!size) return 0;
  const { courses, catalogueReady } = resolveEnrolledCourses(enrolledIds);
  return catalogueReady ? courses.length : size;
}
window.resolveEnrolledCourses = resolveEnrolledCourses;
window.enrolledCourseCount = enrolledCourseCount;

// ─────────────────────────────────────────────────────────────────
// Date filters retained for back-compat consumers (provider counts, detail
// state, catalogue picker). Event time is authoritative: ongoing multi-day
// courses remain current; cancelled/completed and non-active rows are past.
// Date-TBC rows are not past, but Discover gives them their own explicit tab.
// ─────────────────────────────────────────────────────────────────
function isPastCourse(c, today) {
  // Non-active lifecycle (archived / rejected_low_confidence) is treated
  // as "past" so deep links to withdrawn courses don't show a live
  // Register CTA. fetchCourses already filters these out at the list
  // level via lifecycle_status='active', but fetchCourseById intentionally
  // allows deep-linking to them so users see *something* meaningful.
  if (c?.lifecycleStatus && c.lifecycleStatus !== 'active') return true;
  return ['completed', 'cancelled'].includes(deriveEventStatus(c, today));
}
function isUpcomingCourse(c, today) {
  return !isPastCourse(c, today);
}

window.isPastCourse     = isPastCourse;
window.isUpcomingCourse = isUpcomingCourse;

// ─────────────────────────────────────────────────────────────────────────────
// User profile  (unchanged from v1)
// ─────────────────────────────────────────────────────────────────────────────
const USER = {
  name: { en: 'Wing Yi Chan', zh: '陳大文' },
  shortName: { en: 'Wing Yi', zh: '詠儀' },
  initials: 'WY',
  role: { en: 'Registered Nurse · ICU', zh: '註冊護士 · 深切治療部' },
  license: 'RN 24-08217',
  hospital: { en: 'Queen Mary Hospital', zh: '瑪麗醫院' },
  renewalDate: '2026-12-31',
  cycleStart: '2024-01-01',
  required: 45,
  earned: 47.5,
};

const RECORD = [
  { id: 'r-001', date: '2026-04-18', title: { en: 'Pressure Injury Prevention 2026 Update', zh: '2026 年壓瘡預防更新' }, provider: { en: 'HKAN', zh: '香港護理學院' }, points: 3, category: 'core', certificate: true, year: 2026 },
  { id: 'r-002', date: '2026-03-04', title: { en: 'BLS Provider Renewal', zh: 'BLS 證書續期' }, provider: { en: 'AHA · HK', zh: '美心協會 · 香港' }, points: 2, category: 'core', certificate: true, year: 2026 },
  { id: 'r-003', date: '2026-02-21', title: { en: 'Ventilator Modes for the Bedside Nurse', zh: '呼吸機模式：床邊護士篇' }, provider: { en: 'HA · IHE', zh: '醫管局 · IHE' }, points: 5, category: 'specialty', certificate: true, year: 2026 },
  { id: 'r-004', date: '2026-01-30', title: { en: 'Ethics in End-of-Life Decision-Making', zh: '臨終決策中的倫理' }, provider: { en: 'HKAN', zh: '香港護理學院' }, points: 2, category: 'core', certificate: false, year: 2026 },
  { id: 'r-005', date: '2025-11-12', title: { en: 'Diabetes Mellitus: Insulin Titration', zh: '糖尿病：胰島素調節' }, provider: { en: 'PolyU SN', zh: '理大護理學院' }, points: 4, category: 'specialty', certificate: true, year: 2025 },
  { id: 'r-006', date: '2025-10-04', title: { en: 'Conflict Resolution at the Bedside', zh: '床邊衝突處理' }, provider: { en: 'HKU SPACE', zh: '港大專業進修學院' }, points: 2, category: 'elective', certificate: true, year: 2025 },
  { id: 'r-007', date: '2025-09-19', title: { en: 'Stroke Care Pathway Update', zh: '中風護理路徑更新' }, provider: { en: 'HA · IHE', zh: '醫管局 · IHE' }, points: 3, category: 'specialty', certificate: true, year: 2025 },
  { id: 'r-008', date: '2025-07-08', title: { en: 'Reflective Practice Workshop', zh: '反思實踐工作坊' }, provider: { en: 'HKAN', zh: '香港護理學院' }, points: 2, category: 'elective', certificate: true, year: 2025 },
  { id: 'r-009', date: '2025-05-22', title: { en: 'Medication Safety: High-alert Drugs', zh: '用藥安全：高警示藥物' }, provider: { en: 'HA · IHE', zh: '醫管局 · IHE' }, points: 3, category: 'core', certificate: true, year: 2025 },
  { id: 'r-010', date: '2025-03-10', title: { en: 'Cultural Competence in Nursing', zh: '護理中的文化能力' }, provider: { en: 'CUHK', zh: '中文大學' }, points: 2, category: 'elective', certificate: true, year: 2025 },
  { id: 'r-011', date: '2024-12-02', title: { en: 'Wound Dressing Selection', zh: '傷口敷料選擇' }, provider: { en: 'PolyU SN', zh: '理大護理學院' }, points: 3, category: 'specialty', certificate: true, year: 2024 },
  { id: 'r-012', date: '2024-09-14', title: { en: 'Nursing Documentation Audit', zh: '護理文件審核' }, provider: { en: 'HA · IHE', zh: '醫管局 · IHE' }, points: 2, category: 'core', certificate: true, year: 2024 },
];

const REMINDERS = [
  { id: 'rm-1', kind: 'enrolled', date: '2026-05-22', title: { en: 'PALS Provider Course', zh: 'PALS 課程' }, sub: { en: 'In 10 days · AHKNS', zh: '10 天後 · AHKNS' } },
  { id: 'rm-2', kind: 'cert',     date: '2026-04-18', title: { en: 'Upload your certificate', zh: '上傳證書' }, sub: { en: 'Pressure Injury Prevention · 3 pts pending', zh: '壓瘡預防 · 待認可 3 分' } },
  { id: 'rm-3', kind: 'renewal',  date: '2026-12-31', title: { en: '234 days to renewal', zh: '距續期 234 日' }, sub: { en: '12.5 points still needed', zh: '仍需 12.5 分' } },
];

// Qualification wallet helpers. Matching is deliberately conservative: it may
// suggest a category after an AI scan or offer a relevant CNE certificate, but
// it must never silently turn an ordinary course into a professional credential.
function qualificationKindFromTitle(value) {
  const s = typeof value === 'object'
    ? `${value?.en || ''} ${value?.zh || ''}`
    : String(value || '');
  if (/\b(?:NCHK|RN|EN)\b|nursing council|registered nurse|enrolled nurse|practi[cs]ing certificate|midwi(?:fe|fery)|護士管理局|註冊.*護士|登記.*護士|執業證明|助產士/i.test(s)) return 'practising';
  if (/\b(?:APN|PRCC|HKANM)\b|advanced practice nurse|special(?:ist|ty) nursing|fellowship|專科護理|專科資格|院士/i.test(s)) return 'specialty';
  if (/\b(?:diploma|bachelor|master(?:'s)?|doctor(?:ate)?|BSc|MSc|PhD|DNurs)\b|護理(?:文憑|學士|碩士|博士)|學位/i.test(s)) return 'education';
  if (/\b(?:BLS|ACLS(?:-EP)?|PALS|NRP|ILS|CPR|AED|ALSO)\b|basic life support|advanced (?:cardiac|cardiovascular).*life support|paediatric advanced life support|pediatric advanced life support|neonatal resuscitation|first aid|基礎生命支援|高級心臟?生命支援|兒科高級生命支援|新生兒復甦|急救/i.test(s)) return 'resuscitation';
  if (/\b(?:ATLS|ITLS|PHTLS|TNCC|ENPC|PEARS)\b|advanced trauma life support|trauma nursing|創傷|急症/i.test(s)) return 'trauma';
  // A workshop about infection control is ordinary CNE. Only suggest the
  // clinical-skills wallet when the document itself looks like a competency
  // certification/authorisation.
  if (/\b(?:venepuncture|phlebotomy|IV cannulation|intravenous cannulation|blood transfusion|chemotherapy|dialysis|ventilator|ECG|wound care|stoma|infection control)\b|抽血|靜脈置管|輸血|化療|洗腎|呼吸機|傷口|造口|感染控制/i.test(s)
      && /\b(?:competenc(?:y|ies)|certif(?:icate|ication)|authori[sz]ation)\b|能力認證|認證/i.test(s)) return 'clinical';
  return null;
}


// ─────────────────────────────────────────────────────────────────────────
// walletItems — THE definition of "one credential in 我的資格" (§72 C2)
//
// One concept, one derivation. Every surface that claims a wallet number reads
// this: the personal-page count, the wallet rows, the 全部 chip, nearest expiry.
// The bug that started this whole line was two surfaces deriving the same idea
// separately and disagreeing (Personal 1, Wallet 0); making them share STATE was
// not enough, because state can be shared while the derivation drifts.
//
// ‼️ WHAT MAY FEED IT, AND WHAT MAY NOT.
// Practising items come from the PERSISTED identity registry — the occupied slot
// bindings B5.1 minted — never from profileLicenses(). That helper synthesizes a
// primary descriptor for every account with the role defaulted, so minting wallet
// items from it would show 302 nurses a practising registration they never
// claimed. That is a false professional identity, not a display bug, and
// walletItemsFromLicences() below cannot produce one because it has nothing to
// produce it from.
//
// Also NOT inputs: cycle_history (that is B5.2's past-attribution question, a
// different set), evidence, CNE records, or any slot-shaped inference.
//
// ‼️ IDS ARE NAMESPACED. A practising item and a qualification are different
// entities, and their raw uuids come from different tables — colliding them in
// one id space is how a focus, a delete or a navigation eventually lands on the
// wrong domain. `licence:<instance>` and `qualification:<id>` cannot be confused
// even by accident.
function walletItems({ licences = [], qualifications = [] } = {}) {
  const licenceItems = (licences || [])
    // Archived identities are history, not current credentials. They still exist
    // so past cycles keep an owner (§72 C13); they are not things she holds.
    .filter(l => l && l.licenseInstanceId && !l.archivedAt)
    .map(l => ({
      kind: 'practising_licence',
      id: `licence:${l.licenseInstanceId}`,
      licenseInstanceId: l.licenseInstanceId,
      // Display and navigation only. A slot is not an identity — nothing may
      // group, attribute or persist against this field.
      slot: l.slot || null,
      registrationType: l.registrationType || null,
      licenseNo: l.licenseNo || null,
      expiresOn: l.renewalDate || null,
      needsResolution: !!l.needsResolution,
    }));

  const qualificationItems = (qualifications || [])
    .filter(q => q && !q.archivedAt)
    .map(q => {
      const current = q.currentVersion || q.current
        || (q.versions || []).find(v => v && v.isCurrent) || null;
      return {
        kind: 'qualification',
        id: `qualification:${q.id}`,
        qualificationId: q.id,
        title: q.title || null,
        qualificationKind: q.kind || null,
        expiresOn: (current && !current.doesNotExpire && current.expiresOn) || null,
        doesNotExpire: !!(current && current.doesNotExpire),
        source: q,
      };
    });

  return [...licenceItems, ...qualificationItems];
}

// Nearest expiry across the wallet.
//
// ‼️ A MISSING DATE IS NOT A DATE. An item with no expiry — a licence whose
// renewal date she has not entered, a qualification marked "does not expire" —
// simply does not compete here. Treating absent as 0 sorts it first ("expiring
// now"); treating it as Infinity sorts it last but still lets it win an empty
// field. Neither is true: we do not know, so it does not answer this question.
// Whether "we do not know" deserves attention is C3's decision, not this one.
function walletNearestExpiry(items) {
  const dated = (items || []).filter(i => i && i.expiresOn);
  if (!dated.length) return null;
  return dated.reduce((a, b) => (String(a.expiresOn) <= String(b.expiresOn) ? a : b));
}


// Per-item presentation state (§72 C3). Existence was already decided by
// walletItems(); this only says how one item should read.
//
// ‼️ TWO INDEPENDENT PREDICATES, NOT ONE ENUM. An item can be both at once — a
// licence with no number recorded whose renewal is three days away is incomplete
// AND needs attention — and forcing a single value would make something choose
// which truth to discard. They also drive different UI: incompleteness belongs
// in the card body as something she can act on, attention belongs on the warning
// pill. One flag driving both is how "you have not finished setting up" ends up
// wearing the same red as "this expires on Friday".
//
// ‼️ A MISSING DATE IS NOT A RISK. `renewal_date = null` means we do not know
// when it expires, not that it expires soon. Before this, `missing_expiry` was
// counted as attention, which would have lit a warning on every profile that
// never finished onboarding — 46 of 254 occupied slots, measured.
//
// ‼️ needsResolution IS NOT AN EXPIRY RISK. A registration-type change awaiting
// review is an identity-integrity state; folding it into attention would make a
// pill that means "expiring" also mean "we are unsure which licence this is".
// It gets its own flag and C4 decides what it looks like.
//
// ‼️ `missing` IS ITEMISED ON PURPOSE. Measured 2026-08-18: 236 of 254 occupied
// slots carry no licence number — 93%. So `isIncomplete` alone is nearly always
// true and is a poor thing to prompt on. C4 should drive its 補上資料 CTA from
// `missing.includes('renewalDate')` (46 of 254), and treat a missing number as
// audit-readiness detail rather than a setup gap shouted on every card.

// Join identity to facts for the wallet (§72 C4).
//
// ‼️ TWO OWNERS, JOINED HERE AND NOWHERE ELSE. The registry owns which licences
// exist and what their stable ids are; Profile owns their current facts — the
// renewal date, the licence number. Neither may read the other's column, so the
// join is explicit and lives with the derivation rather than inside either
// source.
//
// ‼️ IDENTITIES DRIVE EXISTENCE, PROFILE ONLY DECORATES. The iteration is over
// registry rows, never over profileLicenses(): an account with no persisted
// occupied slot produces nothing here no matter what its role column says. That
// is the 302-accounts invariant, expressed as a loop rather than as a filter
// somebody could later "simplify" away.
function licencesForWallet(profile, identities) {
  const p = profile || {};
  const l2 = (p.license2 && typeof p.license2 === 'object') ? p.license2 : {};
  return (identities || []).map((row) => {
    const secondary = row.slot === 'secondary';
    return {
      slot: row.slot,
      licenseInstanceId: row.licenseInstanceId,
      archivedAt: row.archivedAt || null,
      needsResolution: !!row.needsResolution,
      // Registration type comes from the registry, which is where C13 put it.
      registrationType: row.registrationType
        || (secondary ? (l2.role_en || null) : (p.role_en || null)),
      licenseNo: (secondary ? l2.license_no : p.license_no) || null,
      renewalDate: (secondary ? l2.renewal_date : p.renewal_date) || null,
    };
  });
}


// Which document, if any, proves THIS licence for THIS period (§72 C5).
//
// ‼️ THE KEY IS INSTANCE **AND** PERIOD, AND BOTH HALVES ARE LEAD-BEARING.
// A dual-registered nurse can hold two certificates expiring on the same day, so
// matching on the date alone would show her RM as evidenced because her RN was.
// And matching on the instance alone would show last year's certificate as proof
// of this year's period — which is precisely what covers_renewal_date exists to
// prevent. Same-date is not the edge case; it is the common one for a nurse who
// renewed both registrations together.
function currentLicenceEvidence(evidence, item) {
  if (!item || item.kind !== 'practising_licence' || !item.expiresOn) return null;
  return (evidence || []).find(e => e
    && !e.supersededAt
    && e.licenseInstanceId === item.licenseInstanceId
    && String(e.coversRenewalDate) === String(item.expiresOn)) || null;
}

// Everything she has ever filed for this licence, newest period first. Old
// periods are history, not clutter: a certificate that proved 2027 stays proof
// of 2027 after she renews into 2030.
function licenceEvidenceHistory(evidence, item) {
  if (!item || item.kind !== 'practising_licence') return [];
  return (evidence || [])
    .filter(e => e && e.licenseInstanceId === item.licenseInstanceId)
    .sort((a, b) => String(b.coversRenewalDate).localeCompare(String(a.coversRenewalDate)));
}

function walletItemState(item, today) {
  if (!item) return { isIncomplete: false, needsAttention: false, identityNeedsResolution: false, missing: [], expiry: null };

  const missing = [];
  if (item.kind === 'practising_licence') {
    if (!item.expiresOn) missing.push('renewalDate');
    if (!item.licenseNo) missing.push('licenseNo');
  } else if (item.kind === 'qualification') {
    if (!item.expiresOn && !item.doesNotExpire) missing.push('expiresOn');
  }

  // Attention is computed ONLY from a date we actually have.
  let expiry = null;
  let needsAttention = false;
  if (item.expiresOn) {
    const base = _axisToday(today);
    const days = Math.round(
      (new Date(`${item.expiresOn}T00:00:00`) - new Date(`${base}T00:00:00`)) / 86400000);
    expiry = { on: item.expiresOn, days };
    needsAttention = days <= 90;
  }

  return {
    isIncomplete: missing.length > 0,
    needsAttention,
    identityNeedsResolution: !!item.needsResolution,
    missing,
    expiry,
  };
}

function qualificationStatus(qualification, today) {
  const current = qualification?.current;
  if (qualification?.archivedAt) return { key: 'archived', days: null, rank: 6 };
  if (!current) return { key: 'missing', days: null, rank: 4 };
  if (current.doesNotExpire) return { key: 'permanent', days: null, rank: 5 };
  if (!current.expiresOn) return { key: 'missing_expiry', days: null, rank: 3 };
  const base = _axisToday(today);
  const days = Math.round((new Date(`${current.expiresOn}T00:00:00`) - new Date(`${base}T00:00:00`)) / 86400000);
  if (days < 0) return { key: 'expired', days, rank: 0 };
  if (days <= 7) return { key: 'due_7', days, rank: 1 };
  if (days <= 30) return { key: 'due_30', days, rank: 1 };
  if (days <= 90) return { key: 'due_90', days, rank: 2 };
  return { key: 'valid', days, rank: 5 };
}

// ─────────────────────────────────────────────────────────────────────────
// Client-side reminders derivation
// Given the user's enrolled courses, their records, and profile, produce
// a synthetic reminders list. No DB write — these are recomputed on every
// render of the home screen.
//
// Rules:
//   • enrolled  — every enrolled course whose date is in the next 30 days
//   • cert      — every record without a certificate_url, ≤90 days old
//   • renewal   — profile.renewal_date if within 120 days, OR if user is
//                 behind their required-points pace at any time
// ─────────────────────────────────────────────────────────────────────────
function deriveReminders({ profile, enrolledIds, records, courses, today }) {
  const out = [];
  const now = _axisToday(today);   // local date, never UTC — see todayIso
  const dayMs = 86400000;
  const daysTo = (iso) => Math.round((new Date(iso) - new Date(now)) / dayMs);

  // 1. Upcoming enrolled courses
  const enrolled = new Set(enrolledIds || []);
  (courses || []).forEach(c => {
    if (!enrolled.has(c.id)) return;
    if (!c.date) return;
    const d = daysTo(c.date);
    if (d < 0 || d > 30) return;
    const provShort = (c.providerShort && (c.providerShort.en || c.providerShort.zh)) || '';
    out.push({
      id: 'auto-enr-' + c.id,
      kind: 'enrolled',
      date: c.date,
      courseId: c.id,
      _days: d,
      title: c.title || { en: 'Upcoming course', zh: '即將舉行的課程' },
      sub: {
        en: (d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : `In ${d} days`) + (provShort ? ' · ' + provShort : ''),
        zh: (d === 0 ? '今日' : `${d} 天後`) + (provShort ? ' · ' + (c.providerShort.zh || provShort) : ''),
      },
    });
  });

  // 2. Records still missing a certificate
  (records || []).forEach(r => {
    if (r.certificate || r.certificateUrl || r.certificate_url) return;
    if (!r.date) return;
    const age = -daysTo(r.date);    // positive = past
    if (age > 90) return;
    out.push({
      id: 'auto-cert-' + r.id,
      kind: 'cert',
      recordId: r.id,
      date: r.date,
      _days: age,
      title: { en: 'Upload your certificate', zh: '上傳證書' },
      sub: {
        en: (r.title?.en || 'Logged course') + ` · ${r.points || 0} pts pending`,
        zh: (r.title?.zh || r.title?.en || '已記錄課程') + ` · 待認可 ${r.points || 0} 分`,
      },
    });
  });

  // 3. Renewal countdown — one per practising certificate. A dual-registered
  // nurse gets a reminder per cycle, each labelled with its role (which encodes
  // the register part, e.g. 精神科) so the two are distinguishable; a
  // single-licence nurse gets exactly one, unlabelled and identical to before.
  const renewalLicenses = profileLicenses(profile);
  const showRenewalRole = renewalLicenses.length > 1;
  for (const lic of renewalLicenses) {
    if (!lic.renewalDate) continue;
    const renewIso = lic.renewalDate;
    const d = daysTo(renewIso);                       // days to PC expiry (renew-by)
    // Expired certificate — the countdown below never fires once d < 0, which
    // used to leave an expired cycle with NO reminder at all. Routes to the
    // rollover flow. default_dates (untouched onboarding seed pair) is excluded:
    // that date isn't real, so those users get the confirm-date hygiene prompt
    // (Edit Profile banner), never a false "已續期？".
    const lifecycle = licenseLifecycleState(lic, profile, now);
    if (lifecycle === 'expired') {
      const roleSufEn = showRenewalRole && lic.role ? ` · ${lic.role.en}` : '';
      const roleSufZh = showRenewalRole && lic.role ? ` · ${lic.role.zh}` : '';
      out.push({
        id: 'auto-expired-' + lic.key,
        kind: 'expired',
        licKey: lic.key,
        date: renewIso,
        _days: d, // negative → sorts above every countdown
        title: {
          en: 'Certificate expired — renewed?' + roleSufEn,
          zh: '證書已過期——已續期？' + roleSufZh,
        },
        sub: {
          en: `Expired ${fmtShort(renewIso, 'en')} — tap to update your new cycle`,
          zh: `於 ${fmtShort(renewIso, 'zh')} 到期——按此更新新週期`,
        },
      });
      continue;
    }
    if (lifecycle === 'default_dates') continue; // hygiene banner handles it
    // Earning deadline = CNE cycle end = renewal − 6 months (NCHK rule). Points
    // must be IN by then; the last 6 months to expiry are renewal-processing only.
    const earnEnd = cneWindow(lic.cycleStart || null, lic.renewalDate || null).end;
    const earnDays = earnEnd ? daysTo(earnEnd) : d;
    // 0 = first-PC exempt (no `|| 45` — that would fabricate a 45-point gap).
    const required = Number.isFinite(Number(lic.required)) ? Number(lic.required) : 45;
    // Cycle-scoped earned (CNE window) so the "still needed" gap matches the
    // home ring and the exported PDF — see cycleRecords().
    const earned = (Number(lic.baseline) || 0)
      + cycleRecords(records, lic.cycleStart, lic.renewalDate, now)
          .reduce((s, r) => s + (Number(r.points) || 0), 0);
    const gap = Math.max(0, required - earned);
    // Two triggers: the renewal countdown fires near PC expiry; the points-gap
    // nudge is keyed to the EARN-BY deadline (6 months earlier). Mandatory-only
    // "still needed" pressure — voluntary / first-PC-exempt cycles get a soft note.
    const showByDate = d <= 120 && d >= 0;
    const showByGap  = gap > 0 && earnDays <= 365 && earnDays >= 0;
    if (!(showByDate || showByGap)) continue;
    const gapDriven = showByGap && !showByDate;       // shown because of the earning gap
    const roleEn = showRenewalRole && lic.role ? ` · ${lic.role.en}` : '';
    const roleZh = showRenewalRole && lic.role ? ` · ${lic.role.zh}` : '';
    const gapSubEn = !lic.isMandatory
      ? `${gap} point${gap === 1 ? '' : 's'} to your voluntary target`
      : `${gap} point${gap === 1 ? '' : 's'} needed by ${fmtShort(earnEnd, 'en')}`;
    const gapSubZh = !lic.isMandatory
      ? `距自願目標仍差 ${gap} 分`
      : `${fmtShort(earnEnd, 'zh')} 前仍需 ${gap} 分`;
    out.push({
      id: 'auto-renew-' + lic.key,
      kind: 'renewal',
      date: gapDriven ? (earnEnd || renewIso) : renewIso,
      _days: gapDriven ? earnDays : d,
      title: {
        en: gapDriven
          ? ((earnDays <= 0 ? 'CNE deadline today' : `${earnDays} day${earnDays === 1 ? '' : 's'} to CNE deadline`) + roleEn)
          : ((d <= 0 ? 'Renewal due now' : `${d} day${d === 1 ? '' : 's'} to renewal`) + roleEn),
        zh: gapDriven
          ? ((earnDays <= 0 ? 'CNE 學分死線今日' : `距 CNE 學分死線 ${earnDays} 日`) + roleZh)
          : ((d <= 0 ? '需立即續期' : `距續期 ${d} 日`) + roleZh),
      },
      sub: {
        en: gap > 0 ? gapSubEn : 'You are on track',
        zh: gap > 0 ? gapSubZh : '進度達標',
      },
    });
  }

  // Sort: smaller _days first within a kind; renewal pinned to bottom unless urgent.
  out.sort((a, b) => {
    if (a.kind === 'renewal' && a._days > 14 && b.kind !== 'renewal') return  1;
    if (b.kind === 'renewal' && b._days > 14 && a.kind !== 'renewal') return -1;
    return (a._days ?? 999) - (b._days ?? 999);
  });

  return out;
}

window.deriveReminders = deriveReminders;

const TOPIC_TINT = {
  critical:    { bg: 'oklch(0.94 0.04 25)',  fg: 'oklch(0.42 0.10 25)' },
  palliative:  { bg: 'oklch(0.95 0.025 320)', fg: 'oklch(0.42 0.07 320)' },
  wound:       { bg: 'oklch(0.94 0.04 50)',  fg: 'oklch(0.42 0.10 50)' },
  mental:      { bg: 'oklch(0.94 0.04 270)', fg: 'oklch(0.42 0.10 270)' },
  infection:   { bg: 'oklch(0.93 0.03 175)', fg: 'oklch(0.40 0.07 175)' },
  paediatric:  { bg: 'oklch(0.94 0.04 130)', fg: 'oklch(0.40 0.10 145)' },
  geriatric:   { bg: 'oklch(0.94 0.025 80)', fg: 'oklch(0.40 0.06 80)' },
  leadership:  { bg: 'oklch(0.93 0.03 230)', fg: 'oklch(0.40 0.08 230)' },
  general:     { bg: 'oklch(0.94 0.02 90)',  fg: 'oklch(0.38 0.04 90)' },
  ethics:      { bg: 'oklch(0.94 0.02 90)',  fg: 'oklch(0.38 0.04 90)' },  // legacy
};

const STR = {
  home:{en:'Home',zh:'主頁'}, discover:{en:'Discover',zh:'探索'}, log:{en:'Log',zh:'記錄'},
  record:{en:'Record',zh:'記錄簿'}, saved:{en:'Saved',zh:'收藏'}, profile:{en:'Profile',zh:'個人'},
  goodMorning:{en:'Good morning',zh:'早晨'}, goodAfternoon:{en:'Good afternoon',zh:'午安'}, goodEvening:{en:'Good evening',zh:'晚安'},
  pointsWallet:{en:'CNE Wallet',zh:'CNE 積分'}, of:{en:'of',zh:'/'},
  goalMet:{en:'Goal met',zh:'已達標'},
  // renewBy removed 2026-08-17: the Profile row it labelled was replaced by the
  // 護士登記與執業證書 section (4a38f4d), whose rows are labelled by licence role.
  // Zero consumers remained in any .js/.jsx.
  daysToRenewal:{en:'days to renewal',zh:'日內續期'},
  upcoming:{en:'Upcoming',zh:'即將舉行'}, recommended:{en:'Recommended for you',zh:'為你推薦'},
  recentActivity:{en:'Recent activity',zh:'近期紀錄'}, viewAll:{en:'View all',zh:'查看全部'},
  core:{en:'Core',zh:'核心'}, elective:{en:'Elective',zh:'選修'}, specialty:{en:'Specialty',zh:'專科'},
  searchCourses:{en:'Search course name, code, topic…',zh:'搜尋課程名稱、課程編號、主題…'},
  all:{en:'All',zh:'全部'}, free:{en:'Free',zh:'免費'}, online:{en:'Online',zh:'網上'}, inPerson:{en:'In person',zh:'實體'},
  workshop:{en:'Workshop',zh:'工作坊'}, lecture:{en:'Lecture',zh:'講座'}, elearning:{en:'Course',zh:'課程'},
  saveCourse:{en:'Save',zh:'收藏'}, register:{en:'Register',zh:'報名'}, enrolled:{en:'Enrolled',zh:'已報名'},
  full:{en:'Full',zh:'已滿'},
  spotsLeft:{en:'spots left',zh:'名額剩餘'}, whenWhere:{en:'When & where',zh:'時間及地點'},
  about:{en:'About this course',zh:'課程簡介'}, provider:{en:'Provider',zh:'主辦'},
  format:{en:'Format',zh:'形式'}, language:{en:'Language',zh:'語言'}, cost:{en:'Cost',zh:'費用'},
  feeTbc:{en:'To be confirmed',zh:'費用待確認'}, feeTbcHint:{en:'Contact the organiser',zh:'請向主辦機構查詢'},
  languageTbc:{en:'To be confirmed',zh:'語言待確認'},
  points:{en:'CNE points',zh:'CNE 分'}, pts:{en:'pts',zh:'分'},
  myRecord:{en:'My Record',zh:'我的紀錄'}, exportPdf:{en:'Export PDF',zh:'匯出 PDF'},
  nchkExportTitle:{en:'Export CNE record',zh:'匯出持續進修記錄'},
  nchkModeFilled:{en:'Official record',zh:'正式記錄'},
  nchkModeFilledHint:{en:'NCHK Annex A + declaration, pre-filled — review before export',zh:'護士管理局附件 A ＋聲明，已預先填寫，匯出前可檢視'},
  nchkModeBlank:{en:'Blank template',zh:'空白表格'},
  nchkModeBlankHint:{en:'Empty form to print and fill in by hand',zh:'列印後手寫填寫的空白表格'},
  nchkDetailsTitle:{en:'Your details',zh:'你的資料'},
  nchkDetailsHint:{en:'Needed for the declaration. Saved to your profile for next time.',zh:'聲明所需資料，會儲存至個人檔案以供下次使用。'},
  nchkRegNo:{en:'Registration / Enrolment No.',zh:'註冊／登記編號'},
  nchkNameEn:{en:'Name (English)',zh:'姓名（英文）'},
  nchkNameZh:{en:'Name (Chinese)',zh:'姓名（中文）'},
  nchkContinue:{en:'Continue',zh:'繼續'},
  nchkPreviewTitle:{en:'Review before export',zh:'匯出前檢視'},
  nchkPreviewHint:{en:'Edits here apply to the PDF only — your saved records are unchanged.',zh:'此處的修改只影響 PDF，不會更改已儲存的紀錄。'},
  nchkPeriodTitle:{en:'Declaration period & details',zh:'聲明期間及資料'},
  nchkAddRow:{en:'Add row',zh:'新增一列'},
  nchkRemoveRow:{en:'Remove',zh:'刪除'},
  nchkGenerate:{en:'Generate PDF',zh:'產生 PDF'},
  nchkColNature:{en:'Nature',zh:'活動性質'},
  nchkColActivity:{en:'Activity',zh:'活動名稱'},
  nchkColFrom:{en:'From',zh:'由'},
  nchkColTo:{en:'To',zh:'至'},
  nchkColPoints:{en:'Pts',zh:'學分'},
  nchkColOrg:{en:'Provider',zh:'舉辦機構'},
  nchkPcValid:{en:'Practising Cert. valid',zh:'執業證明書有效期'},
  nchkCnePeriod:{en:'CNE declaration period',zh:'持續進修聲明期間'},
  nchkEmptyRecords:{en:'No records in this cycle to export',zh:'本週期暫無紀錄可匯出'},
  cert:{en:'Certificate',zh:'證書'}, uploadCert:{en:'Upload certificate',zh:'上傳證書'},
  logCourse:{en:'Log a course',zh:'記錄課程'},
  fromCatalog:{en:'From catalogue',zh:'從目錄選取'}, manualEntry:{en:'Enter manually',zh:'手動輸入'},
  // ── AI document scan ─────────────────────────────────────────────────
  // ‼️ NAMING: this entry is deliberately INTENT-NEUTRAL. It was called
  // 「AI 掃描證書」, which described only one of the three inputs it accepts and
  // implied one intent (log something already finished) — nurses never found
  // the link/poster path, and the one thing they most want to capture is the
  // course they JUST ENROLLED IN. Keep the name about the capability, and let
  // the extracted dates decide whether the result is 進行中 or 已完成.
  aiScan:{en:'AI scan',zh:'AI 智能識別'},
  aiScanSub:{en:'Certificate, course poster or link — AI fills in the form',zh:'證書、課程海報或網址,AI 自動幫你填寫'},
  aiScanKinds:{en:'Certificate · Poster/PDF · Course link',zh:'證書 · 海報/PDF · 課程網址'},
  aiScanBoth:{en:'Works for courses you’ve finished and ones you’ve just enrolled in',zh:'已完成或啱啱報咗名嘅課程都記得到'},
  aiScanFill:{en:'Auto-fill from this file',zh:'用 AI 讀取檔案填寫'},
  aiScanning:{en:'Analysing course details…',zh:'正在分析課程資料…'},
  aiScanWait:{en:'Usually takes 10–30 seconds',zh:'一般需時 10 至 30 秒'},
  aiScanLeft:{en:'AI scans left',zh:'次 AI 掃描剩餘'},
  aiScanVerify:{en:'AI filled this in — check the title, category, dates and points before saving.',zh:'AI 已自動填入資料,儲存前請核對名稱、類別、日期及分數。'},
  aiScanQuotaOut:{en:'You’ve used all your AI scans for this year. Submit or correct a course — each approved contribution earns +1 scan.',zh:'今年的 AI 掃描次數已用完。向社群提交或更正課程,每宗獲批可獲 +1 次。'},
  aiScanFailed:{en:'Couldn’t read that. Please fill in the details manually.',zh:'未能讀取,請手動填寫資料。'},
  aiScanNotCert:{en:'This doesn’t look like a CNE certificate or course page — please check the details carefully.',zh:'呢個唔似係 CNE 證書或課程資料,請小心核對。'},
  // doc_type='qualification' — a practising certificate, degree or competency
  // authorisation. In the WALLET flow that is the right document and no toast
  // fires; this fires only in the CNE log flow, where it earns no points and
  // belongs on the other shelf.
  aiScanCredential:{en:'This looks like a professional credential (for example a practising certificate) rather than a CNE course certificate. It has been set to save under My qualifications — it earns no CNE points and is not shared to the community.',zh:'呢份似乎係專業資格證明(例如執業證書),唔係 CNE 課程證書。已改為儲存到「我的資格」—— 唔計 CNE 分,亦唔會提交到社群。'},
  aiScanPrivacy:{en:'The file or link is sent to Google AI for analysis — see the privacy policy.',zh:'檔案或連結將傳送至 Google AI 服務作分析,詳見私隱政策。'},
  aiScanUrl:{en:'Paste a course link',zh:'貼上課程連結'},
  aiScanUrlAsk:{en:'Paste the course page link (https://…)',zh:'貼上課程網頁連結 (https://…)'},
  // Announcement outcomes. Split by date, because the two cases mean opposite
  // things to a nurse: a FUTURE course is the enrolled-course capture we now
  // explicitly support (so state where it lands, don't apologise for it), while
  // a past-dated poster really is a weaker source than a certificate.
  aiScanPoster:{en:'This looks like a course announcement rather than a certificate — we’ve ticked “Submit to community” for you. Review before saving.',zh:'呢個似乎係課程海報/網頁而唔係證書 — 已為你剔選「提交到社群」,儲存前請核對。'},
  aiScanPosterPast:{en:'This looks like a course announcement for a course that has already ended. Your record is saved as usual; ended courses are not shared to the community catalogue.',zh:'呢個似乎係課程海報,而課程已經完結。你嘅記錄照常儲存;已完結嘅課程唔會提交到社群課程庫。'},
  aiScanUpcoming:{en:'Course details filled in. This one hasn’t finished yet, so it’ll be saved under “In progress” — upload the certificate when you’re done.',zh:'已填好課程資料。呢個課程未完成,會存入「進行中」,完成後再上傳證書。'},
  aiScanUrlFailed:{en:'Couldn’t read that link. Check the address or try a screenshot instead.',zh:'未能讀取呢條連結,請檢查網址,或改用截圖。'},
  aiScanFreeCapped:{en:'You’ve hit today’s limit for contribution scans — thank you for contributing. Please continue tomorrow.',zh:'今日的課程提交掃描已達上限。多謝你的貢獻,請明天再繼續。'},
  // ── Destination + points-pending (enrolled-course capture) ───────────
  destInProgress:{en:'Will appear in “In progress”',zh:'將加入「進行中」'},
  destCompleted:{en:'Will appear in “Completed”',zh:'將加入「已完成」'},
  pointsTbdHint:{en:'Not published yet? Leave blank and add it when you finish.',zh:'分數未公布可留空,完成後再補回。'},
  pointsTbdChip:{en:'Points to confirm',zh:'學分待補'},
  pointsTbdWhy:{en:'This record counts 0 points until you add them.',zh:'未填分數前,此紀錄以 0 分計算。'},
  // ── Continue-logging menu (shown when this year's free AI scans run out) ──
  //
  // ‼️ THIS DIALOG IS ABOUT FINISHING THE JOB, NOT ABOUT BUYING CREDITS. It was
  // titled 「賺取 AI 掃描次數」 over three earn rows and a dismiss — every option
  // was a way to ACQUIRE SCANS, so a nurse backfilling a year of certificates
  // read the wall as "Vital won't let me enter any more courses" and wrote in
  // asking whether records were capped. They are not, and never have been:
  // 「無限 CNE 紀錄與證書儲存」 is the free tier's stated promise (paywall.jsx).
  // Manual entry is the free, unlimited answer and MUST stay the FIRST row —
  // the nurse's job is "get 2025 into Vital", and an AI scan is only one way to
  // do it. Monetization comes after the job, never instead of it.
  //
  // ‼️ EVERY quota string here names WHAT is metered. A bare 「次數已用完」 is
  // exactly the ambiguity that caused the misread: she had been "輸入 CNE" all
  // afternoon, so an unqualified "次數" reads as the number of RECORDS.
  aiEarnTitle:{en:'Keep adding records',zh:'繼續加入 CNE 紀錄'},
  aiEarnSub:{en:'You’ve used this year’s free AI certificate scans. Your CNE records and certificate storage stay unlimited.',zh:'今年的免費 AI 證書掃描次數已用完。你的 CNE 紀錄及證書儲存仍然不限數量。'},
  // Qualification-wallet variant — same wall, but "CNE 紀錄" is the wrong noun
  // when the sheet was opened to file a practising certificate or a BLS card.
  aiEarnSubQual:{en:'You’ve used this year’s free AI certificate scans. Your qualification storage stays unlimited.',zh:'今年的免費 AI 證書掃描次數已用完。你的資格證明儲存仍然不限數量。'},
  aiEarnManual:{en:'Enter it manually',zh:'手動輸入'},
  aiEarnManualSub:{en:'Free · unlimited · no scan needed',zh:'免費 · 不限次數 · 無需掃描'},
  aiEarnMore:{en:'Or earn more AI scans',zh:'或賺取更多 AI 掃描次數'},
  aiEarnPro:{en:'Vital Pro',zh:'Vital Pro'},
  aiEarnProSub:{en:'Unlimited AI certificate scanning',zh:'AI 證書識別不限次數'},
  aiEarnSubmit:{en:'Submit a course',zh:'提交課程'},
  aiEarnSubmitSub:{en:'+1 credit when it’s approved',zh:'獲批後 +1 次'},
  aiEarnCorrect:{en:'Correct course info',zh:'更正課程資料'},
  aiEarnCorrectSub:{en:'+1 credit when your fix is applied',zh:'更正獲採納後 +1 次'},
  aiEarnShare:{en:'Share Vital with a friend',zh:'推薦 Vital 給朋友'},
  aiEarnShareSub:{en:'+1 credit a month',zh:'每月 +1 次'},
  aiEarnInvite:{en:'Invite a colleague',zh:'邀請同事'},
  aiEarnInviteSub:{en:'+2 each when they join with your code',zh:'朋友用你嘅邀請碼註冊,你哋各 +2 次'},
  aiEarnLater:{en:'Maybe later',zh:'稍後再說'},
  aiEarnCorrectHint:{en:'Open any course and tap ✎ next to a field to suggest a fix.',zh:'開啟任何課程,㩒欄位旁嘅 ✎ 即可提出更正。'},

  // ── Referrals + ward groups (病房裂變引擎) ──────────────────────────────
  // Invite code (Profile → 我的邀請碼 dialog + onboarding field)
  inviteCode:{en:'Invite code',zh:'邀請碼'},
  inviteCodeMine:{en:'My invite code',zh:'我的邀請碼'},
  inviteCodeSub:{en:'Friends who sign up with your code earn you both +2 AI scans',zh:'朋友用你嘅邀請碼註冊,你同佢各得 +2 次 AI 掃描'},
  inviteCodeCap:{en:'Up to 5 invited colleagues a month',zh:'每月最多 5 位受邀同事'},
  inviteCodeOptional:{en:'Invite code (optional)',zh:'邀請碼(可選)'},
  inviteCodeHint:{en:'Have a colleague’s code? Enter it to get +2 AI scans.',zh:'有同事嘅邀請碼?輸入即可獲 +2 次 AI 掃描。'},
  inviteShareText:{en:'I’m using Vital to track my CNE points and find courses. Sign up with my invite code and we both get +2 free AI certificate scans:',zh:'我用緊 Vital 記 CNE 學分同搵課程,用我嘅邀請碼註冊,我哋各得 +2 次免費 AI 證書掃描:'},
  copy:{en:'Copy',zh:'複製'}, copied:{en:'Copied ✓',zh:'已複製 ✓'},
  referralGot:{en:'You got +2 AI scans 🎉',zh:'已獲 +2 次 AI 掃描 🎉'},
  referralBadCode:{en:'That invite code isn’t valid.',zh:'呢個邀請碼無效。'},
  referralOwnCode:{en:'That’s your own code — share it with a colleague instead.',zh:'呢個係你自己嘅邀請碼 — 分享畀同事啦。'},
  // Ward groups / leaderboard
  wardGroups:{en:'Ward leaderboard',zh:'病房排行榜'},
  wardGroupsSub:{en:'Track CNE progress together with your ward',zh:'同同事一齊追 CNE 進度'},
  wardGroupsEmpty:{en:'Create a ward group or join one with an invite code — see who’s on track and cheer each other on to renewal.',zh:'建立一個病房小組,或用邀請碼加入 — 睇下邊個夠分,一齊追到 renewal。'},
  wardCreate:{en:'Create a group',zh:'建立小組'},
  wardJoin:{en:'Join with a code',zh:'輸入邀請碼加入'},
  wardGroupName:{en:'Group name',zh:'小組名稱'},
  wardGroupNamePh:{en:'e.g. 3A Medical / ICU night',zh:'例:3A 內科 / ICU 夜更'},
  wardInviteCodePh:{en:'8-character invite code',zh:'8 位邀請碼'},
  wardShareInvite:{en:'Invite colleagues',zh:'邀請同事'},
  wardShareText:{en:'Join our ward’s CNE leaderboard on Vital — track your points together so nobody misses renewal:',zh:'一齊加入我哋病房喺 Vital 嘅 CNE 排行榜,互相追進度,唔會漏交分:'},
  wardLeave:{en:'Leave group',zh:'離開小組'},
  wardDelete:{en:'Delete group',zh:'解散小組'},
  wardRemoveMember:{en:'Remove member',zh:'移除成員'},
  wardMembers:{en:'members',zh:'位成員'},
  wardGoalMet:{en:'Goal met',zh:'已達標'},
  wardExempt:{en:'Exempt',zh:'豁免'},
  wardNoCycle:{en:'No cycle set',zh:'未設定週期'},
  wardOwner:{en:'Owner',zh:'組長'},
  wardYou:{en:'You',zh:'你'},
  // Handle gate + cap + errors
  wardHandleNeeded:{en:'Pick a display name to join a ward group — your colleagues see this, not your real name.',zh:'加入病房小組前,先改一個社群顯示名 — 同事只會見到呢個,唔係你真名。'},
  wardCapHit:{en:'You’ve hit this month’s invite limit (5/5). It resets next month.',zh:'本月已達邀請上限 5/5,下月重置。'},
  wardOwnerMustDelete:{en:'You’re the owner — delete the group instead of leaving.',zh:'你係組長 — 請解散小組,而唔係離開。'},
  wardFull:{en:'This group is full.',zh:'呢個小組已滿。'},
  wardNotFound:{en:'Group not found — check the code.',zh:'搵唔到小組,請檢查邀請碼。'},
  // Peer nudge (提一提)
  wardNudge:{en:'Nudge',zh:'提一提'},
  wardNudged:{en:'Nudged',zh:'已提'},
  wardNudgeSent:{en:'Nudge sent 👍',zh:'已提你嘅同事 👍'},
  wardNudgeDone:{en:'You already nudged this colleague today.',zh:'你今日已經提過呢位同事喇。'},
  wardNudgeFail:{en:'Could not send the nudge — please try again.',zh:'未能傳送,請再試。'},
  // Goal-met wallet share
  goalShareChip:{en:'Goal met — share it',zh:'已達標 — 同同事分享'},
  goalShareText:{en:'I’ve completed the CNE points I need for this cycle on Vital ✅ Track yours too:',zh:'我喺 Vital 完成咗今個 CNE 週期需要嘅學分喇 ✅ 你都嚟記低你嘅進度啦:'},
  courseTitle:{en:'Course title',zh:'課程名稱'}, date:{en:'Date',zh:'日期'},
  startDate:{en:'Start date',zh:'開始日期'}, endDate:{en:'End date',zh:'結束日期'},
  category:{en:'Category',zh:'類別'}, optional:{en:'optional',zh:'可選'}, saveRecord:{en:'Save to record',zh:'加入紀錄'},
  account:{en:'Account',zh:'帳戶'}, preferences:{en:'Preferences',zh:'偏好設定'},
  appLanguage:{en:'App language',zh:'應用程式語言'}, notifications:{en:'Notifications',zh:'通知'},
  signOut:{en:'Sign out',zh:'登出'}, licenseNo:{en:'License no.',zh:'執業編號'},
  cycleEnds:{en:'Cycle ends',zh:'週期結束'},

  // ── Feedback (Profile → Leave a comment or suggestion) ──────────────
  feedbackSection:{en:'Feedback',zh:'意見與建議'},
  feedbackRowLabel:{en:'Leave a comment or suggestion',zh:'留下意見或建議'},
  feedbackRowSub:{en:'Help us improve Vital',zh:'協助我們改善 Vital'},
  // Review triage (ReviewAskSheet). The positive action is an explicit,
  // user-initiated link to Vital's App Store review page; the other action
  // opens the private feedback loop the team can answer.
  reviewAskTitle:{en:'How is Vital working for you?',zh:'覺得 Vital 好用嗎？'},
  reviewAskBody:{en:'Your experience matters to us — either answer helps.',zh:'你的感受對我們很重要 — 兩個答案都幫到我們。'},
  reviewAskFootnote:{en:'Feedback goes straight to the team.',zh:'意見會直接送達開發團隊。'},
  reviewAskPositive:{en:'It’s working well 👍',zh:'好用 👍'},
  reviewAskFeedback:{en:'I have some feedback',zh:'有意見想反映'},
  rateAppRowLabel:{en:'Rate Vital on the App Store',zh:'去 App Store 給 Vital 評分'},
  rateAppRowSub:{en:'A rating helps other nurses find Vital',zh:'你的評分讓更多護士認識 Vital'},
  feedbackTitle:{en:'Share your thoughts',zh:'分享你的意見'},
  feedbackIntro:{en:'Suggestions, bugs, or anything on your mind — we read every message.',zh:'建議、問題,或任何想法 — 我們會細閱每則訊息。'},
  feedbackCategory:{en:'Type',zh:'類別'},
  feedbackCatSuggestion:{en:'Suggestion',zh:'建議'},
  feedbackCatBug:{en:'Something is broken',zh:'發現問題'},
  feedbackCatOther:{en:'Other',zh:'其他'},
  feedbackMessage:{en:'Your message',zh:'你的訊息'},
  feedbackMessagePlaceholder:{en:'Tell us what you think…',zh:'告訴我們你的想法…'},
  feedbackEmail:{en:'Contact email',zh:'聯絡電郵'},
  feedbackEmailOptional:{en:'optional',zh:'可選'},
  feedbackEmailHint:{en:'Only if you’d like a reply.',zh:'如希望收到回覆才需填寫。'},
  feedbackSubmit:{en:'Send feedback',zh:'送出意見'},
  feedbackSending:{en:'Sending…',zh:'傳送中…'},
  feedbackThanks:{en:'Thank you — we’ve received your feedback',zh:'多謝你的意見,我們已收到'},
  feedbackError:{en:'Could not send. Please try again.',zh:'傳送失敗,請再試一次。'},

  // ── Profile / onboarding fields ─────────────────────────────────────
  displayName:{en:'Display name',zh:'顯示名稱'},
  displayNameHint:{en:'How your name appears in the app.',zh:'你的名稱在應用程式內的顯示方式。'},
  sex:{en:'Sex',zh:'性別'},
  sexMale:{en:'Male',zh:'男'}, sexFemale:{en:'Female',zh:'女'},
  sexPreferNot:{en:'Prefer not to say',zh:'不願透露'},
  referralSource:{en:'Where did you find us?',zh:'你從何得知我們?'},
  refAppStore:{en:'App Store',zh:'App Store'},
  refColleague:{en:'Colleague / friend',zh:'同事 / 朋友'},
  refSearch:{en:'Search engine',zh:'搜尋引擎'},
  refSocial:{en:'Social media',zh:'社交媒體'},
  refInstagram:{en:'Instagram',zh:'Instagram'},
  refNchk:{en:'NCHK / hospital',zh:'護士管理局 / 醫院'},
  refOther:{en:'Other',zh:'其他'},
  hospitalSector:{en:'Workplace',zh:'工作單位'},
  sectorPublic:{en:'Public',zh:'公立'}, sectorPrivate:{en:'Private',zh:'私立'},
  selectHospital:{en:'Select hospital',zh:'選擇醫院'},
  hospital:{en:'Hospital',zh:'醫院'},
  back:{en:'Back',zh:'返回'}, save:{en:'Save',zh:'儲存'},
  darkMode:{en:'Dark mode',zh:'深色模式'}, appearance:{en:'Appearance',zh:'外觀'},
  light:{en:'Light',zh:'淺色'}, dark:{en:'Dark',zh:'深色'}, auto:{en:'Auto',zh:'自動'},
  sessions:{en:'sessions',zh:'節'},
  memberFee:{en:'Member',zh:'會員'}, nonMemberFee:{en:'Non-member',zh:'非會員'}, earlyBird:{en:'Early bird',zh:'早鳥'},
  speakers:{en:'Speakers',zh:'講者'}, deadline:{en:'Apply by',zh:'報名截止'},
  courseCode:{en:'Course code',zh:'課程編號'},

  // ── Discover filter sheet ───────────────────────────────────────────
  filters:{en:'Filters',zh:'篩選'}, filtersReset:{en:'Reset',zh:'重設'}, filtersApply:{en:'Show results',zh:'顯示結果'},
  filterProvider:{en:'Provider',zh:'主辦機構'}, filterSpecialty:{en:'Specialty',zh:'專科'},
  filterLanguage:{en:'Language',zh:'授課語言'}, filterMode:{en:'Teaching mode',zh:'授課形式'},
  filterEventType:{en:'Event type',zh:'類型'}, filterAudience:{en:'For',zh:'對象'},
  filterEnrollment:{en:'Enrolment',zh:'報名狀態'}, filterDeadlineSoon:{en:'Deadline soon',zh:'即將截止'},
  filterCneStatus:{en:'CNE points status',zh:'CNE 分狀態'},
  filterPoints:{en:'CNE points',zh:'CNE 分'}, filterFee:{en:'Fee (HKD)',zh:'費用（港幣）'},
  filterClearAll:{en:'Clear all',zh:'清除全部'}, activeFilters:{en:'filter applied',zh:'個篩選'},

  // ── Capacity / enrolment status ─────────────────────────────────────
  open:{en:'Open',zh:'可報名'}, closed:{en:'Closed',zh:'已截止'},
  enrollmentEnded:{en:'Enrolment ended',zh:'報名已結束'},
  openingSoon:{en:'Opening soon',zh:'即將開放'},
  statusUnverified:{en:'To be confirmed',zh:'待確認'},
  waitlist:{en:'Waitlist',zh:'候補'},
  cancelledStatus:{en:'Cancelled',zh:'已取消'},
  ongoingStatus:{en:'Ongoing',zh:'進行中'},
  dateTbc:{en:'Date TBC',zh:'日期待定'},
  tabCurrent:{en:'Current',zh:'現行'},
  tabEnded:{en:'Ended',zh:'已結束'},
  // ‼️ NAMED FOR WHAT IT REMOVES, NOT FOR WHAT IT CLAIMS TO KEEP. This read
  // 「只顯示可報名」 while the pool it keeps is open + waitlist + unknown — so a
  // course whose availability the provider never published was being presented
  // as 可報名 by the toggle's own label. The pool is right and unchanged (hiding
  // the unknowns emptied the list, which is why they were folded in); it was
  // only ever the wording that over-claimed.
  openOnly:{en:'Hide full & closed',zh:'隱藏已滿／已截止'},
  notPublished:{en:'Not published',zh:'未公佈'},
  cneConfirmed:{en:'CNE confirmed',zh:'CNE 已確認'},
  cneUnverifiedShort:{en:'Points unverified',zh:'分數待確認'},
  cneUnverifiedNote:{en:'CNE points have not been confirmed by the organiser',zh:'CNE 分數尚未經主辦方確認'},
  sessionsUnit:{en:'sessions',zh:'節'},
  updatedShort:{en:'Updated',zh:'更新'},
  catalogueUpdated:{en:'Data updated',zh:'資料更新於'},
  discoverEyebrow:{en:'Hong Kong CNE opportunities',zh:'香港 CNE 進修機會'},
  unlimited:{en:'Unlimited',zh:'不限名額'},
  seatsRemaining:{en:'seats remaining',zh:'剩餘名額'},
  registerBy:{en:'Register by',zh:'報名截止'},
  daysLeft:{en:'days left',zh:'天後截止'}, dayLeft:{en:'day left',zh:'天後截止'},
  closesToday:{en:'Closes today',zh:'今日截止'},

  // ── Providers index ─────────────────────────────────────────────────
  providers:{en:'Providers',zh:'機構'},
  providersHeader:{en:'NCHK-accredited providers',zh:'NCHK 認可機構'},
  providersCount:{en:'providers',zh:'間機構'},
  coursesCount:{en:'courses',zh:'個課程'},
  byProvider:{en:'By provider',zh:'按機構瀏覽'},
  viewCourses:{en:'View courses',zh:'查看課程'},
  noCoursesFromProvider:{en:'No active courses from this provider right now.',zh:'此機構目前沒有可報課程。'},

  // ── Notification Settings page ──────────────────────────────────────
  // v1 scope: Section 1 toggles control push only; email cells are stored
  // but UI-inert. See db.js getNotificationPrefs header comment.
  notifSettingsTitle:{en:'Notification settings',zh:'通知設定'},
  notifManage:{en:'Manage',zh:'管理'},
  notifSignInToEnable:{en:'Sign in to enable',zh:'登入後啟用'},
  notifTypesSection:{en:'Notification types',zh:'通知類別'},
  notifChannelsSection:{en:'Delivery channels',zh:'傳送方式'},
  notifMasterSection:{en:'Push notifications',zh:'推送通知'},
  // Pre-permission soft-ask (offered once, after a value moment)
  pushAskTitle:{en:'Turn on notifications?',zh:'開啟推送通知?'},
  pushAskBodySave:{
    en:'Saved! Turn on notifications and we’ll remind you before registration closes and before each class.',
    zh:'已收藏!開啟通知後,我們會在報名截止前及上課前提醒你。',
  },
  pushAskBodyRecord:{
    en:'Record saved! Turn on notifications and we’ll remind you about renewal deadlines and any missing records.',
    zh:'紀錄已儲存!開啟通知後,我們會提醒你續牌期限及缺漏的紀錄。',
  },
  pushAskFootnote:{
    en:'You can change this anytime in Profile → Notification settings.',
    zh:'你可隨時在「個人 → 通知設定」更改。',
  },
  pushAskEnable:{en:'Turn on notifications',zh:'開啟通知'},
  pushAskLater:{en:'Not now',zh:'暫不'},
  notifMasterHint:{
    en:'These settings take effect after you turn on push notifications.',
    zh:'開啟推送通知後,以下設定才會生效。',
  },
  notifPushOnlyCaption:{
    en:'Currently controls push notifications. Email delivery is coming soon.',
    zh:'目前僅控制推送通知。電郵通知即將推出。',
  },
  // Categories
  notifCatRenewalTitle:{en:'Licence renewal deadline reminders',zh:'續牌期限提醒'},
  notifCatRenewalDesc:{
    en:'Remind you before renewal deadlines so you have time to complete credits and prepare documents.',
    zh:'在續牌期限前提醒你，讓你有時間完成學分及準備文件。',
  },
  notifCatMissingTitle:{en:'Missing certificate or record reminders',zh:'證書 / 紀錄缺漏提醒'},
  notifCatMissingDesc:{
    en:'Notify you when your records may be incomplete.',
    zh:'當你的紀錄可能不完整時通知你。',
  },
  // Covers both saved-course sweeps (registration deadline + seat state), so the
  // copy names both rather than promising only deadlines.
  notifCatSavedDeadlineTitle:{en:'Saved course alerts',zh:'已儲存課程提醒'},
  notifCatSavedDeadlineDesc:{
    en:'Notify you when a saved course is approaching its registration deadline, or its remaining places run low.',
    zh:'當已儲存的課程接近報名截止,或名額所餘無幾時通知你。',
  },
  notifCatRegulatoryTitle:{en:'Regulatory updates',zh:'法規更新'},
  notifCatRegulatoryDesc:{
    en:'Notify you when the Nursing Council changes the CNE rules that affect your renewal.',
    zh:'當護士管理局更改影響你續證的 CNE 規例時通知你。',
  },
  notifCatCertExpiryTitle:{en:'Qualification expiry reminders',zh:'資格到期提醒'},
  notifCatCertExpiryDesc:{
    en:'Notify you 90, 30 and 7 days before a qualification expires (e.g. a BLS or ACLS card).',
    zh:'在資格到期前 90、30 及 7 日通知你（例如 BLS、ACLS 證書）。',
  },
  notifCatCommunityTitle:{en:'My submissions & reports',zh:'我的提交與回報'},
  notifCatCommunityDesc:{
    en:'Notify you when a course you submitted or a correction you reported is reviewed.',
    zh:'當你提交的課程或回報的修正被審核時通知你。',
  },
  notifCatNudgeTitle:{en:'Ward nudges',zh:'病房同事提醒'},
  notifCatNudgeDesc:{
    en:'When a colleague in your ward group nudges you about your CNE progress.',
    zh:'當你病房小組嘅同事「提你一提」CNE 進度時通知你。',
  },
  notifCatAccountTitle:{en:'Important account events',zh:'重要帳戶事件'},
  notifCatAccountDesc:{
    en:'Sign-in, email change, and security-related notifications.',
    zh:'登入、電郵更改及保安相關通知。',
  },
  notifCatAccountAlwaysOn:{
    en:'Always on for account security.',
    zh:'為帳戶安全而保持開啟。',
  },
  // Channels
  notifChannelEmail:{en:'Email',zh:'電郵'},
  notifChannelEmailDesc:{
    en:'Email delivery is not yet available.',
    zh:'電郵通知功能尚未推出。',
  },
  notifComingSoon:{en:'Coming soon',zh:'即將推出'},
  notifChannelPush:{en:'Push notifications',zh:'推送通知'},
  // Push permission state labels (lifted out of RemindersSection so all
  // notification copy lives in one place)
  notifPushStateOff:{en:'Off',zh:'未開啟'},
  notifPushStateBlocked:{en:'Blocked in Settings',zh:'已封鎖'},
  notifPushStateOn:{en:'On',zh:'已開啟'},
  notifPushDevice:{en:'device',zh:'部裝置'},
  notifPushDevices:{en:'devices',zh:'部裝置'},
  notifPushDeniedHint:{
    en:'Notifications are blocked. Open iOS Settings → Vital → Notifications to enable.',
    zh:'通知已在系統設定中關閉。請至「設定 → Vital → 通知」開啟。',
  },
  notifPushRegistering:{en:'Registering…',zh:'註冊中…'},
  notifPushTimeoutHint:{
    en:'APNs did not return a token. Verify Push Notifications capability in Xcode and that the App ID has Push enabled in the Apple Developer Portal.',
    zh:'APNs 沒有回覆 token。請在 Xcode 確認 Push Notifications capability,以及 Apple Developer Portal 已啟用此 App ID 的 Push。',
  },
  notifPushCannotEnable:{en:'Could not enable',zh:'無法啟用'},
  notifPushCannotDisable:{en:'Could not disable',zh:'無法停用'},
  // Notification center
  notifCenterTitle:{en:'Notifications',zh:'通知記錄'},
  notifCenterSub:{en:'See past reminders and updates',zh:'查看過往提醒及更新'},
  notifCenterEmpty:{en:'No notifications yet',zh:'暫時沒有通知'},
  notifCenterEmptySub:{en:'Reminders and updates will show up here.',zh:'提醒及更新會在這裡顯示。'},
  notifTimeToday:{en:'Today',zh:'今日'},
  notifTimeYesterday:{en:'Yesterday',zh:'昨日'},
  notifMarkAllRead:{en:'Mark all read',zh:'全部標為已讀'},
  notifDelete:{en:'Delete',zh:'刪除'},
  // Delivery mode (scheduled reminders)
  notifDeliverySection:{en:'How reminders arrive',zh:'提醒推送方式'},
  notifDeliveryInstant:{en:'Each reminder',zh:'逐則推送'},
  notifDeliveryInstantDesc:{en:'Get a push for each reminder as it comes up.',zh:'每則提醒即時推送。'},
  notifDeliveryDigest:{en:'Daily summary',zh:'每日摘要'},
  notifDeliveryDigestDesc:{
    en:'One push a day that bundles the day’s reminders. Nothing is dropped — every item still appears in Notifications.',
    zh:'每日一則,整合當天的提醒。內容不會減少,每筆仍保留在通知記錄。',
  },
  notifDeliveryOff:{en:'Off',zh:'關閉推送'},
  notifDeliveryOffDesc:{
    en:'No reminder pushes — they’re still recorded in Notifications.',
    zh:'不推送提醒,但仍會記錄在通知記錄。',
  },
  // Class reminder frequency
  notifClassModeTitle:{en:'Class reminder frequency',zh:'課堂提醒頻率'},
  notifClassModeFull:{en:'Every session',zh:'每堂提醒'},
  notifClassModeConcise:{en:'Lighter',zh:'精簡'},
  notifClassModeConciseDesc:{en:'Only the first session and any class tomorrow.',zh:'只提醒首堂及明日有課。'},
};

function L(o, lang){ if(o==null) return ''; if(typeof o==='string') return o; return o[lang] ?? o.en ?? ''; }
// Normalise ANY date-ish input to a bare 'YYYY-MM-DD', or '' when there isn't
// one. The formatters below build `new Date(x + 'T00:00:00')` to force local
// (not UTC) parsing — but that only works on a bare date. Postgres timestamptz
// columns arrive as '2026-07-16T10:39:25.686539+00:00', and appending
// 'T00:00:00' to THAT yields an Invalid Date, which rendered on the guest home
// screen as the literal text "updated undefined NaN, NaN".
//
// Columns are genuinely mixed — cne_courses.last_updated is a date, updated_at
// is a timestamptz, and callers fall back from one to the other — so the fix
// belongs here, at the shared helper, not at each call site.
function _dateOnly(v){
  const m = String(v == null ? '' : v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
function fmtDate(iso, lang){ const s=_dateOnly(iso); if(!s) return ''; const d=new Date(s+'T00:00:00'); if(lang==='zh') return `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日`; const m=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]; return `${m} ${d.getDate()}, ${d.getFullYear()}`; }
// withYear=true adds the year (e.g. past/expired courses, where "8月6日" alone
// is ambiguous — which year did it end?). Default stays year-less for the common
// upcoming-course case. Keeps the "Date TBC" fallback for dateless rows.
function fmtShort(iso, lang, withYear){ const _s=_dateOnly(iso); if(!_s) return lang==='zh'?'日期待定':'Date TBC'; const d=new Date(_s+'T00:00:00'); if(lang==='zh') return withYear ? `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日` : `${d.getMonth()+1}月${d.getDate()}日`; const m=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]; return withYear ? `${m} ${d.getDate()}, ${d.getFullYear()}` : `${m} ${d.getDate()}`; }
function daysBetween(a,b){ const x=_dateOnly(a), y=_dateOnly(b); if(!x||!y) return NaN; return Math.round((new Date(y+'T00:00:00')-new Date(x+'T00:00:00'))/86400000); }

// ─────────────────────────────────────────────────────────────────────────────
// NCHK practising-certificate ↔ CNE-cycle date math
// ─────────────────────────────────────────────────────────────────────────────
// A practising certificate (PC) is valid for a 3-year block, generally Jan 1
// (year 1) → Dec 31 (year 3). The CNE *earning* cycle is fixed by the NCHK letter
// "Commencement of the first Mandatory CNE Cycle" (30 Jun 2026): each cycle begins
// 3.5 YEARS before the NEXT certificate's effective date and runs 3 years. Since
// next-effective = current-expiry + 1 day, the cycle is anchored on the EXPIRY:
// it ends 6 months before the PC expires and starts 3.5 years before renewal.
// e.g. PC (2027-01-01 … 2029-12-31) → CNE window (2026-07-01 … 2029-06-30).
// (The old code shifted BOTH ends by −2 months — 4 months too late; corrected
// 2026-07 to the −6-month expiry anchor. See project_cne_mandatory_cycle_rule.)
// Points only count if earned within that CNE window — NOT the PC window — so
// anything that buckets records by cycle MUST go through cneWindow(), never the
// raw PC dates. The 6-month gap between cycle end (30 Jun) and PC expiry (31 Dec)
// is the renewal-processing window: earn by 30 Jun, renew by 31 Dec.
//
// All arithmetic is done on the Y-M-D parts via Date.UTC so a local timezone
// can never roll a date across midnight (the inputs/outputs are plain
// 'YYYY-MM-DD' strings, the same shape Supabase `date` columns return).
function _ymd(iso){ return String(iso).split('-').map(Number); }            // → [y,m,d]
function _isoUTC(y, m, d){ return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10); }
// Shift by whole months, clamping the day to the target month's last day so
// e.g. Mar 31 − 1 month → Feb 28/29 rather than overflowing into March.
function shiftMonths(iso, delta){
  if (!iso) return iso;
  const [y, m, d] = _ymd(iso);
  const t = new Date(Date.UTC(y, m - 1 + delta, 1));
  const ty = t.getUTCFullYear(), tm = t.getUTCMonth();
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return _isoUTC(ty, tm + 1, Math.min(d, lastDay));
}
function shiftDays(iso, delta){
  if (!iso) return iso;
  const [y, m, d] = _ymd(iso);
  return _isoUTC(y, m, d + delta);
}
// PC expiry from PC start: a full 3-year block, inclusive (start + 3y − 1 day).
// 2024-01-01 → 2026-12-31.
function pcEndFromStart(pcFromIso){
  if (!pcFromIso) return null;
  const [y, m, d] = _ymd(pcFromIso);
  return shiftDays(_isoUTC(y + 3, m, d), -1);
}
// PC start from an expiry date (the inverse of pcEndFromStart for a standard
// three-year certificate). Onboarding asks for the expiry because that is the
// date nurses are most likely to know; the existing schema still stores both
// validity dates for rollover/export compatibility.
function pcStartFromEnd(pcToIso){
  if (!pcToIso) return null;
  return shiftDays(shiftMonths(pcToIso, -36), 1);
}
// CNE earning window — "Method B", anchored ONLY on the PC expiry (pcToIso =
// renewal_date), never on pcFromIso. Per the NCHK rule (see header above):
//   end   = renewal_date − 6 months
//   start = renewal_date + 1 day − 3.5 years   (== end − 3 years + 1 day)
// Deriving both ends from the expiry keeps irregular first blocks correct — a
// short or late first PC no longer skews the window start. pcFromIso is kept in
// the signature for the legacy call sites and used ONLY as a fallback when the
// expiry is unknown (guest / half-filled profile). Example:
// (…, 2029-12-31) → (2026-07-01 … 2029-06-30).
function cneWindow(pcFromIso, pcToIso){
  if (!pcToIso) {
    return { start: pcFromIso ? shiftMonths(pcFromIso, -6) : null, end: null };
  }
  return {
    start: shiftDays(shiftMonths(pcToIso, -42), 1), // −3.5y then +1 day
    end:   shiftMonths(pcToIso, -6),
  };
}
// Parse anything into a real four-digit calendar year, or null.
//
// ‼️ NEVER range-check a parsed year with a bare `Number.isInteger(...)`:
// `Number('')` is 0 and `Number.isInteger(0)` is TRUE, so an absent date sails
// through as year "0". That is exactly how a licence with no PC dates (326 of
// 516 live profiles — the majority who skip the renewal-date step) grew a bogus
// `0` chip beside 2026 in Record → 已完成紀錄, selectable into an empty timeline
// headed "0 CNE 分". Every year parsed from a possibly-blank string goes
// through here. Regression: scripts/test-data.mjs, the three
// "recordYears: …" checks — `npm run test:data`, wired into CI.
function _calendarYear(value){
  const year = Number(value);
  return Number.isInteger(year) && year >= 1900 && year <= 9999 ? year : null;
}
// A CNE window spans 3 years + the boundary year by construction (Method B),
// so 4 entries is the real maximum. The cap exists only so a corrupt
// `license2` jsonb blob — a free-form, client-written column — can never
// expand into thousands of buttons in the horizontal year scroller.
const MAX_LICENSE_WINDOW_YEARS = 8;
// Mirrors record-extras.jsx's isRecordCompleted (COMPLETED:1 in the notes blob,
// set by 標記為已完成 when a course is finished before its last session). Copied
// rather than imported because data.js is the scoring layer and loads BEFORE
// record-extras.jsx — same reason isRecordExcluded lives here. Keep the two
// regexes identical.
function _recordMarkedComplete(record){
  return /(^|\n)COMPLETED:1(\n|$)/.test(record && record.notes || '');
}
// Every year a nurse can reasonably need to reach from Record history.  `year`
// is a generated database column, so reading it is safe and avoids reparsing
// dates. Keep the current year and all licence CNE windows present even before
// a first record has been logged; this also guarantees focusRecordId can select
// its year rather than presenting an apparently empty timeline.
//
// ‼️ FUTURE YEARS ARE NOT CHIPS. A CNE window runs ~3.5 years ahead of today
// (Method B: a 2029 renewal opens a 2026–2029 window), so expanding it blindly
// used to mint 2027/2028/2029 buttons — and the 已完成紀錄 timeline only ever
// renders records with `date <= today` OR an explicit COMPLETED:1, so those
// chips are empty BY CONSTRUCTION. Paired with the descending sort they became
// the default selection: a nurse with 39 completed records opened the tab onto
// "2029 · 0 分 · 此週期未有記錄". Hence: licence-window years are clamped to the
// current year, and a record's own year is admitted past today ONLY when that
// record is actually reachable in the timeline (marked complete). Whatever you
// add here, every returned year must be able to hold a visible row.
// Regression: scripts/test-data.mjs, the "recordYears: …" / "defaultRecordYear:
// …" checks — `npm run test:data`, wired into CI.
function recordYears(records, licenses, today){
  const years = new Set();
  const parsedNow = _calendarYear(_axisToday(today).slice(0, 4));
  const now = parsedNow === null ? new Date().getFullYear() : parsedNow;
  years.add(now);
  for (const record of (records || [])) {
    const year = _calendarYear(record?.year || String(record?.date || '').slice(0, 4));
    if (year === null) continue;
    // An early-finished course keeps its real (future) end date, so its year is
    // the only future one that can ever show a row. See the header.
    if (year > now && !_recordMarkedComplete(record)) continue;
    years.add(year);
  }
  for (const lic of (licenses || [])) {
    const { start, end } = cneWindow(lic?.cycleStart || lic?.cycle_start || null,
      lic?.renewalDate || lic?.renewal_date || null);
    // A half-filled licence yields a null end (or both) — skip it entirely
    // rather than letting the blank parse to 0. See _calendarYear.
    const from = _calendarYear(String(start || '').slice(0, 4));
    const to = _calendarYear(String(end || '').slice(0, 4));
    if (from === null || to === null || from > to) continue;
    // `now` clamps the future tail; MAX_LICENSE_WINDOW_YEARS still caps a corrupt
    // license2 blob whose window starts in 1900. A window that has not begun
    // yet (from > now) contributes nothing, which is correct for a history view.
    const last = Math.min(to, now, from + MAX_LICENSE_WINDOW_YEARS - 1);
    for (let year = from; year <= last; year++) years.add(year);
  }
  return Array.from(years).sort((a, b) => b - a);
}
// The year 已完成紀錄 opens on, and the year it falls back to when the current
// selection disappears from the list.
//
// ‼️ NEVER `recordYearOptions[0]`. The list is sorted DESCENDING, so [0] is the
// furthest-out year — the emptiest one. Prefer the current year; failing that
// the most recent PAST year (a lapsed profile whose window ended in 2024 should
// land on 2024, not on a future chip); only if every option is still ahead of
// today does the nearest upcoming year win. Both call sites in RecordScreen
// (useState initialiser + the "selection no longer valid" effect) MUST go
// through here — that is what keeps them from drifting apart again.
function defaultRecordYear(years, today){
  const list = (years || []).map(_calendarYear).filter(year => year !== null);
  if (!list.length) return null;
  const parsedNow = _calendarYear(_axisToday(today).slice(0, 4));
  const now = parsedNow === null ? new Date().getFullYear() : parsedNow;
  if (list.includes(now)) return now;
  const past = list.filter(year => year < now);
  return past.length ? Math.max(...past) : Math.min(...list);
}
// Records that count toward the current cycle: earned on/before today AND inside
// the CNE earning window derived from the profile's PC dates. SINGLE SOURCE OF
// TRUTH — the home ring, profile "cycle earned", the renewal reminder, and the
// PDF export all funnel through this so every "this cycle" number agrees. When
// the PC dates are unknown (guest/incomplete profile) the window is open-ended
// and only the future-date guard applies. Pass `pcFrom`/`pcTo` (the PC validity
// dates), NOT the CNE dates — this shifts them internally.
// isRecordExcluded — true when the user manually flipped a record to "don't
// count toward my CNE points" (persisted as EXCLUDED:1 in the notes blob,
// written by buildNoteBlob/parseNoteBlob in record-extras.jsx). Points-only:
// an excluded record still shows in history and still counts in the
// completed-record tallies — it's only subtracted from point TOTALS and the
// NCHK export. Defined here (the scoring layer, loaded before record-extras.jsx)
// so cycleRecords can read it with no load-order coupling; mirrors the
// isRecordCompleted regex pattern.
function isRecordExcluded(record){
  return /(^|\n)EXCLUDED:1(\n|$)/.test(record && record.notes || '');
}
function cycleRecords(records, pcFrom, pcTo, today){
  const now = _axisToday(today);   // local date, never UTC — see todayIso
  const { start, end } = cneWindow(pcFrom || null, pcTo || null);
  return (records || []).filter(r =>
    r && r.date && r.date <= now &&
    !isRecordExcluded(r) &&
    (!start || r.date >= start) &&
    (!end   || r.date <= end));
}

// Cycle-scoped point summary — the ONE place the home ring's centre number and
// its coloured arcs are computed, so they can never drift apart. Pass the PC
// validity dates (pcFrom/pcTo); cycleRecords shifts them into the CNE window
// internally. `total` here excludes baseline_points — callers add that on top.
// ⚠ MIRRORED IN SQL: get_group_progress in
// supabase-migration-referral-ward-groups.sql recomputes this window + sum so a
// colleague's ward-leaderboard number equals their own home ring. If you change
// the window math or the EXCLUDED filter here, update that function AND the
// shared fixture in scripts/test-data.mjs §4 (both sides assert the same numbers).
function cyclePointsSummary(records, pcFrom, pcTo, today){
  const recs = cycleRecords(records, pcFrom, pcTo, today);
  const byCategory = { core: 0, elective: 0, specialty: 0 };
  for (const r of recs){
    const cat = (r.category in byCategory) ? r.category : 'elective';
    byCategory[cat] += Number(r.points) || 0;
  }
  const total = byCategory.core + byCategory.elective + byCategory.specialty;
  return { total, byCategory };
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit readiness — "if the Council asked for your records tomorrow, could you
// hand them over?"
//
// DESIGN CONSTRAINT that keeps this honest: every gap is derived from something
// the NCHK export ACTUALLY needs (nchkAnnexARows / nchkAnnexB below). It is not
// a completeness score invented to be filled in — each item maps to a specific
// blank cell on Annex A or Annex B, or to the supporting document the
// declaration says you may be asked to produce.
//
// Returns { score, max, pct, gaps: [...] }, gaps ordered most-severe first.
// Each gap: { id, severity: 'high'|'medium'|'low', recordId?, label:{en,zh},
//             fix:{en,zh} }.
//
// `score` counts SATISFIED checks, so an empty cycle with a complete profile
// still reads honestly rather than showing 100%.
// ─────────────────────────────────────────────────────────────────────────────
function auditReadiness({ profile, user, records, pcFrom, pcTo, today } = {}) {
  const gaps = [];
  let score = 0, max = 0;
  const add = (ok, gap) => { max++; if (ok) score++; else gaps.push(gap); };

  const b = nchkAnnexB({ profile, user });
  // ── Annex B (the declaration) ──
  // Readiness requires an explicitly saved official name. Annex B still falls
  // back to display_name for backward-compatible previewing, but a nickname is
  // not evidence that the declaration identity has been reviewed.
  const hasOfficialName = !!(
    (profile?.name_en && String(profile.name_en).trim()) ||
    (profile?.name_zh && String(profile.name_zh).trim())
  );
  add(hasOfficialName, {
    id: 'name', severity: 'high',
    label: { en: 'Your name is missing', zh: '未填寫姓名' },
    fix: { en: 'Add your official name under Nursing registration — it heads the declaration form.',
           zh: '在「護士登記與執業證書」填寫官方姓名——聲明表格需要。' },
  });
  add(!!b.regNo, {
    id: 'regNo', severity: 'high',
    label: { en: 'Registration number is missing', zh: '未填寫註冊編號' },
    fix: { en: 'Add the NCHK number to your practising certificate under Nursing registration.',
           zh: '在「護士登記與執業證書」填寫護士管理局登記／註冊編號。' },
  });
  add(!!(b.cneFrom && b.cneTo), {
    id: 'cycle', severity: 'high',
    label: { en: 'Your CNE cycle dates are not set', zh: '未設定 CNE 週期日期' },
    fix: { en: 'Set the certificate expiry under Nursing registration — the whole cycle is derived from it.',
           zh: '在「護士登記與執業證書」設定到期日——整個週期由此推算。' },
  });

  // ── The cycle total ──
  const recs = cycleRecords(records, pcFrom || b.pcFrom, pcTo || b.pcTo, today);
  const lics = profileLicenses(profile, user);
  const required = lics.length ? Number(lics[0].required) || 0 : 0;
  const earned = cyclePointsSummary(records, pcFrom || b.pcFrom, pcTo || b.pcTo, today).total;
  if (required > 0) {
    add(earned >= required, {
      id: 'points', severity: 'high',
      label: { en: `${Math.max(0, required - earned)} more points needed this cycle`,
               zh: `本週期尚欠 ${Math.max(0, required - earned)} 分` },
      fix: { en: 'Log completed courses, or find new ones in Discover.',
             zh: '記錄已完成的課程,或在「探索」尋找新課程。' },
    });
  }

  // ── Annex A, per record ──
  // A missing supporting document is the gap that actually bites at audit: the
  // declaration says the Council may ask to see one, and CNE_FACTS.retentionYears
  // (6) is how long they must be kept.
  for (const r of recs) {
    const tEn = L(r.title, 'en') || L(r.title, 'zh') || '';
    const tZh = L(r.title, 'zh') || tEn;
    add(!!r.certificate, {
      id: `cert:${r.id}`, severity: 'medium', recordId: r.id,
      label: { en: `No certificate attached — ${tEn}`, zh: `未附證書 — ${tZh}` },
      fix: { en: 'Attach the certificate so you can produce it if asked.',
             zh: '附上證書,以備查核時提交。' },
    });
    add(!!(L(r.provider, 'en') || L(r.provider, 'zh')), {
      id: `org:${r.id}`, severity: 'medium', recordId: r.id,
      label: { en: `Organiser missing — ${tEn}`, zh: `未填主辦機構 — ${tZh}` },
      fix: { en: 'Annex A needs the organising body for every activity.',
             zh: 'Annex A 每項活動都要填寫主辦機構。' },
    });
    add(Number(r.points) > 0, {
      id: `points:${r.id}`, severity: 'low', recordId: r.id,
      label: { en: `No points recorded — ${tEn}`, zh: `未記錄分數 — ${tZh}` },
      fix: { en: 'A zero-point row adds nothing to your declared total.',
             zh: '0 分的紀錄不會計入申報總分。' },
    });
  }

  const order = { high: 0, medium: 1, low: 2 };
  gaps.sort((a, z) => order[a.severity] - order[z.severity]);
  return { score, max, pct: max ? Math.round((score / max) * 100) : 100, gaps };
}

// ─────────────────────────────────────────────────────────────────────────────
// Dual-registration licences (a nurse on more than one part of the register)
// ─────────────────────────────────────────────────────────────────────────────
// A Hong Kong nurse whose name is on more than one part of the register (e.g. a
// General registration + a Psychiatric one) holds a separate practising
// certificate — and so a separate 3-year CNE cycle — per registration. Each
// certificate corresponds to a ROLE (Registered/Enrolled, General or Psychiatric
// — see ROLE_OPTIONS in onboarding.jsx), which is what distinguishes the two on
// screen. The overwhelming majority hold ONE, so the primary licence stays in
// the flat profile columns (role_en/role_zh / cycle_start / renewal_date /
// required_points / baseline_points / license_no) exactly as before, and a
// second, optional licence lives in the `license2` jsonb column.
// profileLicenses() normalises the two into a single array so every consumer
// (home ring, profile stats, renewal reminder, NCHK export) can loop instead of
// assuming one cycle. Each descriptor carries its own `role: {en, zh}` label.
//
// v1 counting rule: the NCHK's current manual (2011, voluntary) is silent on how
// points are attributed across two registrations and the mandatory-scheme manual
// is still unpublished, so an activity counts toward EVERY licence whose CNE
// window contains its date — which each licence's window already does, with no
// per-record tag. When the official rule lands, a per-record LICENSE: notes
// marker (mirroring EXCLUDED:1) is the seam to add.
//
// Normalise a profile into an array of licence descriptors. Always returns the
// primary (the flat columns, even when its cycle dates are blank — matching the
// single-cycle status quo); appends the secondary ONLY when license2 carries a
// usable cycle_start, so a half-filled jsonb blob can never surface a ghost card.
// The first MANDATORY CNE cycle applies to certificates expiring on/after
// 31 Dec 2029 (new PC effective 1 Jan 2030 — NCHK letter 30 Jun 2026); earlier
// renewals predate the scheme and stay VOLUNTARY. A first-PC applicant is exempt
// entirely ("nurses who are NOT applying for the first time" are bound). This
// per-licence flag gates ONLY the legal-requirement framing (must / deadline /
// 強制) — the cycle DATES are always the correct −6mo window regardless, so
// tracking stays honest and continuous for voluntary users too. See
// project_cne_mandatory_cycle_rule.
// ‼️ Canonical value lives in cne-facts.js as CNE_FACTS.mandatoryFrom, and is
// mirrored a third time in supabase/functions/notify-cron/index.ts (Deno, no
// bundler). data.js keeps its own literal rather than reading CNE_FACTS so the
// scoring layer stays loadable standalone in the vm test harnesses. All three
// are asserted equal by scripts/test-data.mjs — change one, change all three.
const MANDATORY_CNE_FROM = '2029-12-31';
function licenseIsMandatory(lic){
  return !!(lic && lic.renewalDate && !lic.exempt && lic.renewalDate >= MANDATORY_CNE_FROM);
}
function profileLicenses(profile){
  const p = profile || {};
  // ⚠ `required: 0` is MEANINGFUL — a first-PC-exempt certificate carries no CNE
  // requirement, so the descriptor's required is derived to 0 here (the stored
  // required_points column is untouched; unticking first_pc restores it). Every
  // downstream reader must therefore NEVER coerce with `|| 45` — that pattern
  // silently turns the exempt 0 back into 45. Use the descriptor's value as-is
  // (see licenseProgress / deriveReminders / profileToUser). Guarded by
  // scripts/test-data.mjs.
  const out = [{
    key: 'primary',
    role: { en: p.role_en || 'Registered Nurse', zh: p.role_zh || '註冊護士' },
    licenseNo: p.license_no || '',
    cycleStart: p.cycle_start || null,
    renewalDate: p.renewal_date || null,
    required: p.first_pc ? 0 : (Number(p.required_points) || 45),
    baseline: Number(p.baseline_points) || 0,
    exempt: !!p.first_pc, // Batch B: first-PC applicant → no CNE requirement yet
  }];
  const l2 = p.license2;
  if (l2 && typeof l2 === 'object' && l2.cycle_start) {
    out.push({
      key: 'secondary',
      role: { en: l2.role_en || 'Registered Nurse', zh: l2.role_zh || '註冊護士' },
      licenseNo: l2.license_no || '',
      cycleStart: l2.cycle_start || null,
      renewalDate: l2.renewal_date || null,
      required: l2.first_pc ? 0 : (Number(l2.required_points) || 45),
      baseline: Number(l2.baseline_points) || 0,
      exempt: !!l2.first_pc,
    });
  }
  // Mandatory status is per-certificate (a nurse may hold one bound + one exempt/
  // pre-mandatory registration) — compute it here so every consumer agrees.
  for (const lic of out) lic.isMandatory = licenseIsMandatory(lic);
  return out;
}
// Per-licence progress — the ONE computation the home ring, profile stats and
// renewal reminder share for a given licence, so a card's centre number, its
// coloured arcs and its "still needed" gap can never drift. `earned` folds in
// baseline_points (cyclePointsSummary excludes it); `window` is the CNE window
// the card should label. Pass a descriptor from profileLicenses().
function licenseProgress(records, lic, today){
  const summary = cyclePointsSummary(records, lic && lic.cycleStart, lic && lic.renewalDate, today);
  const baseline = (lic && Number(lic.baseline)) || 0;
  // required 0 (first-PC exempt) must survive — `|| 45` would resurrect the
  // requirement, so only fall back when the descriptor carries no number.
  const req = lic && Number.isFinite(Number(lic.required)) ? Number(lic.required) : 45;
  return {
    earned: baseline + summary.total,
    baseline,
    split: summary.byCategory,
    total: summary.total,
    required: req,
    window: cneWindow((lic && lic.cycleStart) || null, (lic && lic.renewalDate) || null),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE PLANNING — "what is the cheapest legal way to finish this cycle?"
//
// Consumed by the Pro route card. Everything here is DERIVED; nothing is stored,
// so a plan can never go stale against the records or the catalogue it was built
// from.
//
// ‼️ THE GOVERNING RULE OF THIS WHOLE SECTION: UNKNOWN EXCLUDES, IT NEVER
// ASSUMES. An unclassified course is not "probably direct". An unknown fee is
// not "probably free". An unknown repeatability is not "probably repeatable".
// This is a paid feature about professional compliance — a plan that is
// confidently wrong costs a nurse money or her cycle, and no disclaimer under
// the card repairs that. Everything excluded is reported back in `excluded` so
// the UI can say WHY the route is thin instead of pretending it is complete.
// ─────────────────────────────────────────────────────────────────────────────

// Programme identity across re-listings. The DB computes this too (generated
// column `course_repeat_key`, see supabase-migration-course-semantics.sql); this
// is the client-side twin so the app behaves correctly on rows cached before the
// migration, and so the two can be diffed if they ever disagree.
//
// ‼️ NOT course_code. Live data: one BLS programme is 14 rows with 14 DISTINCT
// course_codes (BLS160926A, BLS100626B, …) because the code encodes the date and
// class. Keying on it dedupes nothing.
function courseRepeatKey(course) {
  if (!course) return '';
  const raw = course.raw || {};
  const override = raw.repeat_key_override || course.repeatKeyOverride;
  if (override) return String(override);
  const stored = raw.course_repeat_key;
  if (stored) return String(stored);
  const provider = L(course.provider, 'en') || raw.provider_full_en || raw.provider_code || '';
  const title = L(course.title, 'en') || raw.title_en || raw.title_zh || '';
  const strip = (s) => String(s || '').toLowerCase()
    .replace(/\b(19|20)\d{2}\b/g, '')                 // the year
    .replace(/\bclass\s*[a-z0-9]+\b/g, '')            // "Class D"
    .replace(/\b(course|programme|program)\b/g, '')   // generic nouns
    .replace(/[^\p{L}\p{N}]+/gu, '');                 // punctuation, keeps CJK
  return `${strip(provider)}|${strip(title)}`;
}

// Is this course still completable, for THIS nurse, inside THIS cycle window?
//
// ‼️ DELIBERATELY NOT courseIsActionable(). That function answers a Discover
// question — "is this a bookable event right now" — and the free CNE supply in
// Hong Kong mostly is not one: JCIMED e-learning, monthly talks and 網上文章 are
// self-paced, carry no start_date and have no seats, so they fail every
// bookable-event test while being perfectly completable. Conflating the two is
// what hid the free supply in the first place. Keep them separate.
function courseIsRouteEligible(course, { profile, windowEnd, today } = {}) {
  if (!course) return false;
  if ((course.lifecycleStatus || 'active') !== 'active') return false;

  const now = _axisToday(today);
  const raw = course.raw || {};
  const availability = raw.availability_type || null;

  // Completion must land inside the cycle window, or the points do not count
  // toward THIS renewal — recommending it would be actively misleading.
  const finishesBy = availability === 'evergreen' || availability === 'recurring'
    ? (raw.available_until || course.endDate || null)
    : (course.endDate || course.date || null);
  // An open-ended evergreen course (no available_until) is completable whenever
  // she likes, so it always fits the window; a dated course with no date at all
  // cannot be shown to fit and is excluded.
  if (finishesBy) {
    if (finishesBy < now) return false;                     // already over
    if (windowEnd && finishesBy > windowEnd) return false;   // lands after the cycle
  } else if (availability !== 'evergreen' && availability !== 'recurring') {
    return false;                                            // dated, but undated
  }

  // Register eligibility. NULL = unrestricted (today's behaviour for every row).
  const registers = raw.eligible_registers;
  if (Array.isArray(registers) && registers.length) {
    const hers = String(profile?.role_en || profile?.role || '').toUpperCase();
    const part = /ENROLLED/.test(hers) ? 'EN' : /MIDWIF/.test(hers) ? 'RM' : 'RN';
    if (!registers.map((r) => String(r).toUpperCase()).includes(part)) return false;
  }

  // ‼️ RECOMMENDATION BAR, NOT THE LISTING BAR. A route is the app promising
  // "do these and you are done", so it uses restrictedRecommendable — the
  // stricter of the two predicates defined above. The difference is exactly
  // 'unknown': a restricted course whose axis we cannot judge (no workplace on
  // file, signed-out) stays VISIBLE in Discover on benefit of the doubt, and
  // must not be pushed into a paid plan on the same benefit of the doubt.
  //
  // This read restrictedVisibleByDefault until 2026-08-15 — the listing
  // predicate, which returns true for 'unknown'. The note beside
  // restrictedRecommendable had already predicted that exact leak ("the change
  // leaking from the surface it was designed for into one where the app is
  // making a promise"); the route was that surface. Live exposure measured 0 at
  // the time, but only because all 56 unfinished 只限公立醫院員工 rows publish no
  // fee and were dropped by the unknownFee guard below — an unrelated condition
  // masking this one. Pinned by test-route-plan case 24, which prices the
  // fixture specifically to remove that mask.
  //
  // Do NOT "simplify" this back to restrictedVisibleByDefault. 'open'
  // (membership / unparseable note) passes BOTH predicates, so nothing a nurse
  // could join her way into is lost here.
  if (course.isRestricted && typeof restrictedRecommendable === 'function'
      && !restrictedRecommendable(course, profile)) return false;

  // ‼️ CAN SHE ACTUALLY GET IN? A plan that costs money and names a course whose
  // registration is closed or full is worse than no plan — she budgets for it,
  // tries to book, and finds the door shut. Live data 2026-08-09 shows this is
  // not hypothetical: every free course in the catalogue reads Closed or Full.
  //
  // Only POSITIVE evidence of being unbookable excludes. 'unknown' stays in:
  // most of the catalogue publishes no registration status at all, and treating
  // silence as closed would empty the planner for the opposite reason.
  const registration = deriveRegistrationStatus(course, today);
  if (registration === 'closed' || registration === 'full'
      || registration === 'not_applicable') return false;

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// PROFESSIONAL MEMBERSHIPS
//
// ‼️ THIS FIELD WAS EFFECTIVELY DEAD UNTIL 2026-08-10, AND THE REASON IS THE
// WHOLE DESIGN OF WHAT FOLLOWS. It was one free-text line, and the matcher
// compared what she typed to `L(course.provider,'en')` — the provider's full
// ENGLISH name. Measured against the live catalogue:
//
//     "HKACCN"        → NO MATCH   (provider is
//                                   "Hong Kong Association of Critical Care
//                                    Nurses", which does not contain "hkaccn")
//     "香港護士協會"    → NO MATCH   (CJK text vs an English name)
//     the full English name, typed verbatim → match
//
// Both failing examples were the field's OWN placeholder text. So the honest
// reading of "133 of 133 live routes priced conditionally" is not that nobody
// filled it in — it is that filling it in did nothing.
//
// The fix is to stop matching prose. The picker writes `provider_code`, and
// the FIRST rule below is an exact code comparison that no substring logic can
// misfire on. Free text still round-trips (legacy rows, and societies outside
// the catalogue) through the fuzzy rules, which now also read the 繁中 name and
// the short code — the two things the old matcher could never see.
// ─────────────────────────────────────────────────────────────────────────────

// Fuzzy matching on 1–3 characters is how "HK" would silently make her a member
// of everything. A wrong TRUE here is the expensive direction: it quotes a
// discount she cannot obtain, so she budgets less than the course costs.
const MEMBERSHIP_MIN_FUZZY_LEN = 4;
// Bounds on what we will store. The client can PATCH its own profile row
// directly (RLS allows own-row update), so these are enforced here, at both UI
// call sites through sanitiseMemberships(), and again as a CHECK constraint in
// supabase-migration-memberships-bounds.sql. Belt and braces on purpose: this
// is the only free-text array a user can write into their profile.
const MEMBERSHIP_MAX_ENTRIES = 12;
const MEMBERSHIP_MAX_LEN = 60;

function membershipNorm(s) {
  return String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

// The provider code a course belongs to, from either course shape.
function courseProviderCode(course) {
  return String(course?.providerCode || course?.raw?.provider_code || '').trim();
}

// Does ONE stored membership entry mean "I am a member of this course's
// provider"? Two rules, most reliable first.
function membershipMatchesCourse(entry, course) {
  const raw = String(entry || '').trim();
  if (!raw || !course) return false;
  const code = courseProviderCode(course);

  // 1. CODE — what the picker stores. Exact and case-insensitive; nothing
  //    fuzzy can go wrong here, which is the entire point of the rewrite.
  if (code && raw.toLowerCase() === code.toLowerCase()) return true;

  const m = membershipNorm(raw);
  if (m.length < MEMBERSHIP_MIN_FUZZY_LEN) return false;

  // 2. LEGACY FREE TEXT. Short forms (codes, 縮寫) must match EXACTLY — they
  //    are too short for containment to be evidence of anything. Full names
  //    may match either way round, so "HKACCN 香港危重病學護士協會" still
  //    resolves against the 繁中 name even though it carries extra words.
  const exact = [code, L(course.providerShort, 'en'), L(course.providerShort, 'zh'),
                 PROVIDERS[code]?.short_en, PROVIDERS[code]?.short_zh];
  if (exact.some(c => { const n = membershipNorm(c); return n && n === m; })) return true;

  const names = [L(course.provider, 'en'), L(course.provider, 'zh'),
                 PROVIDERS[code]?.en, PROVIDERS[code]?.zh];
  return names.some(c => {
    const n = membershipNorm(c);
    return n.length >= MEMBERSHIP_MIN_FUZZY_LEN && (n.includes(m) || m.includes(n));
  });
}

function profileHoldsMembershipFor(profile, course) {
  const held = Array.isArray(profile?.memberships) ? profile.memberships : [];
  return held.some(entry => membershipMatchesCourse(entry, course));
}

// The picker's options: providers whose catalogue rows actually publish
// DIFFERENT member and non-member prices.
//
// ‼️ DERIVED FROM THE CATALOGUE, NEVER HARDCODED. A hardcoded list is wrong the
// day a provider starts (or stops) publishing a split, and this list is a
// promise: every option is a membership that WILL change a number on the route
// card. Measured 2026-08-10 on live data: 169 of 494 rows carry a split, across
// exactly 7 providers — which is what makes a picker possible at all.
//
// ‼️ IT IS NOT "ALL HONG KONG NURSING SOCIETIES", and it must never be sold as
// that. The free-text box next to it stays, because a society we cannot price
// against is still hers to record — the UI just has to be honest that entering
// it changes no cost. That was the original objection to a picker, and it is
// answered by keeping both, not by dropping either.
//
// ARGUMENT SEMANTICS: omit `courses` to read the live catalogue (what both UI
// call sites do); pass an array — INCLUDING an empty one — to use exactly that.
// `[]` must mean "no catalogue", not "no argument", or a caller that genuinely
// has nothing to offer would silently get the global list instead and the UI
// could not distinguish "still loading" from "loaded, nothing to offer".
function membershipProviderOptions(courses) {
  const pool = Array.isArray(courses)
    ? courses
    : (typeof COURSES !== 'undefined' && Array.isArray(COURSES) ? COURSES : []);
  const byCode = new Map();
  pool.forEach((c) => {
    const member = Number(c?.costMember);
    const nonMember = Number(c?.costNonMember);
    if (!(member > 0) || !(nonMember > 0) || member === nonMember) return;
    const code = courseProviderCode(c);
    if (!code) return;
    const entry = byCode.get(code) || { code, en: '', zh: '', courses: 0 };
    entry.courses += 1;
    entry.en = entry.en || L(c.provider, 'en') || PROVIDERS[code]?.en || code;
    entry.zh = entry.zh || L(c.provider, 'zh') || PROVIDERS[code]?.zh || entry.en;
    byCode.set(code, entry);
  });
  return Array.from(byCode.values())
    .sort((a, b) => b.courses - a.courses || a.code.localeCompare(b.code));
}

// Everything written to profiles.memberships goes through here. Trims, drops
// blanks, de-duplicates case-insensitively (so picking a chip cannot double up
// with the same code typed by hand), and clamps both dimensions.
function sanitiseMemberships(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    // STRINGS ONLY — not "coerce whatever arrives". String(0) is "0" and
    // String({}) is "[object Object]", and both would be stored verbatim in a
    // text[] the user then sees in her own profile. A non-string here means a
    // caller bug, and dropping it is the only reading that cannot surprise her.
    if (typeof raw !== 'string') return;
    // Control characters have no place in a society name and are a rendering
    // and log-injection nuisance; the CHECK constraint rejects them too, so
    // stripping here keeps the client from producing an unexplainable 23514.
    // ‼️ slice() COUNTS UTF-16 UNITS, so a cut can land between the two halves
    // of a surrogate pair and leave a lone surrogate — which is not valid UTF-8
    // and would be rejected somewhere between JSON.stringify and Postgres, as a
    // failure with no useful message. Drop a trailing orphan.
    const v = raw.replace(/[\p{Cc}\p{Cf}]/gu, '').trim()
      .slice(0, MEMBERSHIP_MAX_LEN)
      .replace(/[\uD800-\uDBFF]$/, '');
    if (!v) return;
    const key = v.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    if (out.length < MEMBERSHIP_MAX_ENTRIES) out.push(v);
  });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// WHICH CERTIFICATE THE PLANNER IS ABOUT
//
// A dual-registered nurse has two certificates with two windows and two
// requirements, and the route card plans for exactly one of them: the biggest
// shortfall — the one she has to act on first.
//
// ‼️ ONE DEFINITION, BECAUSE THE ANSWER IS STORED AGAINST A LICENCE KEY. The
// "have you started this cycle?" answer is filed per licence
// (prefs.cneNotStarted[key]). If the sheet that ASKS chose one certificate by
// one rule and the card that READS chose another by a copy of that rule, the
// answer would be stored under a key nothing looks at — and the card would go
// on asking forever, with no way for her to tell why. Both call this.
//
// `earned` may legitimately be 0 and `required` may legitimately be 0 (a
// first-PC-exempt certificate) — hence Number.isFinite rather than `|| 45`,
// which is the bug profileLicenses' own header warns about.
function cnePlanLicence(licences) {
  return (Array.isArray(licences) ? licences : []).reduce((worst, lic) => {
    const req = Number(lic?.required);
    if (!Number.isFinite(req)) return worst;
    const gapHere = req - (Number(lic?.earned) || 0);
    const gapWorst = worst ? Number(worst.required) - (Number(worst.earned) || 0) : -Infinity;
    return gapHere > gapWorst ? lic : worst;
  }, null);
}

// The licence list in the shape the planner reasons about: a dual-registered
// nurse's own descriptors, or the single synthesised primary for everyone else.
// Mirrors what HomeScreen builds for its wallet cards — kept here so a caller
// that only has `u` (app.jsx) picks the same certificate the card does.
function cnePlanLicences(u) {
  if (!u) return [];
  if (Array.isArray(u.licenses) && u.licenses.length) return u.licenses;
  return [{
    key: 'primary',
    cycleStart: u.cycleStart,
    renewalDate: u.renewalDate,
    required: u.required,
    earned: u.earned,
  }];
}

// Which published price does SHE actually pay, and do we KNOW it?
//
// 84% of the actionable catalogue publishes member and non-member prices. Taking
// the lower one because it is lower is how a solver picks a course she cannot
// obtain at that price — `costIsFrom` on the label does not fix a route that was
// CHOSEN wrongly. So: resolve the tier when her memberships say we can, and
// otherwise return the non-member price (what she would actually pay) while
// flagging the whole plan conditional.
function applicableFee(course, profile) {
  if (!course || course.feeUnknown) return { amount: null, certain: false };
  if (course.isFree) return { amount: 0, certain: true };
  const member = Number(course.costMember) > 0 ? Number(course.costMember) : null;
  const nonMember = Number(course.costNonMember) > 0 ? Number(course.costNonMember) : null;
  const base = Number(course.costFrom) > 0 ? Number(course.costFrom) : null;

  if (member == null || nonMember == null || member === nonMember) {
    return { amount: base ?? member ?? nonMember, certain: base != null };
  }
  // Conditional pricing. Only a recorded membership naming this provider settles
  // it; a membership of some OTHER society tells us nothing about this fee.
  if (profileHoldsMembershipFor(profile, course)) return { amount: member, certain: true };
  // Not known to be a member → she pays the non-member price. Honest, and it
  // keeps the solver from preferring a discount she may not get.
  return { amount: nonMember, certain: false };
}

// Above this many candidates, enumerate-everything stops being free: 2^18 is
// 262k subsets (a few ms), 2^24 is 16M (seconds, on the main thread, on a
// phone). The DP below is exact for the cheapest objective at any size, so this
// is a performance switch, NOT an accuracy one.
const ROUTE_EXHAUSTIVE_MAX = 18;
// CNE points come in halves (0.5, 1.5, 4.5). Scaling by 2 makes the DP's point
// axis integral without floating-point drift.
const ROUTE_POINT_SCALE = 2;

// ‼️ GREEDY IS WRONG HERE AND MUST NOT COME BACK. Sorting by cost-per-point and
// accumulating does NOT minimise cost: for a 5-point gap with A(4pt $40),
// B(4pt $40), C(5pt $60), greedy takes A+B = $80 while the answer is C = $60.
// The card says "cheapest", so the solver has to actually be right.
// scripts/test-route-plan.mjs pins this exact case.
function _bestSubsetExhaustive(items, targetPoints, compare, indirectAllowance) {
  const n = items.length;
  let best = null;
  for (let mask = 1; mask < (1 << n); mask++) {
    let points = 0;
    let cost = 0;
    let count = 0;
    let relevance = 0;
    let indirect = 0;
    for (let i = 0; i < n; i++) {
      if (!(mask & (1 << i))) continue;
      points += items[i].points;
      cost += items[i].cost;
      relevance += items[i].relevance || 0;
      if (items[i].isIndirect) indirect += items[i].points;
      count++;
    }
    if (points + 1e-9 < targetPoints) continue;
    // The 5-point-per-cycle ceiling on indirectly-related programmes. Only
    // courses KNOWN to be indirect count against it — unclassified ones are
    // unconstrained, because no per-course determination has been published.
    if (indirect > indirectAllowance + 1e-9) continue;
    const cand = { mask, points, cost, count, relevance, excess: points - targetPoints };
    if (!best || compare(cand, best) < 0) best = cand;
  }
  if (!best) return null;
  return items.filter((_, i) => best.mask & (1 << i));
}

// Exact minimum-cost cover by dynamic programming, for catalogues too large to
// enumerate. 2D table (items × points) rather than the in-place 1-D trick: the
// rolling version cannot reconstruct WHICH courses were chosen without the
// parent chain being corrupted by later writes, and we need the actual list.
// TWO axes, not one: points reached, and indirect points consumed. The indirect
// ceiling cannot be applied afterwards by filtering a finished plan — dropping a
// capped course from an optimal route leaves a route that no longer covers the
// gap. It has to be a constraint the search carries.
function _bestSubsetDp(items, targetPoints, indirectAllowance) {
  const target = Math.max(1, Math.round(targetPoints * ROUTE_POINT_SCALE));
  const cap = Math.max(0, Math.round(indirectAllowance * ROUTE_POINT_SCALE));
  const width = cap + 1;
  const n = items.length;
  const INF = Infinity;
  const idx = (t, k) => t * width + k;
  // cost[i][t*width+k] = cheapest way to reach ≥t points using ≤k indirect
  // points, from the first i items.
  const cost = Array.from({ length: n + 1 },
    () => new Float64Array((target + 1) * width).fill(INF));
  cost[0][idx(0, 0)] = 0;
  for (let i = 0; i < n; i++) {
    const pts = Math.max(0, Math.round(items[i].points * ROUTE_POINT_SCALE));
    const ind = items[i].isIndirect ? pts : 0;
    for (let t = 0; t <= target; t++) {
      for (let k = 0; k < width; k++) {
        const here = cost[i][idx(t, k)];
        if (here === INF) continue;
        if (here < cost[i + 1][idx(t, k)]) cost[i + 1][idx(t, k)] = here;   // skip
        if (pts <= 0) continue;
        const nk = k + ind;
        if (nk >= width) continue;                       // would break the ceiling
        const nt = Math.min(target, t + pts);            // "at least" caps here
        const take = here + items[i].cost;
        if (take < cost[i + 1][idx(nt, nk)]) cost[i + 1][idx(nt, nk)] = take;
      }
    }
  }
  // Cheapest state that reached the target, at any indirect usage within cap.
  let bestK = -1;
  let bestCost = INF;
  for (let k = 0; k < width; k++) {
    const c = cost[n][idx(target, k)];
    if (c < bestCost) { bestCost = c; bestK = k; }
  }
  if (bestK < 0 || bestCost === INF) return null;
  // Walk back through the table to recover the chosen items.
  const chosen = [];
  let t = target;
  let k = bestK;
  for (let i = n; i > 0; i--) {
    if (cost[i][idx(t, k)] === cost[i - 1][idx(t, k)]) continue;   // item skipped
    const item = items[i - 1];
    const pts = Math.max(0, Math.round(item.points * ROUTE_POINT_SCALE));
    const ind = item.isIndirect ? pts : 0;
    let found = false;
    for (let p = Math.max(0, t - pts); p <= t && !found; p++) {
      const pk = k - ind;
      if (pk < 0) break;
      if (cost[i - 1][idx(p, pk)] !== INF
          && Math.min(target, p + pts) === t
          && Math.abs(cost[i - 1][idx(p, pk)] + item.cost - cost[i][idx(t, k)]) < 1e-9) {
        chosen.push(item);
        t = p;
        k = pk;
        found = true;
      }
    }
  }
  return chosen.reverse();
}

function _solveRoute(items, targetPoints, compare, indirectAllowance) {
  if (!items.length || !(targetPoints > 0)) return null;
  const allowance = Number.isFinite(indirectAllowance) ? indirectAllowance : Infinity;
  // Only the points that can actually be claimed count towards feasibility: an
  // indirect course beyond the ceiling is not available capacity.
  const usable = items.reduce((sum, item) => sum + item.points, 0);
  if (usable + 1e-9 < targetPoints) return null;                 // cannot be covered
  return items.length <= ROUTE_EXHAUSTIVE_MAX
    ? _bestSubsetExhaustive(items, targetPoints, compare, allowance)
    // The DP indexes an integer ceiling, so an unbounded allowance is clamped to
    // the target — beyond that it cannot bind anyway.
    : _bestSubsetDp(items, targetPoints, Math.min(allowance, targetPoints));
}

// The partial-coverage path: keep every direct course, and as much indirect
// credit as the remaining allowance permits — cheapest indirect points first, so
// the ceiling is spent on the best value. Used when nothing can close the gap and
// "the route" is simply everything claimable.
function _capIndirect(items, allowance) {
  const direct = items.filter((item) => !item.isIndirect);
  const indirect = items.filter((item) => item.isIndirect)
    .sort((a, b) => (a.cost / a.points) - (b.cost / b.points));
  let room = Number.isFinite(allowance) ? allowance : Infinity;
  const kept = [];
  for (const item of indirect) {
    if (item.points > room + 1e-9) continue;
    kept.push(item);
    room -= item.points;
  }
  return [...direct, ...kept];
}

// ─────────────────────────────────────────────────────────────────────────────
// COURSE WATCH RULES — "tell me when a course like this appears"
//
// ‼️ THE FIELD LIST IS DELIBERATELY SMALLER THAN DISCOVER'S FILTER SHEET, and
// that is the whole design. A rule is evaluated on the SERVER, in notify-cron,
// against columns Postgres can see: topic / is_free / is_online / is_weekend
// (generated columns, supabase-migration-course-facets.sql) plus cne_points and
// provider_code. Letting a nurse build a rule on a dimension the sweep cannot
// read — a registration deadline, a price ceiling — would produce a watch that
// silently never fires, which is worse than not offering it. Every field here
// maps to a real column; nothing else is offered.
//
// ‼️ THE SERVER RE-CLAMPS ALL OF THIS. prefs is client-writable, so these limits
// are UX here and defence there: notify-cron applies the same numbers on read.
// A client-side cap alone decides nothing.
const RULE_LIMITS = {
  maxRules: 10,        // per nurse — a cron job's workload must not be user-set
  maxTopics: 8,
  maxProviders: 8,
  maxLabel: 60,
  maxPoints: 50,
};

// Normalise whatever is stored (or submitted) into a safe, bounded rule list.
// Anything unrecognised is dropped rather than repaired: a rule we cannot read
// confidently is a rule that would notify about the wrong courses.
function normaliseCourseRules(raw) {
  if (!Array.isArray(raw)) return [];
  const strList = (v, max) => (Array.isArray(v) ? v : [])
    .map((s) => String(s || '').slice(0, 40))
    .filter(Boolean)
    .slice(0, max);
  const out = [];
  for (const item of raw.slice(0, RULE_LIMITS.maxRules)) {
    if (!item || typeof item !== 'object') continue;
    const points = Number(item.minPoints);
    out.push({
      id: String(item.id || '').slice(0, 40) || `r${out.length + 1}`,
      label: String(item.label || '').slice(0, RULE_LIMITS.maxLabel),
      topics: strList(item.topics, RULE_LIMITS.maxTopics),
      providers: strList(item.providers, RULE_LIMITS.maxProviders),
      // Tri-state on purpose: true = must be, false = must not be, null = don't
      // care. A plain boolean would make "don't care" indistinguishable from
      // "must be false" and quietly narrow every rule.
      free: item.free === true ? true : (item.free === false ? false : null),
      online: item.online === true ? true : (item.online === false ? false : null),
      weekend: item.weekend === true ? true : (item.weekend === false ? false : null),
      minPoints: Number.isFinite(points) && points > 0
        ? Math.min(RULE_LIMITS.maxPoints, Math.round(points * 2) / 2)
        : null,
    });
  }
  return out;
}

// Does a course match one rule? Pure boolean comparison over facets that were
// already derived elsewhere — NOT a re-derivation. That is what makes it safe to
// run this same logic on the server: the facts come from the generated columns,
// so there is no second implementation of "what is a free course".
//
// An empty rule matches everything, which is why the editor refuses to save one.
function courseMatchesRule(course, rule) {
  if (!course || !rule) return false;
  if (rule.topics.length && !rule.topics.includes(course.topic)) return false;
  if (rule.free !== null && !!course.isFree !== rule.free) return false;
  if (rule.online !== null && !!course.online !== rule.online) return false;
  if (rule.weekend !== null) {
    const wk = course.shiftTag ? course.shiftTag.weekend : null;
    // Unknown never satisfies a weekend rule in either direction — we cannot say.
    if (wk === null || wk === undefined || wk !== rule.weekend) return false;
  }
  if (rule.minPoints !== null && !(Number(course.points) >= rule.minPoints)) return false;
  if (rule.providers.length) {
    const code = course.raw?.provider_code || '';
    if (!rule.providers.includes(code)) return false;
  }
  return true;
}

// Does this candidate match the nurse's stated course preferences?
//
// ‼️ MODE IS AND, TIMING IS OR — and that asymmetry is the whole point. Mode is a
// single choice, so it simply applies. Timing is "when can I actually attend",
// where 週末 and 夜間 are alternatives, not requirements: someone who can do
// either wants BOTH kinds offered. Treating timing as AND is how a preference
// set collapses to nothing.
//
// The measured reason this matters: in the live actionable pool (n=81), online
// alone leaves 28 courses / 211 points and weekend alone leaves 17 / 117.5 — but
// online AND weekend leaves 5 courses / 22.5 points, less than half a 45-point
// requirement. Preferences multiply fast, which is why they produce an EXTRA
// plan here and never filter the candidate pool (see plans.preferred).
function _matchesPrefs(item, prefs) {
  if (!prefs) return true;
  if (prefs.mode === 'online' && !item.online) return false;
  if (prefs.mode === 'inPerson' && item.online) return false;
  const timing = Array.isArray(prefs.timing) ? prefs.timing : [];
  if (timing.length) {
    const tag = item.course && item.course.shiftTag;
    const ok = timing.some((t) => (t === 'weekend' && tag && tag.weekend === true)
                               || (t === 'evening' && tag && tag.evening === true));
    if (!ok) return false;
  }
  return true;
}

const _byCheapest = (a, b) =>
  a.cost - b.cost || a.excess - b.excess || a.count - b.count || b.relevance - a.relevance;
const _byFewest = (a, b) =>
  a.count - b.count || a.cost - b.cost || a.excess - b.excess || b.relevance - a.relevance;

// ─────────────────────────────────────────────────────────────────────────────
// cneRoutePlan — the Pro route card's entire data source.
//
//   → null   when there is nothing to plan: an exempt licence (required 0) or a
//            gap already closed. Rendering nothing is correct in both cases.
//   → object with `plans` (up to three), `coverage`, `costCertainty` and a full
//            `excluded` breakdown.
//
// ‼️ `required: 0` MUST SURVIVE. A first-PC-exempt nurse carries no requirement
// (profileLicenses spells out why); `|| 45` anywhere in this path invents one.
// ─────────────────────────────────────────────────────────────────────────────
// `exclude` — course ids to leave OUT of the candidate pool.
//
// This is what "換一個" is built on: the card hands back the ids of the route it
// is currently showing, and the next call plans around them. Two properties make
// that honest rather than a shuffle:
//   • The result is still the CHEAPEST route over what remains, so the
//     alternative is the genuine runner-up, not a random reordering.
//   • Exclusion never invents capacity. If nothing else can close the gap, the
//     call returns a partial plan or null, and the caller must not offer the
//     swap at all — which is why the card peeks at the next route before
//     showing the button.
// ‼️ IDs ONLY, and only for this one computation. Nothing is stored, so a
// swapped-away course returns the moment she reloads or the catalogue moves —
// hiding a real option permanently would be a worse bug than not offering swaps.
function cneRoutePlan({ records, lic, courses, profile, today, exclude } = {}) {
  if (!lic || lic.exempt) return null;
  const progress = licenseProgress(records || [], lic, today);
  const gap = Math.round((progress.required - progress.earned) * 100) / 100;
  if (!(gap > 0)) return null;

  const windowEnd = progress.window && progress.window.end;
  // ‼️ DUCK-TYPED, NOT `instanceof Set`. A Set built in another realm — the vm
  // sandbox the tests run data.js in, and equally an iframe or a worker — is not
  // an instance of THIS realm's Set, so `instanceof` silently treats it as "no
  // exclusions" and the swap does nothing. Caught by test-route-plan case 23f.
  const excludeIds = (exclude && typeof exclude.has === 'function')
    ? exclude
    : new Set((Array.isArray(exclude) ? exclude : []).map((id) => String(id)));
  const excluded = {
    unknownFee: 0, completed: 0, ineligible: 0, noPoints: 0, duplicate: 0, swapped: 0,
  };

  // How much INDIRECT credit she may still claim this cycle.
  //
  // CNE_FACTS.indirectCap is a per-cycle ceiling on points from programmes only
  // indirectly related to nursing. The correct constraint is the REMAINING
  // allowance, not the raw cap: a nurse who has already logged 4 indirect points
  // may add 1, not 5.
  //
  // Records carry no direct/indirect marker today, so already-earned indirect
  // points read as 0 and the full allowance is available. That is the right
  // failure direction — it never blocks a course she could legitimately take —
  // and it tightens automatically if records ever carry the marker.
  const indirectCap = (typeof CNE_FACTS === 'object' && Number(CNE_FACTS?.indirectCap)) || 5;

  // Programmes she has already completed inside this cycle. Compared by REPEAT
  // KEY, never by course id — the same BLS programme is 14 catalogue rows.
  // ‼️ A RECORD IS NOT SHAPED LIKE A COURSE ROW. getRecords (db.js) normalises
  // to { title: {en,zh}, provider: {en,zh}, courseId, date, points, notes } —
  // there is no `title` string and no `raw`. An earlier version read
  // `record.title` as a string, which silently produced "[object Object]" keys
  // and matched nothing, so de-duplication never fired on real data. Two
  // matchers now, strongest first:
  //   1. courseId — an exact link, present whenever the record was logged from
  //      the catalogue rather than typed by hand.
  //   2. repeat key from provider + title — for hand-entered records, and for
  //      catalogue rows re-listed under a new id (the BLS case: one programme,
  //      21 rows, 21 ids).
  const doneCourseIds = new Set();
  const done = new Set();
  let indirectEarned = 0;
  const cycle = cycleRecords(records || [], lic.cycleStart, lic.renewalDate, today);
  for (const record of cycle) {
    if (record?.courseId) doneCourseIds.add(String(record.courseId));
    // A record already carries {en,zh} for both fields, so it can go straight
    // into courseRepeatKey — the same function the catalogue side uses, which is
    // what makes the two keys comparable at all.
    if (record?.title) done.add(courseRepeatKey(record));
    if ((record?.cneRelation || record?.raw?.cne_relation) === 'indirect') {
      indirectEarned += Number(record.points) || 0;
    }
  }
  const indirectAllowance = Math.max(0, indirectCap - indirectEarned);

  const seen = new Map();          // repeat key → best candidate for that programme
  for (const course of (courses || [])) {
    // Swapped away by the caller ("換一個"). First, so a course the nurse has
    // just rejected cannot come back through any later branch.
    if (excludeIds.size && excludeIds.has(String(course.id))) { excluded.swapped++; continue; }
    if (!courseIsRouteEligible(course, { profile, windowEnd, today })) { excluded.ineligible++; continue; }
    const points = Number(course.points);
    if (!(points > 0)) { excluded.noPoints++; continue; }

    const key = courseRepeatKey(course);
    // Already earned this programme's points this cycle. Only a course KNOWN to
    // be repeatable may be recommended again — unknown stays excluded, because
    // recommending a re-take that cannot be claimed costs her the fee.
    const repeatability = course.raw?.repeatability || null;
    const alreadyDone = doneCourseIds.has(String(course.id)) || (key && done.has(key));
    if (alreadyDone && repeatability !== 'repeatable') { excluded.completed++; continue; }

    const fee = applicableFee(course, profile);
    if (fee.amount == null) { excluded.unknownFee++; continue; }

    // The indirect-CNE cap (CNE_FACTS.indirectCap, 5 points/cycle) applies to
    // programmes only INDIRECTLY related to nursing.
    //
    // ‼️ UNCLASSIFIED IS NOT INDIRECT. The Council has not published a
    // per-course direct/indirect determination, so demanding one before a course
    // may be planned would demand something that does not exist — and would
    // leave the planner permanently empty. An unclassified course therefore
    // enters the route unconstrained, exactly as a nurse choosing for herself
    // would treat it.
    //
    // What the cap DOES bind is a course we positively know to be indirect: those
    // enter as capped candidates and the solver may not take more than the
    // remaining allowance of them. So the field costs nothing while it is empty
    // and starts working the moment anything is classified — the opposite of a
    // hard gate.
    //
    // The honest consequence, which the UI must carry: a plan built from
    // unclassified courses can be described as "reaches N points", never as
    // "completes your requirement".
    const isIndirect = course.raw?.cne_relation === 'indirect';

    const candidate = {
      course, points, cost: fee.amount, costCertain: fee.certain,
      online: !!course.online, key, isIndirect,
      relevance: 0,
    };
    // One listing per programme: three sittings of the same BLS are one option,
    // not three, and letting all three into the solver would let it "cover" a
    // gap by booking the same course three times.
    const held = key ? seen.get(key) : null;
    if (!held) { if (key) seen.set(key, candidate); else seen.set(Symbol('nokey'), candidate); }
    else {
      excluded.duplicate++;
      if (candidate.cost < held.cost) seen.set(key, candidate);
    }
  }

  const candidates = [...seen.values()];
  // CLAIMABLE points, not listed points: indirect credit beyond the remaining
  // ceiling is not capacity she can actually use, so counting it would overstate
  // coverage and let `routeComplete` claim a gap is closable when it is not.
  const available = _capIndirect(candidates, indirectAllowance)
    .reduce((sum, c) => sum + c.points, 0);
  const covered = Math.min(gap, available);
  const routeComplete = available + 1e-9 >= gap;

  const build = (items, compare) => {
    // When even everything available cannot close the gap, the honest "route" is
    // everything available — there is no cheaper subset of a set that is already
    // insufficient. routeComplete tells the UI to say so.
    //
    // The partial case still has to respect the indirect ceiling, or a plan that
    // cannot close the gap would ALSO be one she cannot fully claim.
    const chosen = routeComplete
      ? _solveRoute(items, gap, compare, indirectAllowance)
      : _capIndirect(items, indirectAllowance);
    if (!chosen || !chosen.length) return null;
    const cost = chosen.reduce((sum, c) => sum + c.cost, 0);
    const points = chosen.reduce((sum, c) => sum + c.points, 0);
    return {
      courses: chosen.map((c) => c.course),
      items: chosen,
      cost,
      points,
      count: chosen.length,
      // ONE uncertain price makes the whole total uncertain — the label has to
      // read "from HK$X", and the plan must not be called "cheapest".
      costCertain: chosen.every((c) => c.costCertain),
      complete: points + 1e-9 >= gap,
    };
  };

  const cheapest = build(candidates, _byCheapest);
  const fewestCourses = build(candidates, _byFewest);
  const onlineItems = candidates.filter((c) => c.online);
  // An all-online plan is only offered when online courses alone can close the
  // gap; a partial one would be a different promise than its label makes.
  const onlineFirst = onlineItems.reduce((sum, c) => sum + c.points, 0) + 1e-9 >= gap
    ? build(onlineItems, _byCheapest)
    : null;

  // ── The preference route ──────────────────────────────────────────────────
  // ‼️ AN EXTRA PLAN, NEVER A FILTER ON THE POOL. `cheapest` always survives, so
  // a preference can narrow what she is OFFERED but can never narrow what she is
  // ABLE to see — which is the difference between a helpful preference and one
  // that quietly makes the planner useless.
  //
  // When the preferred subset cannot close the gap we return null AND say what
  // relaxing each preference would buy. Telling her "只要網上＋週末 最多得
  // 22.5 分，放寬週末 → 211 分" is a decision she can act on; silently shrinking
  // her route, or showing an empty list, is not.
  const coursePrefs = profile && profile.prefs && profile.prefs.coursePrefs;
  const prefsActive = !!(coursePrefs
    && (coursePrefs.mode || (Array.isArray(coursePrefs.timing) && coursePrefs.timing.length)));
  let preferred = null;
  let preferredInfo = null;
  if (prefsActive) {
    const pool = candidates.filter((item) => _matchesPrefs(item, coursePrefs));
    const reach = (list) => _capIndirect(list, indirectAllowance)
      .reduce((sum, c) => sum + c.points, 0);
    const availablePoints = reach(pool);
    if (availablePoints + 1e-9 >= gap) {
      preferred = build(pool, _byCheapest);
    }
    if (!preferred) {
      // What would dropping ONE preference buy? Only relaxations that actually
      // help are worth showing.
      const relaxations = [];
      if (coursePrefs.mode) {
        const p = reach(candidates.filter((i) => _matchesPrefs(i, { ...coursePrefs, mode: null })));
        if (p > availablePoints) relaxations.push({ drop: 'mode', availablePoints: Math.round(p * 100) / 100 });
      }
      for (const t of (Array.isArray(coursePrefs.timing) ? coursePrefs.timing : [])) {
        const rest = coursePrefs.timing.filter((v) => v !== t);
        const p = reach(candidates.filter((i) => _matchesPrefs(i, { ...coursePrefs, timing: rest })));
        if (p > availablePoints) relaxations.push({ drop: t, availablePoints: Math.round(p * 100) / 100 });
      }
      preferredInfo = {
        applied: [coursePrefs.mode, ...(coursePrefs.timing || [])].filter(Boolean),
        availablePoints: Math.round(availablePoints * 100) / 100,
        relaxations: relaxations.sort((a, b) => b.availablePoints - a.availablePoints),
      };
    }
  }

  return {
    gap,
    window: progress.window,
    required: progress.required,
    earned: progress.earned,
    // ‼️ HOW MANY RECORDS THIS CYCLE ACTUALLY HAS — the difference between
    // "she is 45 points short" and "we have no idea where she stands".
    //
    // Measured on live data 2026-08-09: 145 of 190 onboarded nurses (76%) have
    // logged NOTHING, and 136 of those accounts are 31–90 days old — so they are
    // not new users who genuinely have zero points, they are users who have not
    // told us anything. For them `gap` equals the whole requirement, and a card
    // reading "你尚欠 45 分 · HK$5,970" is either flatly wrong (she did the CNE
    // and never logged it) or alarming (she has not started and is being handed
    // a bill). Neither is a thing to say to someone with no data on file.
    //
    // We deliberately do NOT ask her for a starting number: PointsStep was
    // removed from onboarding on purpose (onboarding.jsx:151 — "cycle progress
    // is derived entirely from records the user adds one by one"), and a
    // self-reported figure would flow into the NCHK export as an unevidenced
    // baseline. So the honest move is to say we don't know, not to guess and
    // not to ask. Callers MUST branch on this before showing a gap or a price.
    cycleRecordCount: cycle.length,
    plans: { cheapest, fewestCourses, onlineFirst, preferred },
    // Present ONLY when preferences are set and could not cover the gap. Carries
    // what they can reach and what relaxing each one would buy, so the UI can
    // offer a trade-off instead of an empty list.
    preferredInfo,
    coveragePoints: Math.round(covered * 100) / 100,
    uncoveredGap: Math.round(Math.max(0, gap - available) * 100) / 100,
    routeComplete,
    // 'conditional' whenever any chosen price depends on a membership we cannot
    // confirm. The UI MUST NOT print "最平 / cheapest" in that state.
    costCertainty: cheapest && cheapest.costCertain ? 'confirmed' : 'conditional',
    // Eligible courses the cheapest plan did NOT take, best value first. This is
    // what backs an honest "other options" comparison: every row is a real,
    // tappable listing, so a saving can be shown against something she can
    // actually open — never against a modelled average.
    considerations: (() => {
      const inPlan = new Set((cheapest?.items || []).map((item) => item.course.id));
      return candidates
        .filter((item) => !inPlan.has(item.course.id))
        .sort((a, b) => (a.cost / a.points) - (b.cost / b.points))
        .map((item) => ({
          course: item.course, points: item.points,
          cost: item.cost, costCertain: item.costCertain,
        }));
    })(),
    excluded,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Licence lifecycle — derived states (no stored status column)
// ─────────────────────────────────────────────────────────────────────────────
// Every state is COMPUTED from the stored dates at read time, so it can never
// drift from them. The one stored bit is prefs.dates_confirmed_at (set by the
// "日期正確" hygiene CTA), which retires the default_dates state for a profile
// whose seed dates happen to be real.
//
// ONBOARDING_SEED_* mirror onboarding.jsx initialForm's fallback cycle dates.
// 27 live profiles (2026-07-10) still carry this exact untouched pair — they
// must see "confirm your real date", and the notify-cron sweeps must skip
// them, or the seed date fires false renewal alarms in Dec 2026.
const ONBOARDING_SEED_START = '2024-01-01';
const ONBOARDING_SEED_END   = '2026-12-31';
function profileHasSeedDates(profile){
  const p = profile || {};
  return p.cycle_start === ONBOARDING_SEED_START &&
         p.renewal_date === ONBOARDING_SEED_END &&
         !(p.prefs && p.prefs.dates_confirmed_at);
}
// State for ONE licence descriptor (from profileLicenses). `profile` is needed
// because default_dates is a property of the primary flat columns + prefs.
//   'none'          no renewal date — cycle never set up
//   'default_dates' untouched onboarding seed pair, unconfirmed. Checked BEFORE
//                   expired: after 2027-01-01 the seed rows are both, and must
//                   get the confirm-date prompt, never the "已續期？" rollover.
//   'expired'       today past the renewal date → rollover prompt
//   'processing'    CNE window closed, renewal pending (last 6 months)
//   'active'        inside the CNE earning window
function licenseLifecycleState(lic, profile, today){
  const now = _axisToday(today);   // local date, never UTC — see todayIso
  if (!lic || !lic.renewalDate) return 'none';
  if (lic.key === 'primary' && profileHasSeedDates(profile)) return 'default_dates';
  if (now > lic.renewalDate) return 'expired';
  const end = cneWindow(lic.cycleStart || null, lic.renewalDate).end;
  if (end && now > end) return 'processing';
  return 'active';
}
// Prefill for the rollover sheet: the day after the old expiry — then, for
// multi-cycle-stale profiles (live data has renewal dates back to 2002, i.e.
// nurses who entered their ORIGINAL registration period), walk forward whole
// 3-year blocks until today falls inside [start, end], so the prefill lands on
// the certificate period the nurse is actually in. The user can still adjust.
function nextCycleStartFrom(prevRenewalIso, today){
  if (!prevRenewalIso) return null;
  const now = _axisToday(today);   // local date, never UTC — see todayIso
  let start = shiftDays(prevRenewalIso, 1);
  let guard = 0;
  while (pcEndFromStart(start) < now && guard++ < 40) {
    start = shiftDays(pcEndFromStart(start), 1);
  }
  return start;
}

// ─────────────────────────────────────────────────────────────────────────────
// Widget snapshot — the ONLY data contract shared with the iOS WidgetKit target
// ─────────────────────────────────────────────────────────────────────────────
// This stays deliberately small and contains no person-identifying text. In
// particular, DO NOT add a licence's `role`, `licenseNo`, profile name, hospital
// or any record title/provider/certificate name here: widgets can render on the
// lock screen. The native target owns fixed "Licence 1 / 第一註冊" labels.
//
// Keep this pure and deterministic for a supplied `today`. WidgetAPI dedupes its
// JSON output to prevent unrelated React re-renders from spending a WidgetCenter
// reload. `updatedAt` belongs to the bridge call, never this payload.
function buildWidgetSnapshot({ profile, records, lang, today } = {}){
  const safeLang = lang === 'en' ? 'en' : 'zh';
  if (!profile) {
    return { schema: 1, state: 'signedOut', lang: safeLang, focus: 0, licenses: [] };
  }

  const licenses = profileLicenses(profile).map((lic, index) => {
    const progress = licenseProgress(records || [], lic, today);
    const required = progress.required;
    return {
      // Never derive this from the editable role label: the enum is safe for a
      // lock-screen payload and remains stable if the nurse changes her label.
      slot: index === 0 ? 'primary' : 'secondary',
      lifecycle: licenseLifecycleState(lic, profile, today),
      exempt: required === 0,
      earned: progress.earned,
      required,
      pct: required > 0 ? Math.round((progress.earned / required) * 100) : null,
      // Method B derives its dates from the expiry. Do not substitute
      // lic.cycleStart here: an irregular first PC may have a different start.
      cycleStart: progress.window?.start || null,
      cycleEnd: progress.window?.end || null,
      renewalDate: lic.renewalDate || null,
    };
  });

  // A one-slot widget should show the nearest real CNE obligation. Exempt,
  // unconfigured and expired licences must never displace an active deadline.
  const candidates = licenses
    .map((lic, index) => ({ lic, index }))
    .filter(({ lic }) => (lic.lifecycle === 'active' || lic.lifecycle === 'processing') &&
      lic.required > 0 && !!lic.cycleEnd)
    .sort((a, b) => String(a.lic.cycleEnd).localeCompare(String(b.lic.cycleEnd)));
  const focus = candidates.length ? candidates[0].index : 0;

  return { schema: 1, state: 'ok', lang: safeLang, focus, licenses };
}

// Back-compat alias for old call sites
function isFull(s){ return /full|已滿|closed|ended/i.test(String(s||'')); }

// ─────────────────────────────────────────────────────────────────
// enrolmentStatusInfo — collapses (enrollment_status, seats, deadline, ended)
// into a single visual descriptor. Single source of truth for badge colour,
// label, and which CTA is appropriate.
//
// Returns:
//   {
//     kind: 'open' | 'limited' | 'waitlist' | 'unknown' | 'full' | 'closed'
//         | 'ended' | 'cancelled' | 'soon' | 'archived' | 'withdrawn' | null,
//     label: { en, zh },
//     tone:  'success' | 'warning' | 'danger' | 'muted',
//     showCta: boolean,
//   }
// ─────────────────────────────────────────────────────────────────
function enrolmentStatusInfo(course, today) {
  if (!course) return null;
  const now = _axisToday(today);
  const axes = deriveCourseAxes(course, now);

  // 0) Lifecycle gate — non-active courses are not bookable regardless of
  // date. Distinguishes 'archived' (provider withdrew it) from
  // 'rejected_low_confidence' (scraper flagged the data as unreliable) so
  // detail UI can be honest about why the Register CTA is hidden.
  if (course.lifecycleStatus && course.lifecycleStatus !== 'active') {
    if (course.lifecycleStatus === 'archived') {
      return { kind: 'archived',
               label: { en: 'No longer offered', zh: '不再提供' },
               tone: 'muted', showCta: false };
    }
    return { kind: 'withdrawn',
             label: { en: 'Unavailable', zh: '資料不可信' },
             tone: 'muted', showCta: false };
  }

  // 1) Event state overrides registration evidence.
  if (axes.eventStatus === 'cancelled') {
    return { kind: 'cancelled', label: STR.cancelledStatus, tone: 'danger', showCta: false };
  }
  if (axes.eventStatus === 'completed') {
    return { kind: 'ended', label: { en: 'Ended', zh: '已結束' }, tone: 'muted', showCta: false };
  }

  const s = (course.status || '') + ' ' + (course.enrollmentStatus?.en || '') + ' ' + (course.enrollmentStatus?.zh || '');

  // 2) Opening-soon is a display refinement of registrationStatus=closed.
  // ‼️ Shares RE_NOT_YET_OPEN with deriveRegistrationStatus ON PURPOSE. These
  // two functions parse the SAME text, and when each carried its own copy of the
  // phrase list they drifted: a value this list misses still resolves 'closed'
  // upstream and would render 「報名已截止」 over a course that has not opened yet
  // — trading one reverse fact for another. One vocabulary, two readers.
  if (RE_NOT_YET_OPEN.test(s)) {
    return { kind: 'soon', label: STR.openingSoon, tone: 'warning', showCta: false };
  }
  if (axes.registrationStatus === 'closed') {
    const deadlinePassed = course.registrationDeadline
      && String(course.registrationDeadline).slice(0, 10) < String(now).slice(0, 10);
    return { kind: 'closed', label: deadlinePassed ? STR.enrollmentEnded : STR.closed, tone: 'muted', showCta: false };
  }
  if (axes.registrationStatus === 'waitlist') {
    return { kind: 'waitlist', label: STR.waitlist, tone: 'warning', showCta: true };
  }
  if (axes.registrationStatus === 'full') {
    return { kind: 'full', label: STR.full, tone: 'danger', showCta: false };
  }
  if (axes.registrationStatus === 'open') {
    return course.seats?.kind === 'limited'
      ? { kind: 'limited', label: STR.open, tone: 'warning', showCta: true }
      : { kind: 'open', label: STR.open, tone: 'success', showCta: true };
  }
  // Unknown = no positive open/closed/full evidence, but the course is on a
  // bookable date (scheduled/ongoing, active, deadline not passed — every
  // non-bookable state returned earlier above). Don't dead-end the nurse on
  // missing data: show the Register CTA so she can try the provider page (which
  // shows the real status; RegisterDialog handles a missing URL). The muted
  // 待確認 badge stays visible elsewhere, so 報名 + 待確認 read honestly.
  // NB showCta:true here also lets a deadline pill appear for these rows within
  // 14d (components.jsx showDeadline); that is intended/informative. Do NOT set
  // this false again to "hide uncertain rows" — the "只顯示可報名" toggle filters
  // on deriveRegistrationStatus, not showCta, so it still excludes unknown.
  return { kind: 'unknown', label: STR.statusUnverified, tone: 'muted', showCta: true };
}

// Days until a deadline (negative if past).
function daysUntil(iso, today) {
  if (!iso) return null;
  // _axisToday, never toISOString — see the guard on todayIso. This feeds the
  // card deadline pill and the closing-soon strip, so a UTC "today" kept
  // yesterday's expired deadlines on screen until 08:00 HKT.
  return daysBetween(_axisToday(today), iso);
}

window.enrolmentStatusInfo = enrolmentStatusInfo;
window.daysUntil = daysUntil;

// ─────────────────────────────────────────────────────────────────────────────
// NCHK CNE record export — Annex A (個人持續護理進修記錄) + Annex B (聲明表格).
// Renders the official two-part Nursing Council form as a self-contained HTML
// document and hands it to the OS print pipeline (Save to PDF / Files). Works in
// the browser (hidden iframe + window.print()) and inside the iOS WKWebView via
// the native Printer plugin — no PDF library, one source of truth for the
// layout. buildNchkHtml() owns the markup; printNchkHtml() owns dispatch.
//
// NCHK_NATURE_OPTIONS — presets for Annex A's 活動性質 (nature) column. The app's
// internal category (core/elective/specialty) is a DIFFERENT taxonomy from the
// Council's nature-of-activity, so there's no faithful auto-map: every row
// defaults to `course` and the user adjusts it per-row in the export preview.
// `other` swaps in a free-text value (natureOther).
// ─────────────────────────────────────────────────────────────────────────────
const NCHK_NATURE_OPTIONS = [
  { id: 'course',    en: 'Educational activity (course)',             zh: '教育活動(教育課程)' },
  { id: 'seminar',   en: 'Educational activity (conference/seminar)', zh: '教育活動(專業研討會)' },
  { id: 'workshop',  en: 'Educational activity (workshop)',           zh: '教育活動(工作坊)' },
  { id: 'selfstudy', en: 'Self-study activity',                       zh: '自學活動' },
  { id: 'other',     en: 'Other (specify)',                           zh: '其他(請註明)' },
];
function nchkNatureLabel(id, lang) {
  const o = NCHK_NATURE_OPTIONS.find(x => x.id === id);
  return o ? L(o, lang) : '';
}

// Map cycle-filtered records → Annex A rows. `from` (參加日期 由) is parsed from
// the START: marker the log sheet stores in notes for multi-day courses; absent
// → blank (single-day or unknown). `to` (至) is the completion date. The caller
// passes records ALREADY filtered to the CNE earning window via cycleRecords().
function nchkAnnexARows(records, lang) {
  return (records || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    .map(r => {
      const m = String(r.notes || '').match(/^START:(\d{4}-\d{2}-\d{2})/m);
      // NATURE:<id>[|free text] — the user's per-row 活動性質 choice, persisted
      // by buildNoteBlob (record-extras.jsx). Absent → 'course', the historical
      // default. Before this was read back, every export reset the column.
      const nat = String(r.notes || '').match(/^NATURE:([a-z]+)(?:\|(.*))?$/m);
      const natureId = nat && NCHK_NATURE_OPTIONS.some(o => o.id === nat[1]) ? nat[1] : 'course';
      return {
        // UI-only join key: lets the audit-pack builder find this exact
        // record's evidence after the nurse has reviewed/reordered Annex A.
        // It is stripped before the official form is generated.
        recordId: r.id || null,
        nature: natureId,
        natureOther: natureId === 'other' ? ((nat && nat[2]) || '') : '',
        activity: L(r.title, lang),
        from: m ? m[1] : '',
        to: r.date || '',
        points: Number(r.points) || 0,
        org: L(r.provider, lang),
      };
    });
}

// Annex B declaration identity + dates. PC validity = the raw cycle_start /
// renewal_date (those columns ARE the practising-certificate window); the
// declared CNE period is that window anchored on the expiry via cneWindow()
// (end = renewal_date − 6mo) — the same earning window cycleRecords() buckets
// on. PII (tel / address / employer)
// is intentionally NOT collected: left blank on the form for the nurse to write.
function nchkAnnexB({ profile, user, lang } = {}) {
  const p = profile || {};
  const pcFrom = (user && user.cycleStart) || p.cycle_start || '';
  const pcTo   = (user && user.renewalDate) || p.renewal_date || '';
  const { start: cneFrom, end: cneTo } = cneWindow(pcFrom || null, pcTo || null);
  return {
    nameEn: (p.name_en && p.name_en.trim()) || (p.display_name && p.display_name.trim()) || '',
    nameZh: (p.name_zh && p.name_zh.trim()) || '',
    regNo:  (p.license_no != null ? String(p.license_no).trim() : ''),
    pcFrom, pcTo,
    cneFrom: cneFrom || '',
    cneTo:   cneTo || '',
  };
}

// buildNchkHtml — the self-contained Annex A + B document. `blank: true` emits an
// empty printable template (≈12 ruled rows, all-blank declaration). The declared
// total is ALWAYS the sum of the Annex A rows' points, so the table footer and
// the declaration sentence can never disagree. Labels are bilingual regardless
// of `lang`; only values follow `lang`. Fonts: device system stack, TC-first
// (PingFang TC ships on iOS) — embedding Noto Sans TC base64 would bloat every
// doc for no on-device gain (the OFL-embedding rule is for CI/redistributable
// assets, not a PDF the user renders locally from their own data).
function buildNchkHtml({ annexA, annexB, lang, blank, titleSuffix, evidence } = {}) {
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const fmt = (iso) => fmtDate(iso, lang);                 // raw (unescaped) date
  const NB = '&nbsp;';
  const b = annexB || {};

  const dataRows = blank ? new Array(12).fill(null) : (annexA || []);
  const total = blank ? null : dataRows.reduce((s, r) => s + (Number(r && r.points) || 0), 0);
  const bodyHtml = dataRows.map((r, i) => {
    const nature = r ? (r.nature === 'other' ? (r.natureOther || '') : nchkNatureLabel(r.nature, lang)) : '';
    return `<tr>
      <td class="c">${i + 1}</td>
      <td>${r ? esc(nature) : NB}</td>
      <td>${r ? esc(r.activity) : NB}</td>
      <td class="c nw">${r ? esc(fmt(r.from)) : NB}</td>
      <td class="c nw">${r ? esc(fmt(r.to)) : NB}</td>
      <td class="c">${r ? esc(r.points) : NB}</td>
      <td>${r ? esc(r.org) : NB}</td>
    </tr>`;
  }).join('');

  // Inline ruled blank (or value) used in the declaration sentence.
  const v = (raw) => `<span class="v">${raw ? esc(raw) : NB}</span>`;
  const pcFrom = blank ? '' : b.pcFrom, pcTo = blank ? '' : b.pcTo;
  const cneFrom = blank ? '' : b.cneFrom, cneTo = blank ? '' : b.cneTo;
  const totalStr = total != null ? String(total) : '';

  const declEn = `I hereby declare that I have obtained ${v(totalStr)} CNE Points accredited by the Nursing Council of Hong Kong during the period from ${v(fmt(cneFrom))} to ${v(fmt(cneTo))}. I understand that the Nursing Council of Hong Kong may require me to provide records and supporting documents in relation to the accredited CNE Points I declare to have obtained in this declaration.`;
  const declZh = `本人現聲明本人於 ${v(fmt(cneFrom))} 至 ${v(fmt(cneTo))} 期間，已取得獲香港護士管理局認可的持續護理教育學分共 ${v(totalStr)} 分。本人明白香港護士管理局可要求本人，就有關本聲明中提及已取得的持續護理教育學分，提供紀錄及證明文件。`;

  const pcLine = `${esc(fmt(pcFrom)) || '________'} ${lang === 'zh' ? '至' : 'to'} ${esc(fmt(pcTo)) || '________'}`;
  // Audit-pack evidence is deliberately an appendix to the official free Annex
  // A+B form. Existing rows/exports do not change when `evidence` is absent.
  const evidenceItems = !blank && Array.isArray(evidence?.items) ? evidence.items : [];
  const skippedPdf = !blank ? Number(evidence?.skippedPdf || 0) : 0;
  const evidenceHtml = evidenceItems.length || skippedPdf ? `
  <section class="annex annex-evidence">
    <div class="annexlabel">${lang === 'zh' ? '證書附頁 · Audit Pack' : 'Audit Pack · Certificate appendix'}</div>
    <div class="doctitle">${lang === 'zh' ? '證書附頁' : 'Certificate appendix'}</div>
    <div class="docsub">${lang === 'zh' ? '每張圖片已對應附件 A 的行號' : 'Each image is matched to its Annex A line'}</div>
    ${evidenceItems.map(item => `<div class="evidence-item">
      <div class="evidence-title">#${esc(item.line)} · ${esc(item.activity || '')}</div>
      <img src="${esc(item.dataUrl)}" alt="${esc(item.activity || 'Certificate evidence')}" />
    </div>`).join('')}
    ${skippedPdf ? `<div class="note">${lang === 'zh'
      ? `${skippedPdf} 張 PDF 證書未能併入，請另行附上。`
      : `${skippedPdf} PDF certificate${skippedPdf === 1 ? '' : 's'} could not be merged; please attach separately.`}</div>` : ''}
  </section>` : '';

  return `<!doctype html><html lang="${lang === 'zh' ? 'zh-Hant' : 'en'}"><head>
<meta charset="utf-8">
<title>${esc('NCHK CNE Record' + (titleSuffix ? ' — ' + titleSuffix : ''))}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "PingFang HK", "PingFang TC", "Microsoft JhengHei", "Heiti TC", "Noto Sans TC", "Helvetica Neue", sans-serif; color: #000; margin: 0; font-size: 11.5px; line-height: 1.4; }
  .annex { page-break-inside: auto; }
  .annex-b { page-break-before: always; }
  .doctitle { text-align: center; font-size: 16px; font-weight: 700; margin: 0 0 2px; }
  .docsub { text-align: center; font-size: 12px; margin: 0 0 12px; }
  .annexlabel { text-align: right; font-size: 10px; color: #444; margin: 0 0 6px; }
  table.idrow { width: 100%; border-collapse: collapse; margin: 0 0 8px; }
  table.idrow td { padding: 3px 4px; vertical-align: bottom; }
  .idlabel { white-space: nowrap; width: 1%; font-weight: 600; }
  .idline { border-bottom: 1px solid #000; }
  table.nchk { width: 100%; border-collapse: collapse; }
  table.nchk th, table.nchk td { border: 1px solid #000; padding: 5px 6px; vertical-align: top; }
  table.nchk thead { display: table-header-group; }   /* repeat the column header on every page */
  table.nchk tfoot { display: table-footer-group; }
  table.nchk th { font-size: 10.5px; font-weight: 700; text-align: center; background: #f2f2f2;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Never split a row (or cell) across the page break. iOS Safari / WKWebView
     ignore the legacy page-break-inside on table rows, so set the modern
     break-inside too — together they keep tall rows whole and kill the empty
     sliver that appeared at the bottom of the page on multi-page exports. */
  table.nchk tr { break-inside: avoid; page-break-inside: avoid; }
  table.nchk td, table.nchk th { break-inside: avoid; }
  td.c { text-align: center; }
  td.nw { white-space: nowrap; }
  tfoot td { font-weight: 700; }
  .decl { margin: 4px 0 12px; text-align: justify; }
  .v { display: inline-block; min-width: 80px; border-bottom: 1px solid #000; padding: 0 4px; text-align: center; }
  table.fields { width: 100%; border-collapse: collapse; margin-top: 4px; }
  table.fields td { padding: 7px 4px; vertical-align: bottom; }
  .flabel { white-space: nowrap; width: 1%; font-weight: 600; padding-right: 12px; }
  .fval { border-bottom: 1px solid #000; min-width: 200px; }
  .sigbox { height: 52px; border: 1px solid #000; }
  .note { margin-top: 14px; font-size: 9.5px; color: #555; }
  .annex-evidence { page-break-before: always; }
  .evidence-item { break-before: page; page-break-before: always; }
  .evidence-item:first-of-type { break-before: auto; page-break-before: auto; }
  .evidence-title { font-size: 12px; font-weight: 700; margin: 0 0 8px; }
  .evidence-item img { display: block; width: 100%; max-height: 255mm; object-fit: contain; object-position: top left; }
</style></head><body>

  <section class="annex annex-a">
    <div class="annexlabel">${lang === 'zh' ? '附件 A · Annex A' : 'Annex A · 附件 A'}</div>
    <div class="doctitle">個人持續護理進修記錄</div>
    <div class="docsub">Personal Record of Continuing Nursing Education</div>
    <table class="idrow"><tr>
      <td class="idlabel">護士姓名 Name of Nurse:</td>
      <td class="idline">${blank ? NB : (esc(b.nameEn) || NB)}</td>
      <td class="idlabel" style="padding-left:14px;">註冊／登記編號 Reg./Enrol. No.:</td>
      <td class="idline">${blank ? NB : (esc(b.regNo) || NB)}</td>
    </tr></table>
    <table class="nchk">
      <thead>
        <tr>
          <th>項<br>No.</th>
          <th>持續護理教育活動性質<br>Nature of CNE activity</th>
          <th>持續護理教育活動<br>CNE activity</th>
          <th>參加日期 由<br>Date from</th>
          <th>參加日期 至<br>Date to</th>
          <th>所獲學分<br>CNE Points</th>
          <th>舉辦機構<br>Organising body</th>
        </tr>
      </thead>
      <tbody>${bodyHtml}</tbody>
      ${blank ? '' : `<tfoot><tr>
        <td class="c" colspan="5">合計 Total</td>
        <td class="c">${esc(total)}</td>
        <td></td>
      </tr></tfoot>`}
    </table>
  </section>

  <section class="annex annex-b">
    <div class="annexlabel">${lang === 'zh' ? '附件 B · Annex B' : 'Annex B · 附件 B'}</div>
    <div class="doctitle">聲明表格</div>
    <div class="docsub">Declaration Form</div>
    <p class="decl">${declEn}</p>
    <p class="decl">${declZh}</p>
    <table class="fields">
      <tr><td class="flabel">簽署<br>Signature:</td><td><div class="sigbox"></div></td></tr>
      <tr><td class="flabel">姓名 Name<br>(英文 English):</td><td class="fval">${blank ? NB : (esc(b.nameEn) || NB)}</td></tr>
      <tr><td class="flabel">姓名 Name<br>(中文 Chinese):</td><td class="fval">${blank ? NB : (esc(b.nameZh) || NB)}</td></tr>
      <tr><td class="flabel">登記／註冊編號<br>Enrolment / Registration No.:</td><td class="fval">${blank ? NB : (esc(b.regNo) || NB)}</td></tr>
      <tr><td class="flabel">上一張執業證明書有效期<br>Valid Period of the Last Practising Certificate:</td><td class="fval">${blank ? NB : pcLine}</td></tr>
      <tr><td class="flabel">聯絡電話<br>Contact Tel. No.:</td><td class="fval">${NB}</td></tr>
      <tr><td class="flabel">住址<br>Residential Address:</td><td class="fval">${NB}</td></tr>
      <tr><td class="flabel">現職僱主<br>Present Employer:</td><td class="fval">${NB}</td></tr>
      <tr><td class="flabel">日期<br>Date:</td><td class="fval">${NB}</td></tr>
    </table>
    <div class="note">＊ 請刪去不適用者 · Please delete where inappropriate</div>
  </section>

  ${evidenceHtml}

</body></html>`;
}

// printNchkHtml — shared dispatch. Native iOS: window.print() is a no-op in the
// WKWebView, so hand the HTML to the Printer plugin (WKWebView viewPrintFormatter
// → native Print / Save-to-PDF sheet). Web/desktop: print a hidden, laid-out
// iframe. Toasts on failure so a mis-registered plugin is loud, not silent.
function printNchkHtml(html, lang) {
  const isZh = lang === 'zh';
  const toast = (msg) => {
    const t = document.createElement('div');
    t.className = 'vital-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 1800);
  };

  if (globalThis.Capacitor?.isNativePlatform?.()) {
    try {
      const Printer = globalThis.Capacitor.registerPlugin('Printer');
      Printer.print({ html }).catch((e) => {
        console.warn('printNchkHtml native print failed', e);
        toast(isZh ? '匯出失敗，請再試' : 'Export failed — please try again');
      });
    } catch (e) {
      console.warn('printNchkHtml native print threw', e);
      toast(isZh ? '匯出失敗，請再試' : 'Export failed — please try again');
    }
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Off-screen but laid out at A4 px: WebKit has historically printed blank for
  // degenerate/hidden iframes, so keep it sized and just out of sight.
  Object.assign(iframe.style, {
    position: 'fixed', left: '-10000px', top: '0',
    width: '794px', height: '1123px', border: '0',
    opacity: '0', pointerEvents: 'none',
  });
  document.body.appendChild(iframe);

  let cleaned = false;
  const cleanup = () => { if (cleaned) return; cleaned = true; try { iframe.remove(); } catch (_) {} };
  const fire = () => {
    const win = iframe.contentWindow;
    if (!win || typeof win.print !== 'function') { toast(isZh ? '匯出失敗，請再試' : 'Export failed — please try again'); cleanup(); return; }
    try {
      win.focus();
      win.onafterprint = cleanup;
      win.print();
    } catch (e) {
      console.warn('printNchkHtml print failed', e);
      toast(isZh ? '匯出失敗，請再試' : 'Export failed — please try again');
      cleanup();
      return;
    }
    setTimeout(cleanup, 60000); // afterprint never fires on some iOS WebViews
  };

  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  if (doc.readyState === 'complete') setTimeout(fire, 300);
  else iframe.onload = () => setTimeout(fire, 300);
}

// exportNchkPdf — build + print from already-normalized data. The in-app filled
// flow (NchkExportSheet) passes the user-reviewed annexA/annexB straight here so
// edits are PDF-only and never touch the DB. `blank: true` prints the template.
function exportNchkPdf({ annexA, annexB, lang, blank, titleSuffix, evidence } = {}) {
  printNchkHtml(buildNchkHtml({ annexA, annexB, lang, blank, titleSuffix, evidence }), lang);
}

function exportBlankNchkPdf({ lang } = {}) {
  exportNchkPdf({ lang, blank: true });
}

// exportRecordPdf — back-compat + non-preview convenience: build the FILLED NCHK
// PDF straight from the user's records, no edit step. The in-app export routes
// through NchkExportSheet (preview) instead, which calls exportNchkPdf with the
// reviewed rows. Records are bucketed by the CNE earning window (cycleRecords),
// NOT the raw PC dates — see the date-math block near fmtDate().
function exportRecordPdf({ user, profile, records, lang } = {}) {
  const rows = cycleRecords(records, user?.cycleStart || null, user?.renewalDate || null);
  if (rows.length === 0) {
    const t = document.createElement('div');
    t.className = 'vital-toast';
    t.textContent = L(STR.nchkEmptyRecords, lang);
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 1800);
    return;
  }
  const annexA = nchkAnnexARows(rows, lang);
  const annexB = nchkAnnexB({ profile, user, lang });
  exportNchkPdf({ annexA, annexB, lang });
}

// ReviewAPI — thin wrapper over the app-local `Review` Swift plugin, which
// surfaces Apple's in-app App Store rating prompt (SKStoreReviewController /
// StoreKit AppStore.requestReview). Native iOS only: on web/desktop
// isSupported() is false and requestReview() is a no-op, so callers don't have
// to branch on platform. Mirrors the inline registerPlugin('Printer') pattern.
//
// requestReview() is best-effort and gives NO signal about whether the OS
// actually showed the sheet (Apple throttles it to 3×/365d and respects the
// user's Settings opt-out). The decision of WHEN to ask — value moment +
// engagement + cooldown gating — lives in app.jsx maybeOfferReview(), not here.
const ReviewAPI = {
  isSupported() {
    try { return !!globalThis.Capacitor?.isNativePlatform?.(); }
    catch (_) { return false; }
  },
  async requestReview() {
    if (!this.isSupported()) return { requested: false };
    try {
      const Review = globalThis.Capacitor.registerPlugin('Review');
      return await Review.requestReview();
    } catch (e) {
      // "Plugin not implemented" here means the Swift plugin failed to
      // register — loud + named beats a silent miss (same as exportRecordPdf).
      console.warn('ReviewAPI.requestReview failed', e);
      return { requested: false };
    }
  },
};

// CalendarAPI — thin wrapper over the app-local `Calendar` Swift plugin
// (EKEventEditViewController). Mirrors ReviewAPI's shape.
//
// Native iOS presents the system Add-Event sheet, so the nurse sees and
// confirms the event before anything is written. On web there is no equivalent
// hand-off, so we fall back to downloading the .ics — which the desktop OS
// opens in the user's calendar app.
//
// Returns { result: 'saved' | 'cancelled' | 'downloaded' | 'denied' | 'failed' }.
// Callers MUST NOT report success on 'cancelled' — declining the sheet is a
// normal outcome, and a "added to your calendar" toast would be a lie.
const CalendarAPI = {
  isNative() {
    try { return !!globalThis.Capacitor?.isNativePlatform?.(); }
    catch (_) { return false; }
  },
  async addCourse(course, lang = 'zh') {
    if (!course || !course.date) return { result: 'failed' };

    if (this.isNative()) {
      try {
        const Calendar = globalThis.Capacitor.registerPlugin('Calendar');
        // Epoch SECONDS, built from local Y/M/D + H:M so the instant matches
        // the wall-clock time printed on the course page. Never Date.parse on
        // a bare 'YYYY-MM-DD' — that is UTC midnight.
        const range = _icsTimeRange(course.time || course.raw?.time_text);
        const [y, mo, d] = String(course.date).split('-').map(Number);
        const mk = (hhmm) => {
          const [h, mi] = hhmm ? hhmm.split(':').map(Number) : [0, 0];
          return new Date(y, mo - 1, d, h, mi, 0).getTime() / 1000;
        };
        const start = mk(range ? range.start : null);
        const end = range
          ? mk(range.end || `${String(Math.min(23, Number(range.start.split(':')[0]) + 2)).padStart(2, '0')}:00`)
          : start + 86400;
        const res = await Calendar.addEvent({
          title: L(course.title, lang),
          start, end,
          allDay: !range,
          location: L(course.venue, lang) || (course.online ? (lang === 'zh' ? '網上' : 'Online') : ''),
          notes: [L(course.providerShort, lang), course.registrationUrl].filter(Boolean).join('\n'),
        });
        return { result: res?.result || 'cancelled' };
      } catch (e) {
        if (String(e?.code || e?.message || '').includes('permission_denied')) {
          return { result: 'denied' };
        }
        console.warn('CalendarAPI.addCourse failed', e);
        return { result: 'failed' };
      }
    }

    // Web fallback — hand the OS a real .ics file.
    try {
      const ics = courseToIcs(course, lang);
      if (!ics) return { result: 'failed' };
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(L(course.title, lang) || 'course').replace(/[^\w一-鿿-]+/g, '_').slice(0, 60)}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return { result: 'downloaded' };
    } catch (e) {
      console.warn('CalendarAPI .ics download failed', e);
      return { result: 'failed' };
    }
  },
};

// WidgetAPI — thin wrapper over the app-local WidgetBridgePlugin. The plugin is
// deliberately resolved only at call time: `registerPlugin` returns a proxy even
// when a dev build has not yet added the native implementation, and each failed
// call must be contained instead of breaking a React effect.
const WidgetAPI = {
  _last: null,
  isNative() {
    try { return !!globalThis.Capacitor?.isNativePlatform?.(); }
    catch (_) { return false; }
  },
  async publish(snapshot) {
    if (!this.isNative()) return { ok: false, unsupported: true };
    let json;
    try { json = JSON.stringify(snapshot); }
    catch (e) {
      console.warn('WidgetAPI.publish failed', e);
      return { ok: false, error: 'serialize_failed' };
    }
    if (json === this._last) return { ok: true, deduped: true };
    try {
      const Widget = globalThis.Capacitor.registerPlugin('Widget');
      await Widget.publish({ json, updatedAt: new Date().toISOString() });
      this._last = json;
      return { ok: true };
    } catch (e) {
      console.warn('WidgetAPI.publish failed', e);
      return { ok: false, error: e?.message || 'publish_failed' };
    }
  },
  async clear() {
    if (!this.isNative()) return { ok: false, unsupported: true };
    try {
      const Widget = globalThis.Capacitor.registerPlugin('Widget');
      await Widget.clear();
      this._last = null;
      return { ok: true };
    } catch (e) {
      console.warn('WidgetAPI.clear failed', e);
      return { ok: false, error: e?.message || 'clear_failed' };
    }
  },
};

// PurchaseAPI — thin wrapper over RevenueCat's Capacitor plugin.
//
// ‼️ REQUIRES A NATIVE DEPENDENCY THAT IS NOT YET INSTALLED:
//     npm i @revenuecat/purchases-capacitor && npx cap sync ios
// Until that runs, isSupported() is false everywhere and every method is a
// no-op — the paywall renders an honest "not available yet" instead of
// throwing. Accessed via Capacitor.registerPlugin (the same pattern as
// Printer/Review/Calendar) rather than a static import, so the JS bundle keeps
// building with or without the package. See RELEASE.md §25.
//
// WHY REVENUECAT AND NOT HAND-ROLLED STOREKIT: the subscription state machine —
// proration, family sharing, refunds, billing retry, grace periods, restore
// across devices — is where hand-rolled implementations bleed, and it is not
// where this product differentiates. RevenueCat ships that plus server-side
// receipt validation and the webhook this app's iap-webhook consumes.
//
// ‼️ NOTHING HERE GRANTS ACCESS. A successful purchase only triggers
// DB.syncEntitlement(), which asks the SERVER to re-read the store. The server
// row (public.entitlements) is the only thing extract-certificate trusts.
const PurchaseAPI = {
  _plugin: null,
  _configured: false,
  isSupported() {
    try {
      if (!globalThis.Capacitor?.isNativePlatform?.()) return false;
      // No key configured → subscriptions are OFF, honestly and everywhere.
      if (!globalThis.REVENUECAT_PUBLIC_KEY) return false;
      return !!this._get();
    } catch (_) { return false; }
  },
  _get() {
    if (this._plugin) return this._plugin;
    try {
      const p = globalThis.Capacitor?.registerPlugin?.('Purchases');
      // registerPlugin returns a proxy even when the native side is absent, so
      // presence alone proves nothing; calls will simply reject and each method
      // below handles that.
      this._plugin = p || null;
      return this._plugin;
    } catch (_) { return null; }
  },
  // Must run before any other call, and AFTER sign-in: appUserID is the
  // Supabase user id, which is what iap-webhook keys entitlements on. Getting
  // this wrong means purchases land on an anonymous RevenueCat id and the
  // webhook cannot map them to a nurse.
  async configure(apiKey, userId) {
    const p = this._get();
    if (!p || !apiKey || !userId) return false;
    try {
      if (!this._configured) {
        await p.configure({ apiKey, appUserID: userId });
        this._configured = true;
      } else {
        await p.logIn({ appUserID: userId });
      }
      return true;
    } catch (e) { console.warn('PurchaseAPI.configure failed', e); return false; }
  },
  // Returns the offering's packages for the paywall, or [] when unavailable —
  // the paywall must never render a price it made up.
  async getPackages() {
    if (!this.isSupported()) return [];
    const p = this._get();
    if (!p) return [];
    try {
      const res = await p.getOfferings();
      const current = res?.offerings?.current || res?.current;
      return current?.availablePackages || [];
    } catch (e) { console.warn('PurchaseAPI.getPackages failed', e); return []; }
  },
  // { ok, cancelled, error } — a user cancelling is NOT an error and must not
  // surface as one.
  async purchase(pkg) {
    if (!this.isSupported()) return { ok: false, error: 'unavailable' };
    const p = this._get();
    if (!p || !pkg) return { ok: false, error: 'unavailable' };
    try {
      await p.purchasePackage({ aPackage: pkg });
      return { ok: true };
    } catch (e) {
      if (e?.code === 'PURCHASE_CANCELLED_ERROR' || e?.userCancelled) {
        return { ok: false, cancelled: true };
      }
      console.warn('PurchaseAPI.purchase failed', e);
      return { ok: false, error: e?.message || 'purchase_failed' };
    }
  },
  // ‼️ THE ONLY WAY A REAL DISCOUNT CAN REACH A VITAL PRO PURCHASE.
  //
  // Apple sets the price of an auto-renewable subscription, so "20% off" is not
  // something this app can compute or charge. What exists is an App Store OFFER
  // CODE: the discounted offer is configured in App Store Connect against the
  // subscription, Apple generates the codes, and this sheet is where the nurse
  // types one. Apple validates it, applies it and bills — we are only told the
  // outcome afterwards, through the normal entitlement sync.
  //
  // Vital's OWN access codes are a different thing entirely (they grant free
  // access, never a discount) and go through DB.redeemPromoCode instead. Both
  // exist because they answer different questions; neither can do the other's
  // job. See supabase-migration-promo-codes.sql for the full reasoning.
  //
  // Returns false when the sheet cannot be shown (web, or an SDK without it) so
  // the caller can hide the entry point rather than offer a dead button.
  async presentOfferCodeSheet() {
    if (!this.isSupported()) return false;
    const p = this._get();
    if (!p) return false;
    try {
      if (typeof p.presentCodeRedemptionSheet !== 'function') return false;
      await p.presentCodeRedemptionSheet();
      return true;
    } catch (e) {
      console.warn('PurchaseAPI.presentOfferCodeSheet failed', e);
      return false;
    }
  },
  // App Store review REQUIRES a visible Restore Purchases path (Guideline
  // 3.1.1). Do not remove the button that calls this.
  async restore() {
    if (!this.isSupported()) return { ok: false, error: 'unavailable' };
    const p = this._get();
    if (!p) return { ok: false, error: 'unavailable' };
    try { await p.restorePurchases(); return { ok: true }; }
    catch (e) { console.warn('PurchaseAPI.restore failed', e); return { ok: false, error: e?.message }; }
  },
};

// ‼️ THE ONE QUESTION EVERY UPSELL MUST ASK BEFORE IT ASKS FOR MONEY:
//     can a subscription actually be BOUGHT from here?
//
// It is a shared function rather than an inline check because the rule was
// already re-derived once as a local `canPurchase` in screens.jsx, and the
// surface that never asked it at all — Home's Vital Pro route card — dead-ended
// every web visitor three taps deep: 「了解 Vital Pro」 → the paywall →
// 「訂閱功能暫時無法使用,請稍後再試」, which on the web is not a wait, it is a
// lie. A second copy of this rule is a bug, not a convenience (same reasoning as
// cnePlanLicence).
//
// ‼️ IT GATES THE ASK. IT MUST NEVER GATE THE ACCESS.
// Entitlement lives in public.entitlements and is read from the SERVER
// (useProEntitlement) — a nurse who subscribed on her iPhone, or redeemed a
// VITAL code, is Pro in the web app too and keeps every surface she paid for.
// Gate 「了解 Vital Pro」 on this; never gate `isPro` content on it. Hiding a
// paid feature from the person paying for it is the worse bug of the two.
//
// False on the web (no StoreKit) and false on native until RevenueCat is
// installed and keyed (see PurchaseAPI above) — both are states where a
// purchase path leads somewhere that cannot take her money, so both must hide
// it. Fails closed: an unknown host never gets asked for money.
function proPurchasable(){
  try { return !!window.PurchaseAPI?.isSupported?.(); }
  catch (_) { return false; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Deep-link classification — the ONE place that decides what an incoming URL
// means. app.jsx's handleDeepLink calls this BEFORE its recovery parser, and
// scripts/test-deep-links.mjs drives this exact function, so the routing
// contract cannot drift between the app and its test.
//
// ‼️ THE CONTRACT (do not weaken):
//   1. Only allowlisted NAV_TARGETS are navigation. The auth hosts
//      (reset / signup / siwa) must NEVER be added — they carry Supabase
//      tokens and belong to the recovery parser.
//   2. ANY url carrying auth material (access_token / refresh_token / error /
//      error_code) is 'recovery', even on an allowlisted host. A nav link is
//      by definition token-free, so this can never cost a real navigation.
//   3. https share hosts resolve to their own targets, and NEVER fall through
//      to the recovery parser (which would treat them as a malformed link and
//      dump the user on the recovery-error screen).
//
// Returns one of:
//   { kind: 'nav',      target, params }  — route in-app, session flow untouched
//   { kind: 'ignore' }                    — a share host we have no route for
//   { kind: 'recovery' }                  — hand to the auth/recovery parser
const DEEPLINK_NAV_TARGETS = [
  'log', 'home', 'discover', 'record', 'saved', 'profile', 'course', 'group',
  // 'wallet' = the qualification wallet (Record tab → 資格錢包 view). Carried by
  // BOTH vital://wallet and the https Universal Link below, because it is the
  // App Store in-app event deep link and Apple prefers a Universal Link.
  'wallet',
];
const DEEPLINK_SHARE_HOSTS = ['vitalcne.com', 'www.vitalcne.com'];

function deeplinkHasAuthMaterial(params){
  return !!(params.get('access_token') || params.get('refresh_token')
    || params.get('error') || params.get('error_code'));
}

// Params live in the hash for Supabase links and the query for our own links.
function deeplinkParams(u){
  const raw = u.hash.startsWith('#') ? u.hash.slice(1)
            : u.search.startsWith('?') ? u.search.slice(1)
            : '';
  return new URLSearchParams(raw);
}

function classifyDeepLink(url){
  if (!url || typeof url !== 'string') return { kind: 'recovery' };

  // ── vital:// navigation ────────────────────────────────────────────────
  if (url.toLowerCase().startsWith('vital://')) {
    try {
      const nu = new URL(url);
      // Non-special schemes parse `vital://log` with host 'log' per WHATWG,
      // but fall back to the first path segment for engines that leave the
      // authority in pathname.
      const target = (nu.host || nu.pathname.replace(/^\/+/, '').split('/')[0] || '')
        .toLowerCase();
      const params = deeplinkParams(nu);
      if (DEEPLINK_NAV_TARGETS.includes(target) && !deeplinkHasAuthMaterial(params)) {
        return { kind: 'nav', target, params: Object.fromEntries(params) };
      }
    } catch (_) { /* unparseable — the recovery parser decides */ }
    return { kind: 'recovery' };
  }

  // ── https Universal Links ──────────────────────────────────────────────
  // Auth links are vital:// (per the Supabase redirect allowlist, RELEASE
  // §5.5), so nothing here should carry tokens — but rule 2 still applies.
  if (/^https:\/\//i.test(url)) {
    try {
      const wu = new URL(url);
      if (!DEEPLINK_SHARE_HOSTS.includes(wu.hostname)) return { kind: 'recovery' };
      const params = deeplinkParams(wu);
      if (deeplinkHasAuthMaterial(params)) return { kind: 'recovery' };

      const path = wu.pathname.replace(/\/+$/, '') || '/';
      const cm = path.match(/^\/c\/([^/]+)/);
      if (cm) return { kind: 'nav', target: 'course', params: { id: decodeURIComponent(cm[1]) } };
      const gm = path.match(/^\/g\/([^/]+)/);
      if (gm) return { kind: 'nav', target: 'group', params: { code: decodeURIComponent(gm[1]) } };
      // Qualification wallet — codeless, so it is a stable marketing target.
      if (path === '/wallet') return { kind: 'nav', target: 'wallet', params: {} };
      return { kind: 'ignore' };   // share host, no route — never 'recovery'
    } catch (_) { /* unparseable — fall through */ }
  }

  return { kind: 'recovery' };
}

Object.assign(window, {
  // CNE points domain contract — issue #42. One normalisation, three doors.
  normalizeCnePointsInput, cnePointsForSubmission,
  evidenceDisplayState,
  COURSE_SCHEMA, PROVIDERS, RAW_COURSES, COURSES,
  VITAL_APP_VERSION,
  USER, RECORD, REMINDERS, TOPIC_TINT, STR,
  // Topic vocabulary — a BOUNDED, already-bilingual set with 100% coverage on
  // every course (deriveTopic never returns null). That combination is why the
  // Pro preference picker offers topics rather than raw specialty_area strings:
  // a picker whose options map 1:1 onto a field every course carries can be a
  // real filter, not a wish. Consumed by ProSetupSheet. Same tree-shake caveat
  // as everything in this list.
  TOPIC_LABEL,
  L, fmtDate, fmtShort, daysBetween, isFull,
  // Deep-link routing contract (app.jsx handleDeepLink + test-deep-links.mjs).
  classifyDeepLink, DEEPLINK_NAV_TARGETS, DEEPLINK_SHARE_HOSTS,
  qualificationKindFromTitle, qualificationStatus,
  walletItems, walletNearestExpiry, walletItemState, licencesForWallet,
  currentLicenceEvidence, licenceEvidenceHistory,
  exportRecordPdf, ReviewAPI, CalendarAPI, WidgetAPI, PurchaseAPI,
  // ‼️ MUST STAY LISTED. data.js is a plain .js file outside the build's JSX
  // auto-expose plugin, so an unlisted helper is tree-shaken away and every
  // `window.proPurchasable?.()` call site silently reads undefined — which here
  // means "cannot buy", i.e. the upsell disappears on iOS too. Fails quiet, not
  // loud: exactly the shape scripts/test-route-card.mjs pins.
  proPurchasable,
  // PC↔CNE cycle date math. pcEndFromStart is called cross-file by onboarding's
  // CycleDatesField; data.js is a plain .js so it is NOT covered by the build's
  // JSX auto-expose plugin and must list its cross-file helpers here by hand.
  // (shiftMonths/shiftDays stay internal — only used inside data.js.)
  pcEndFromStart, pcStartFromEnd,
  // NCHK export pipeline (Annex A + B). The preview sheet builds reviewed rows
  // and calls exportNchkPdf directly; cneWindow/cycleRecords stay the shared
  // CNE-window source of truth.
  NCHK_NATURE_OPTIONS, nchkNatureLabel, nchkAnnexARows, nchkAnnexB,
  buildNchkHtml, printNchkHtml, exportNchkPdf, exportBlankNchkPdf,
  cneWindow, recordYears, defaultRecordYear, cycleRecords, cyclePointsSummary, isRecordExcluded,
  // Audit readiness — consumed by RecordScreen's readiness card and the NCHK
  // export sheet's gap list. Same tree-shake caveat: list it here by hand.
  auditReadiness,
  // Dual-registration licence helpers — consumed cross-file by userFromProfile,
  // HomeScreen's WalletCard, ProfileOverview, deriveReminders and the NCHK export
  // sheet. Same tree-shake caveat: list them here by hand. Each licence carries a
  // role: {en, zh} label (the register-part distinction lives in the role).
  profileLicenses, licenseProgress, licenseIsMandatory, MANDATORY_CNE_FROM,
  // Route planning — consumed by HomeScreen's Pro route card and pinned by
  // scripts/test-route-plan.mjs. Same tree-shake caveat: data.js is a plain .js
  // outside the JSX auto-expose plugin, so an unlisted export is silently
  // dropped from the bundle and the card renders nothing.
  cneRoutePlan, courseIsRouteEligible, courseRepeatKey, applicableFee,
  // Membership layer. data.js is a plain .js file, so it is NOT covered by the
  // build's JSX auto-expose plugin — a cross-file helper missing from this list
  // is tree-shaken away and the screen that calls it renders blank. The picker
  // (components.jsx) needs the options + sanitiser; screens.jsx and paywall.jsx
  // both save through the sanitiser.
  membershipProviderOptions, sanitiseMemberships, membershipMatchesCourse,
  MEMBERSHIP_MAX_ENTRIES, MEMBERSHIP_MAX_LEN,
  // Which certificate the planner (and the "have you started?" question) is
  // about. Shared by HomeScreen's route card and app.jsx's setup sheet — see
  // the header: a second copy of this rule is a bug, not a convenience.
  cnePlanLicence, cnePlanLicences,
  // Course watch rules — consumed by the rule editor in Notification settings
  // and mirrored (deliberately, with the same limits) by notify-cron's sweep.
  // Same tree-shake caveat as everything in this list.
  normaliseCourseRules, courseMatchesRule, RULE_LIMITS,
  // Lifecycle states + rollover date math — consumed cross-file by HomeScreen
  // (WalletCard chip, rollover banner/sheet), Edit Profile (hygiene banner,
  // manual-rollover detection) and deriveReminders. Same tree-shake caveat.
  licenseLifecycleState, profileHasSeedDates, nextCycleStartFrom, buildWidgetSnapshot,
  // Restricted-course model + eligibility. RESTRICTION.build is called by
  // screens.jsx buildRestrictionNote; the visibility/eligibility helpers by
  // DiscoverScreen, HomeScreen, DiscoverFilterSheet and CourseCard. data.js is
  // a plain .js (not covered by the JSX auto-expose plugin) so cross-file
  // helpers MUST be listed here or esbuild tree-shakes them → blank page.
  // ‼️ Every one of these must be listed here or esbuild tree-shakes it and the
  // screens' `typeof window.x === 'function'` guards fail SILENTLY (see
  // reference_js_files_window_export_list). restrictionVerdict is exported so
  // scripts/test-discover.mjs can assert the predicate directly rather than
  // inferring it from the three wrappers.
  RESTRICTION, restrictionVerdict, restrictedVisibleByDefault,
  restrictionEligibility, restrictedRecommendable,
  // ‼️ registrationTargetUrl WAS MISSING FROM THIS LIST AND WAS THEREFORE NEVER
  // IN THE BUNDLE. screens.jsx calls it behind `typeof … === 'function'`, so the
  // tree-shaken build did not throw — it silently fell back to
  // `course.registrationUrl`, which normaliseSupabaseCourse leaves NULL for any
  // row without a registration_url. Measured 2026-08-16 (N=191 unfinished):
  // source semantics gave 191 rows a course-specific link, the built bundle gave
  // 54, and 143 rows opened a provider index page instead of their own outline.
  // The defensive guard is exactly what made it silent. Both names are pinned in
  // build.mjs's smoke list so this fails the BUILD next time, not production.
  registrationTargetUrl, registrationDestination,
  // deriveCourseAction is the CTA's ONLY intent source. Unlisted here it is
  // tree-shaken, screens.jsx's `window.deriveCourseAction ? … : fallback`
  // silently takes the fallback, and every button reverts to a hardcoded
  // 報名 — the precise defect this function exists to remove, restored
  // invisibly and with a green source-only test suite.
  deriveCourseAction,
  // Delivery mode — the four-value fact Discover's 「形式」 facet consumes.
  // Listed for the SAME reason as registrationTargetUrl above: data.js is a
  // plain .js outside the JSX auto-expose plugin, and discover-filters.jsx
  // reads this through `window.`, so an unlisted helper is tree-shaken and the
  // facet silently falls back to whatever its guard does next.
  deriveDeliveryMode,
  // Peer-signal rollups. setExperienceRollups is called by app.jsx after the
  // catalogue fetch; the two grain accessors by CourseCard/GridCourseCard.
  // ‼️ courseRollupKey/courseRollupCounts are GONE (§65). This comment used to
  // say the admin apply dialog needed courseRollupKey — it does not; that dialog
  // uses courseRepeatKey, a different function, which stays.
  // Same tree-shake caveat as everything else in this list.
  setExperienceRollups, courseOccurrenceCounts, courseSeriesCounts,
  // Review rollups — setReviewRollups is called by app.jsx after the catalogue
  // fetch; courseReviewSummary by the card components. Same tree-shake caveat.
  setReviewRollups, courseReviewSummary, courseOccurrenceReviewSummary,
  courseSeriesKey, courseOfferingKey,
  // Chip vocabulary shared by the review card and the review sheet, so the two
  // entry points can never offer different options. Mirrors the SQL functions
  // review_course_chips() / review_provider_chips() — a chip that is not in
  // both is rejected server-side with 'unknown course chip'.
  REVIEW_COURSE_CHIPS, REVIEW_PROVIDER_CHIPS,
  // Phase 3 chip catalogue + the two helpers the form uses. Same tree-shake
  // caveat as everything else in this list: data.js is a plain .js outside the
  // JSX auto-expose plugin, so an unlisted export is dropped from the bundle
  // and the chip rows silently render empty.
  REVIEW_CHIP_CATALOG, reviewChipBucket, reviewChipsFor, reviewToggleChip,
  // Discover's four orthogonal status axes. Event/registration are derived at
  // read time so cached rows never freeze a date-dependent state. The two
  // normalisers are exposed only for parity/regression tests.
  deriveCourseAxes, deriveEventStatus, deriveRegistrationStatus,
  deriveCneStatusNorm, derivePublicationStatus, courseBucket, deriveShiftTag,
  // Calendar export — consumed by CourseDetailScreen's 加入日曆 action.
  courseToIcs,
  courseIsActionable, discoverStatusCounts, sortForDiscover,
  // Free CNE headline count — consumed by HomeScreen's Free CNE card. Its
  // predicate is tied to Discover's free+actionable list; see the note on the
  // function. Same tree-shake caveat: list it here by hand or the card renders
  // a blank number (and data.js is a plain .js, outside the auto-expose plugin).
  freeCneSummary,
  normaliseCourse, normaliseSupabaseCourse,
  // todayIso is the single local-date "today" source for every cross-file
  // caller (DiscoverScreen, dfApply). Same tree-shake caveat as above.
  todayIso,
});
